/**
 * GameState — single source of truth. Owns the economy, the mothership (+ drones),
 * a two-layer map (solar system / planet), and the upgrade tree, whose purchases
 * fold into `eff` (effective stats) applied to the drones, hull, and economy.
 */
import { Vector2 } from "@/math/Vector2";
import { ResourceStore } from "@/game/economy/resources";
import { createMothership, type Mothership } from "@/game/mothership/Mothership";
import {
  SolarSystemManager,
  planetSeed,
} from "@/game/world/SolarSystemGenerator";
import {
  PlanetLayerManager,
  type MoonTurret,
} from "@/game/world/PlanetLayerGenerator";
import {
  EnemyManager,
  type EnemyShip,
  type EnemyBullet,
} from "@/game/npc/EnemyShips";
import { CruiserManager, type Cruiser } from "@/game/npc/Cruisers";
import { CarrierManager, type Carrier } from "@/game/npc/Carriers";
import { StationManager } from "@/game/npc/Station";
import { PlayerWorm } from "@/game/player/PlayerWorm";
import {
  WormManager,
  type WormSegment,
  type WormBullet,
  type WormBeam,
  type WormCtx,
} from "@/game/npc/Worm";
import {
  PlayerFighterManager,
  type FighterLoadout,
} from "@/game/npc/PlayerFighters";
import {
  makeCrew,
  mdcBuff,
  ftpBuff,
  rdcBuff,
  gcBuff,
  fdcBuff,
  rerollCrew as rollCrewAgain,
  upgradeCost as crewUpgradeCost,
  CREW_MAX_TIER,
  RECRUIT_COST,
  REROLL_COST,
  type CrewMember,
  type CrewType,
} from "@/game/crew/crew";
import type { Damageable } from "@/game/combat/turrets";
import { makeRng } from "@/math/rng";
import {
  MOTHERSHIP,
  HANGAR,
  MINING_DRONE,
  MECH_DRONE,
  SHIP_DEFENSE,
  DROPS,
  TRADE,
  SERVICE,
  TURRET,
  CANNON,
  ENEMY,
  CRUISER,
  ENEMY_CARRIER,
  WORM,
  PLAYER_FIGHTER,
  PROGRESSION,
  MODS,
  ITEM_SELL,
  MOON_TURRET,
  MKV,
  MKVI,
  BIG_CANNON,
  FIGHTER_DRONE,
  MISSILE,
  STATION,
  MKVII,
  WARP,
} from "@/config/balance";
import { makeShieldMod, type Modification, type Rarity } from "@/game/mods/modifications";
import { TurretSystem, mountsForTier } from "@/game/combat/turrets";
import { CannonSystem, BigCannonSystem } from "@/game/combat/cannons";
import { MissileSystem } from "@/game/combat/Missiles";
import { RECIPES_BY_ID } from "@/game/crafting/recipes";
import {
  INTRO_LINES,
  ENDING_LINES,
  PRAISE_LINE,
  QUEST_COUNT,
  questDef,
} from "@/game/quests/questline";
import {
  rollMarket,
  MARKET_REFRESH_SEC,
  type MarketStock,
} from "@/game/economy/market";
import type { PlanetService } from "@/game/world/SolarSystemGenerator";
import { MiningSystem, type MiningTargetFrame } from "@/game/mining/MiningSystem";
import { DroneBay, type DroneUnit } from "@/game/mining/DroneBay";
import { RepairBay } from "@/game/mining/RepairBay";
import { NpcManager, type NpcShip } from "@/game/npc/NpcShips";
import type { MineableBody } from "@/game/world/body";
import {
  computeMods,
  canPurchase,
  UPGRADES_BY_ID,
} from "@/game/upgrades/upgradeTree";

export type MapLayer = "system" | "planet";

/** Transient VFX events drained by the renderer each frame. */
export interface FloaterEvent {
  x: number;
  y: number;
  amount: number;
  kind: "mineral" | "xp" | "level" | "loot";
  label?: string; // for "loot" — the item name shown
}
export interface ExplosionEvent {
  x: number;
  y: number;
  radius: number;
  big?: boolean; // planetary (moon) explosion → larger, brighter VFX
  blue?: boolean; // homing-missile detonation → blue blast instead of the warm default
}
/** Combat VFX routed to the 3D layer: a ship blowing up, or a bolt strike. */
export interface CombatFxEvent {
  x: number;
  y: number;
  kind: "hit" | "kill";
}

/** Dot data for the HUD minimap (current zone). */
export interface MinimapDot {
  x: number;
  y: number;
  s: number; // size bucket: asteroids 0/1/2; else unused
}
export interface MinimapData {
  isSystem: boolean;
  extent: number; // half-size of the zone in world units (fit the whole map)
  player: { x: number; y: number };
  planet: { x: number; y: number; r: number } | null; // central planet (blue)
  moons: MinimapDot[]; // purple
  asteroids: MinimapDot[]; // green, sized by ore
  enemies: MinimapDot[]; // red
  playerFighters: MinimapDot[]; // our escort fighters (blue)
  planets: { x: number; y: number; service: string; rogue: boolean }[]; // system view (blue)
  highlightSafe: boolean; // tutorial: pulse safe-zone planets
  riftArmed: boolean; // Mk VIII: rift is armed — tap the minimap to set the teleport exit
  waypoint: { x: number; y: number } | null; // click-to-move destination (the ship's moveTarget)
}

/** Mk VII SWL Rift Swarm: one flying purple homing arrow (a piercing projectile, NOT a unit). */
interface RiftArrow {
  pos: Vector2;
  heading: number;
  target: { pos: Vector2; hp: number } | null;
  retarget: number; // seconds until it re-picks a target (if the current one survives)
  life: number; // total lifetime remaining
  hitCd: number; // per-arrow cooldown between damage ticks
  trail: { x: number; y: number }[]; // recent positions for the long trail
}

interface EffectiveStats {
  hangarLevel: number;
  mechDrones: number;
  droneSpeed: number;
  droneCargo: number;
  droneRate: number;
  droneRange: number;
  shipSpeed: number;
  solarSpeedMul: number; // multiplier applied to flight speed in the SOLAR layer
  miningRange: number;
  beamRate: number; // minerals/s per beam
  beamCount: number; // number of hull beams
  hullRate: number; // total hull mining = beamRate × beamCount
  yieldMul: number;
  fighterSlots: number; // Mk IV escort-fighter slots unlocked
  fighterDrones: number; // Mk V Swarm-lord fighter-drone slots unlocked
  missiles: number; // Mk V Swarm-lord homing-missile-salvo size
  siloMissiles: number; // Mk VI Battleship homing-missile-salvo size
  turretRangeMul: number; // Mk V Battleship turret-range upgrades
  hullHpBonus: number; // Mk V Battleship flat hull-HP upgrades
  bigCannonBeamMul: number; // Mk VI Battleship big-cannon beam-duration multiplier
  omniMining: boolean; // mine 360° around the hull
  wormBodies: number; // Mk VII: extra worm body segments from upgrades
  wormMissiles: number; // Mk VII: red homing missiles per salvo
  wormBeamDurMul: number; // Mk VII: maw-beam duration multiplier
  wormBeamDmgMul: number; // Mk VII: maw-beam damage multiplier
  branchChance: number; // missile branch-split chance (Swarm-lord / worm)
  branchCount: number; // branch missiles per split
  missileCdMul: number; // Mk VI Swarm-lord: missile-salvo cooldown multiplier (<1 = faster)
  fighterMissiles: boolean; // Mk VI Swarm-lord: escort fighters fire homing missiles
  droneMissiles: boolean; // Mk VI Swarm-lord: fighter drones fire homing missiles (1 / 15s)
  smartMissiles: boolean; // Mk VI Swarm-lord: missiles re-acquire on target death
}

/** First-run tutorial steps (see startTutorial / advanceTutorial). */
export const TUTORIAL = {
  GOTO_PLANET: 0, // fly to a highlighted safe planet
  MINE: 1, // mine asteroids for the first upgrade
  BUY_UPGRADE: 2, // enough minerals → pause + point to Upgrades
  WAIT_LEVEL2: 3, // dormant until the player reaches level 2
  SPEND_SKILL: 4, // teach spending a skill point
  DONE: 99,
} as const;
const TUTORIAL_FIRST_UPGRADE_COST = 100; // = laser1.cost (Mining Laser I)

/** Snapshot view of the tutorial for the HUD (string step + what to highlight). */
export type TutorialStep =
  | "goToPlanet"
  | "mine"
  | "buyUpgrade"
  | "spendSkill"
  | null;

/** A remote pilot in the Central Hub — their ship state, interpolated toward the last broadcast. */
export interface RemotePlayer {
  username: string;
  tier: number;
  segCount: number; // worm body-segment count (tiers 6/7 render as a worm chain)
  x: number; // smoothed render pose
  y: number;
  heading: number;
  ax: number; // last authoritative pos + heading (dead-reckoning anchor)
  ay: number;
  th: number;
  vx: number; // last authoritative velocity (dead-reckoning + drives thrusters)
  vy: number;
  age: number; // seconds since the last authoritative update
  wormTrail?: { x: number; y: number; heading: number }[]; // head→tail chain (worm tiers only)
}

export class GameState {
  resources = new ResourceStore();
  mothership: Mothership;
  system: SolarSystemManager;
  layer: MapLayer = "system";
  planetLayer: PlanetLayerManager | null = null;
  private fromPlanetId: string | null = null;

  private mining = new MiningSystem();
  elapsed = 0;
  warps = 0;
  miningTargets: MiningTargetFrame[] = []; // rocks the hull beams are mining
  miningImpact: Vector2 | null = null; // first target's impact (for 3D sparks)
  moveBodyId: string | null = null; // body the hull is flying toward / mining
  systemTargetId: string | null = null; // planet being flown to (system layer)
  readonly droneBay = new DroneBay();
  readonly repairBay = new RepairBay(); // mechanical repair drones

  readonly purchasedUpgrades = new Set<string>();
  private eff!: EffectiveStats;

  // Meta-progression + UI/VFX state.
  paused = false;
  playerName = "Captain"; // set during onboarding; shown on the hull nameplate
  accountName = ""; // logged-in account username (drives account-based admin; not serialized)
  level = 1;
  exp = 0;
  expToNext = 50; // = xpForLevel(1)
  skillPoints = 0;
  tutorialStep: number = TUTORIAL.DONE; // legacy coach machine (kept inert; questline drives highlights)
  questStep = 0; // Tutor questline: 0 = not started, 1..QUEST_COUNT active, then ending, 99 = done
  private dialogLines: string[] = []; // active Tutor dialog (empty = none showing)
  private dialogIndex = 0; // which line is on screen
  private dialogSpeakers: ("aria" | "eater")[] = []; // per-line speaker for the dialog
  // --- Eater of Worlds awakening cutscene ---
  private eaterPhase: "none" | "intro" | "explode" | "emerge" = "none";
  private eaterTimer = 0;
  private introShown = false;
  private rolledEater = false; // one awakening roll per planet (spent on the first moon)
  cameraOverride: { x: number; y: number } | null = null; // forces the camera during the cutscene
  // --- warp-jump cutscene (spaghettify the system → rift → emerge in the new system) ---
  private warpPhase: "none" | "collapse" | "open" | "enter" | "emerge" | "close" = "none";
  private warpTimer = 0;
  private warpSeed = 0; // destination-system seed, applied mid-cutscene
  warpProgress = 0; // 0..1 spaghettification amount (read by the renderer)
  warpShipScale = 1; // 0..1 hull render scale while diving into / out of the rift (renderer)
  warpRift: { cx: number; cy: number; heading: number; open: number; fade: number; worldWidth: number } | null = null;
  private warpEnterFrom = { x: 0, y: 0 }; // ship position when the "enter" dive begins
  eaterPlanetId: string | null = null; // the map planet the Eater awoke in (persists across layers)
  eaterDefeated = false; // the Eater is dead — its head marker clears, the planet stays wrecked
  private eaterExplodedFrame = 0; // which Exploded_planet_sheet frame (0..15)
  private didWarp = false; // set on arrival by applyWarpArrival(); consumed by the final "warp away" quest
  private moonsDestroyed = 0; // moons cracked during the Moon Core quest (drives the hint)
  private moonCoreHintShown = false; // one-shot "buy Moon Cores at the trade hub" nudge
  showMiningRange = false;
  hullMiningEnabled = true; // left-side toggle
  autoMineEnabled = false; // auto-mine: fly to the nearest ore & mine, seek the next when it's spent
  dronesEnabled = true; // left-side toggle
  cannonsEnabled = true; // left-side toggle (Mk II+ turrets)
  mechDronesEnabled = true; // left-side toggle (repair drones)
  combatActive = false; // true while a fighter is aggro'd → mining is suspended
  playerDead = false; // hull reached 0 → destroyed, awaiting respawn
  // --- attract mode (main-menu "aquarium" preview) — an immortal, fully-armed MK VI
  // Battleship fights an endless swarm. No HUD, no input; the engine just plays itself. ---
  attract = false;
  private attractCannonTimer = 1.2; // countdown → auto-fire the Big Cannons at a foe
  private attractMissileTimer = 2.5; // countdown → auto-launch a missile salvo
  private attractAngle = 0; // current angle along the slow showcase orbit (radians)
  private readonly attractOrbitR = 360; // radius of the circle the showcase ship flies
  private readonly attractOrbitW = 0.16; // angular speed (rad/s) → a slow, lazy loop
  // --- Central Hub (multiplayer): the real engine in a peaceful shared space ---
  hub = false; // true → hub mode (no enemies / mining / damage; other pilots rendered)
  readonly remotePlayers = new Map<string, RemotePlayer>(); // other pilots (rendered as ships)
  station: StationManager | null = null; // orbiting ring-station boss (around the planet)
  private stationPlanetIds = new Set<string>(); // rogue planets that host a ring-station this system
  private stationCache = new Map<string, StationManager>(); // per-planet station state (session)
  private stationDefeated = new Set<string>(); // planets whose station is beaten (won't re-arm)
  playerWorm: PlayerWorm | null = null; // MK VII: the player's worm body (head follows the mothership)
  private baseMaxHp = 0; // base hull HP (captured at build) — bonus HP upgrades add on top
  shieldLevel = 0; // legacy mechanical-planet shield upgrades (kept for old saves)
  readonly mods: Modification[] = []; // owned ship modifications (non-stacking)
  equippedShieldModId: string | null = null; // equipped shield generator
  noticeText: string | null = null; // one-shot toast (e.g. trade-hub mods upgraded)
  private lastModTier = 1; // tracks the last announced trade-hub mod tier
  private mkvIntroSeen = false; // Aria's Mk V refit briefing plays once
  readonly inventory: Record<string, number> = {}; // moonCore, energyCore, …
  bossResult: { xp: number; units: number; drops: { id: string; count: number }[] } | null =
    null; // set on a boss defeat → drives the results popup
  private newDropIds = new Set<string>(); // item ids dropped since last inventory view
  private newDropCount = 0;
  private market: MarketStock[] = []; // trade-hub market stock (re-rolls periodically)
  private marketRefreshAt = 0; // elapsed seconds of the next re-roll
  private hitTimer = 999; // seconds since the last collision (starts "safe")
  private shieldBrokenTimer = 0; // extra regen lockout after the shield fully breaks
  shieldFlash = 0; // 1 on hit, decays → drives the shield-bubble VFX
  private bounceVel = new Vector2();
  private bounceTimer = 0;
  private planetCache = new Map<string, PlanetLayerManager>(); // persist per-planet state
  private pendingDestroyed: Record<string, string[]> = {}; // saved destroyed-body ids, applied on planet entry
  moonTurretBullets: (EnemyBullet & { dmg: number })[] = []; // rogue moon-turret red bolts
  private protectedMoonIds = new Set<string>(); // moons shielded by a live turret (unmineable)
  private npcs: NpcManager | null = null; // competitor miner ships (planet layer)
  private enemies: EnemyManager | null = null; // hostile raiders (planet layer)
  private cruisers: CruiserManager | null = null; // capital-ship raiders (deep jumps)
  private carriers: CarrierManager | null = null; // capital-ship carriers (deeper jumps)
  private worm: WormManager | null = null; // the Eater of Worlds boss (from jump 15)
  readonly playerFighters = new PlayerFighterManager(); // Mk IV escort fighters
  // Swarm-lord fighter drones — pilotless mini-fighters (faster/weaker/smaller).
  readonly playerFighterDrones = new PlayerFighterManager({
    speed: FIGHTER_DRONE.speed,
    maxHp: FIGHTER_DRONE.maxHp,
    maxShield: FIGHTER_DRONE.maxShield,
    boltDamage: FIGHTER_DRONE.boltDamage,
  });
  // Mk VII SWL "Rift Swarm" skill: a volley of purple homing ARROWS fired from two rifts — piercing
  // projectiles (NOT units) that fly through enemies, re-target, and fade after their lifetime.
  riftSwarmCd = 0; // Rift Swarm cooldown timer
  readonly riftArrows: RiftArrow[] = []; // the flying purple arrows
  readonly riftPortals: { x: number; y: number; age: number; heading: number; emitted: number; batchCd: number }[] = []; // EOW rift-mouth VFX + wave-emission state
  readonly missileSystem = new MissileSystem(); // Swarm-lord homing-missile salvo skill
  // Per fleet-slot fighter (null = empty). Slots 0..maxFighterSlots-1 are usable.
  readonly fighters: ({ pilotId: string | null } | null)[] = Array.from(
    { length: PLAYER_FIGHTER.maxSlots },
    () => null,
  );
  readonly crew: CrewMember[] = []; // recruited crew roster (Space Tavern)
  equippedMdcId: string | null = null; // active Miner Drone Controller
  equippedRdcId: string | null = null; // active Repair Drone Controller
  equippedGcId: string | null = null; // active Gun Commander (Battleship MK V)
  equippedFdcId: string | null = null; // active Fighter Drone Controller (Swarm-lord MK V)
  readonly turretSystem = new TurretSystem(); // Mk II+ turrets (planet layer)
  readonly cannonSystem = new CannonSystem(); // Mk IV manual spinal cannons
  readonly bigCannonSystem = new BigCannonSystem(); // Mk V Battleship centre-spine super-guns
  cannonAim: Vector2 | null = null; // locked cannon aim point (world)
  readonly unlockedCannonSlots = new Set<number>(); // which cannon slots are unlocked
  readonly equippedCannons: (string | null)[] = Array(MKVI.bts.cannons).fill(null); // per-slot cannon (Mk VI Battleship = 8; lower tiers use fewer)
  readonly unlockedBigCannonSlots = new Set<number>(); // Battleship big-cannon slots unlocked
  readonly equippedBigCannons: (string | null)[] = Array(BIG_CANNON.slots).fill(null);
  cannonTargetBodyId: string | null = null; // locked moon/asteroid for the cannons
  cannonTargetEnemy: Damageable | null = null; // locked enemy/cruiser (focus fire)
  cannonReticle: { x: number; y: number; locked: boolean } | null = null; // drawn reticle
  readonly unlockedSlots = new Set<number>(); // which turret mounts are unlocked
  private get nextSlotCost(): number {
    return TURRET.slotUnlockCost[this.unlockedSlots.size] ?? 0;
  }
  // per-mount turret id — 8 mounts (Mk II uses the first 4, Mk III all 8)
  readonly equipped: (string | null)[] = Array(TURRET.slotsMkVI).fill(null); // up to 18 turret slots (Mk VI)
  readonly floaterEvents: FloaterEvent[] = [];
  readonly explosions: ExplosionEvent[] = [];
  readonly combatFx: CombatFxEvent[] = []; // ship-death / bolt-hit VFX → 3D layer

  constructor(seed = (Math.random() * 2 ** 32) >>> 0) {
    this.mothership = createMothership(new Vector2(0, -180));
    this.baseMaxHp = this.mothership.maxHp; // base hull HP before any upgrade bonus
    this.system = new SolarSystemManager(seed);
    this.recomputeStats();
  }

  // --- upgrades ---
  private recomputeStats(): void {
    const m = computeMods(this.purchasedUpgrades);
    this.eff = {
      // Start with 0 drones — drone slots come only from the gated Hangar upgrades.
      hangarLevel: m.hangar,
      mechDrones: m.mechDrones,
      droneSpeed: MINING_DRONE.speed * m.droneSpeedMul,
      droneCargo: MINING_DRONE.cargoCapacity * m.droneCargoMul,
      droneRate: MINING_DRONE.mineRatePerSec * m.droneRateMul,
      droneRange: MINING_DRONE.deployRange * m.droneRangeMul,
      shipSpeed: MOTHERSHIP.speed * m.shipSpeedMul * (1 + (this.isWorm ? 0 : m.swlSpeedBonus)),
      solarSpeedMul: m.solarSpeedMul,
      miningRange: MOTHERSHIP.miningRange * m.rangeMul,
      beamRate: MOTHERSHIP.beamRate * m.hullRateMul, // Laser upgrades boost per-beam
      beamCount: MOTHERSHIP.baseBeams + m.beams, // beam upgrades add beams
      hullRate:
        MOTHERSHIP.beamRate * m.hullRateMul * (MOTHERSHIP.baseBeams + m.beams),
      yieldMul: m.yieldMul,
      fighterSlots: m.fighterSlots,
      fighterDrones: m.fighterDrones,
      missiles: m.missiles,
      siloMissiles: m.siloMissiles,
      turretRangeMul: m.turretRangeMul,
      hullHpBonus: m.hullHpBonus,
      bigCannonBeamMul: m.bigCannonBeamMul,
      omniMining: m.omniMining,
      wormBodies: m.wormBodies,
      wormMissiles: m.wormMissiles,
      wormBeamDurMul: m.wormBeamDurMul,
      wormBeamDmgMul: m.wormBeamDmgMul,
      branchChance: Math.min(0.35, m.branchChance), // capped at 35% (Mk VI Splitting Pods)
      branchCount: Math.min(8, m.branchCount), // 1 base + up to 7 extra (Mk VI)
      missileCdMul: m.missileCdMul,
      fighterMissiles: m.fighterMissiles,
      droneMissiles: m.droneMissiles,
      smartMissiles: m.smartMissiles,
    };
    // Equipped Miner Drone Controller boosts mining speed + drone capacity.
    const mdc = this.equippedMdc;
    if (mdc) {
      const b = mdcBuff(mdc);
      this.eff.beamRate *= b.mineSpeedMul;
      this.eff.droneRate *= b.mineSpeedMul;
      this.eff.hullRate = this.eff.beamRate * this.eff.beamCount;
      this.eff.droneCargo *= b.cargoMul;
    }
    // Battleship hull-HP upgrades add flat max HP on top of the base hull value.
    if (this.baseMaxHp > 0) {
      this.mothership.maxHp = this.baseMaxHp + m.hullHpBonus;
      if (this.mothership.hp > this.mothership.maxHp) this.mothership.hp = this.mothership.maxHp;
    }
    this.syncWormHull(); // MK VII: build/resize/tear down the worm body to match the hull
  }

  /** Keep the MK VII worm body in sync with the hull tier + segment upgrades. */
  private syncWormHull(): void {
    if (this.isWorm) {
      const m = this.mothership;
      if (!this.playerWorm) {
        this.playerWorm = new PlayerWorm(m.pos.x, m.pos.y, m.heading, this.wormBodyCount);
      } else {
        this.playerWorm.setBodyCount(this.wormBodyCount, m.pos.x, m.pos.y, m.heading);
      }
    } else if (this.playerWorm) {
      this.playerWorm = null;
    }
  }

  /** Mk IV escort-fighter slots the player has unlocked (0 until the Fighter Bays). */
  get fighterSlots(): number {
    return this.eff.fighterSlots;
  }

  get beamCount(): number {
    return this.eff.beamCount;
  }
  get droneRange(): number {
    return this.eff.droneRange;
  }
  get dronesActive(): boolean {
    return this.eff.hangarLevel > 0;
  }
  get npcShips(): NpcShip[] {
    return this.npcs?.ships ?? [];
  }
  get enemyShips(): EnemyShip[] {
    return this.enemies?.ships ?? [];
  }
  get enemyBullets(): EnemyBullet[] {
    return this.enemies?.bullets ?? [];
  }
  get cruiserShips(): Cruiser[] {
    return this.cruisers?.ships ?? [];
  }
  get cruiserBullets(): EnemyBullet[] {
    return this.cruisers?.bullets ?? [];
  }
  get carrierShips(): Carrier[] {
    return this.carriers?.ships ?? [];
  }
  get carrierBullets(): EnemyBullet[] {
    return this.carriers?.bullets ?? [];
  }
  /** Eater-of-Worlds boss segments (head + body + tail; includes dead wreckage). */
  get wormSegments(): WormSegment[] {
    return this.worm?.segments ?? [];
  }
  get wormBullets(): WormBullet[] {
    return this.worm?.bullets ?? [];
  }
  get wormBeam(): WormBeam | null {
    return this.worm?.beam ?? null;
  }
  get wormBeamPhase(): "idle" | "charge" | "fire" {
    return this.worm?.beamPhase ?? "idle";
  }
  get wormCharge(): number {
    return this.worm?.chargeFrac ?? 0;
  }
  get wormActive(): boolean {
    return this.worm != null && !this.worm.defeated;
  }
  /** The central planet is a scattered wreck (shown in its own layer AND on the map). */
  get planetExploded(): boolean {
    return (
      this.layer === "planet" &&
      this.fromPlanetId != null &&
      this.fromPlanetId === this.eaterPlanetId
    );
  }
  get explodedFrame(): number {
    return this.eaterExplodedFrame;
  }
  /** Rogue-planet moon turrets in the current planet layer (includes dead ones). */
  get moonTurrets(): MoonTurret[] {
    return this.planetLayer?.turrets ?? [];
  }
  /** Live moon turrets — Damageable targets for the player's guns. */
  get liveMoonTurrets(): MoonTurret[] {
    return this.moonTurrets.filter((t) => !t.dead && t.hp > 0);
  }
  /** Per-moon turret equip chance at the current jump: every rogue moon is armed
   * (100%) from warp 7 onward, none before. */
  get moonTurretChance(): number {
    return this.warps < MOON_TURRET.fromWarp ? 0 : 1;
  }
  /** All hostile things the player's guns target (fighters + cruisers + moon turrets). */
  get hostiles(): Damageable[] {
    return [
      ...this.enemyShips,
      ...this.cruiserShips,
      ...this.carrierShips,
      ...this.wormSegments.filter((s) => s.hp > 0),
      ...this.liveMoonTurrets,
      ...(this.station?.liveWeapons() ?? []), // station-boss guns are shootable targets
    ];
  }
  /** Camera focus during capital-ship combat: the cruiser we're locked on (or the
   * nearest aggro'd cruiser), so the renderer can keep BOTH us and it on screen.
   * Null when no cruiser is engaged. */
  get combatFocus(): { x: number; y: number } | null {
    const locked = this.cannonTargetEnemy;
    if (
      locked &&
      locked.hp > 0 &&
      (this.cruiserShips.some((c) => c === locked) ||
        this.carrierShips.some((c) => c === locked) ||
        this.wormSegments.some((s) => s === locked))
    ) {
      return { x: locked.pos.x, y: locked.pos.y };
    }
    // The Eater of Worlds → only frame its head once EVERY body/tail segment is dead
    // (before that, the camera follows the player normally).
    if (this.worm && !this.worm.head.dead && this.worm.head.hp > 0) {
      const bodiesLeft = this.worm.segments.some((s) => s.kind !== "head" && !s.dead);
      if (!bodiesLeft) return { x: this.worm.head.pos.x, y: this.worm.head.pos.y };
    }
    // Nearest aggro'd capital ship (cruiser OR carrier) → keep both it and us framed.
    let best: { pos: { x: number; y: number } } | null = null;
    let bestD = Infinity;
    const m = this.mothership;
    for (const c of [...this.cruiserShips, ...this.carrierShips]) {
      if (c.hp <= 0 || !c.aggro) continue;
      const d = (c.pos.x - m.pos.x) ** 2 + (c.pos.y - m.pos.y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best ? { x: best.pos.x, y: best.pos.y } : null;
  }
  get playerFighterShips() {
    return this.playerFighters.ships;
  }
  get playerFighterBullets() {
    return this.playerFighters.bullets;
  }
  get playerFighterMissiles() {
    return this.playerFighters.missiles; // Mk VI SWL fighter homing missiles
  }
  /** Usable fleet slots = how many Fighter Bay upgrades are owned (capped). */
  get maxFighterSlots(): number {
    // Swarm-lord fields up to 12 escort fighters (Mk VI = 16); others cap at 8.
    if (this.mkvPath === "swarmlord") {
      return Math.min(this.hullTier >= 5 ? 16 : 12, this.eff.fighterSlots + 4);
    }
    return Math.min(PLAYER_FIGHTER.maxSlots, this.eff.fighterSlots);
  }
  /** Buy an escort fighter into a specific (unlocked, empty) fleet slot. */
  buyFighter(slot: number): boolean {
    if (this.currentService !== "mechanical") return false;
    if (slot < 0 || slot >= this.maxFighterSlots) return false;
    if (this.fighters[slot]) return false;
    if (!this.resources.spend({ units: PLAYER_FIGHTER.buyCost })) return false;
    this.fighters[slot] = { pilotId: null };
    return true;
  }
  /** Scrap the fighter in a slot (no refund). */
  dismissFighter(slot: number): boolean {
    if (this.currentService !== "mechanical") return false;
    if (slot < 0 || slot >= this.fighters.length || !this.fighters[slot]) return false;
    this.fighters[slot] = null;
    return true;
  }
  /** Loadouts for fighters that can actually deploy — i.e. that have a valid
   * Fighter Pilot assigned. A fighter with no pilot stays grounded. */
  private buildFighterLoadouts(): FighterLoadout[] {
    const out: FighterLoadout[] = [];
    if (this.attract) {
      // Menu preview: deploy a full escort wing (no crew/pilot bookkeeping needed). Given a huge
      // HP/shield pool so they're effectively IMMORTAL — they never fall, so the wing stays on
      // screen and the footage never thins.
      for (let i = 0; i < 6; i++) {
        out.push({ pilotId: null, speedMul: 1.1, hpMul: 1e4, shieldMul: 1e4, fireMul: 1.2 });
      }
      return out;
    }
    for (let i = 0; i < this.maxFighterSlots; i++) {
      const f = this.fighters[i];
      if (!f) continue;
      const pilot = this.pilotFor(f.pilotId);
      if (!pilot || pilot.kia) continue; // no living pilot → grounded
      const b = ftpBuff(pilot);
      out.push({
        pilotId: pilot.id,
        speedMul: 1,
        hpMul: b.hpMul,
        shieldMul: b.shieldMul,
        fireMul: b.fireMul,
      });
    }
    return out;
  }
  get playerFighterDroneShips() {
    return this.playerFighterDrones.ships;
  }
  get playerFighterDroneBullets() {
    return this.playerFighterDrones.bullets;
  }
  get playerFighterDroneMissiles() {
    return this.playerFighterDrones.missiles; // Mk VI SWL drone homing missiles
  }
  get playerMissiles() {
    return this.missileSystem.missiles;
  }
  /** Homing-missile salvo size, driven by the accumulated `missiles` upgrade effect.
   * Swarm-lord = Missile Pods; Mk VI Battleship = Missile Silos (both cap at MISSILE.maxCount).
   * 0 until the first missile upgrade is bought, which is what unlocks the skill button. */
  get maxMissiles(): number {
    if (this.isWorm) return this.wormMissileCount; // worm fires the shared salvo (red-tinted)
    // Swarm-lord and Mk VI Battleship use SEPARATE effect accumulators so a hull can
    // never inherit the other path's missile upgrades (Missile Pods vs Missile Silos).
    if (this.mkvPath === "swarmlord") return Math.min(MISSILE.maxCount, this.eff.missiles);
    if (this.hullTier >= 5) return Math.min(MISSILE.maxCount, this.eff.siloMissiles);
    return 0;
  }
  /** Fire the homing-missile salvo (the right-rail skill). No-op on cooldown, off a
   * Swarm-lord hull, in a safe zone, or off the planet layer. */
  launchMissileSalvo(): boolean {
    if (this.maxMissiles <= 0 || this.inSafeZone || this.layer !== "planet") return false;
    const m = this.mothership;
    // Only lock enemies within the cannons' reach (same engage area as the guns).
    const rangeSq = (CANNON.boltSpeed * CANNON.boltLife) ** 2;
    const inRange = this.hostiles.filter(
      (e) => e.hp > 0 && (e.pos.x - m.pos.x) ** 2 + (e.pos.y - m.pos.y) ** 2 <= rangeSq,
    );
    // Battleship shoot-to-mine: with NO enemy in reach, the salvo homes on the in-range
    // asteroids/moons (nearest first) so the player can mine with missiles.
    let rocks: MineableBody[] = [];
    if (inRange.length === 0 && this.mkvPath === "battleship" && this.planetLayer) {
      rocks = this.mineableBodies(this.planetLayer)
        .filter(
          (b) => b.ore > 0 && (b.pos.x - m.pos.x) ** 2 + (b.pos.y - m.pos.y) ** 2 <= rangeSq,
        )
        .sort(
          (a, b) =>
            (a.pos.x - m.pos.x) ** 2 +
            (a.pos.y - m.pos.y) ** 2 -
            ((b.pos.x - m.pos.x) ** 2 + (b.pos.y - m.pos.y) ** 2),
        );
    }
    return this.missileSystem.launch(this.maxMissiles, inRange, rocks, this.eff.missileCdMul);
  }
  /** Fighter-drone swarm size (Swarm-lord only) — from Drone Swarm upgrades (Mk V cap 12, Mk VI cap 18). */
  get maxFighterDroneSlots(): number {
    if (this.mkvPath !== "swarmlord") return 0;
    const cap = this.hullTier >= 5 ? MKVI.swl.fighterDrones : FIGHTER_DRONE.maxSlots;
    return Math.min(cap, this.eff.fighterDrones);
  }
  /** All active drones fly one shared FDC-buffed loadout (pilotless). */
  private buildDroneLoadouts(): FighterLoadout[] {
    const n = this.maxFighterDroneSlots;
    if (n <= 0) return [];
    const b = this.equippedFdc ? fdcBuff(this.equippedFdc) : null;
    const load: FighterLoadout = {
      pilotId: null,
      speedMul: b?.speedMul ?? 1,
      hpMul: b?.hpMul ?? 1,
      shieldMul: 1,
      fireMul: 0.5, // fighter drones fire their bolts at half the escort-fighter rate
    };
    return Array.from({ length: n }, () => ({ ...load }));
  }
  /** The FTP crew member with this id, if it exists and is a pilot. */
  private pilotFor(id: string | null): CrewMember | null {
    if (!id) return null;
    const c = this.crew.find((m) => m.id === id);
    return c && c.type === "ftp" ? c : null;
  }
  get equippedMdc(): CrewMember | null {
    if (!this.equippedMdcId) return null;
    return this.crew.find((m) => m.id === this.equippedMdcId && m.type === "mdc") ?? null;
  }
  get equippedRdc(): CrewMember | null {
    if (!this.equippedRdcId) return null;
    return this.crew.find((m) => m.id === this.equippedRdcId && m.type === "rdc") ?? null;
  }
  get equippedGc(): CrewMember | null {
    if (!this.equippedGcId) return null;
    return this.crew.find((m) => m.id === this.equippedGcId && m.type === "gc") ?? null;
  }
  get equippedFdc(): CrewMember | null {
    if (!this.equippedFdcId) return null;
    return this.crew.find((m) => m.id === this.equippedFdcId && m.type === "fdc") ?? null;
  }
  /** FTP unlocked once the hull is a Mk IV Battleship. */
  get ftpUnlocked(): boolean {
    return this.hullTier >= 3;
  }
  /** Gun Commander recruitable only on the Battleship MK V path. */
  get gcUnlocked(): boolean {
    return this.mkvPath === "battleship";
  }
  /** Fighter Drone Controller recruitable only on the Swarm-lord MK V path. */
  get fdcUnlocked(): boolean {
    return this.mkvPath === "swarmlord";
  }

  // --- Space Tavern (crew) ---
  recruitCrew(type: CrewType): boolean {
    if (this.currentService !== "tavern") return false;
    if (type === "ftp" && !this.ftpUnlocked) return false;
    if (type === "gc" && !this.gcUnlocked) return false;
    if (type === "fdc" && !this.fdcUnlocked) return false;
    if (!this.resources.spend({ units: RECRUIT_COST })) return false;
    this.crew.push(makeCrew(type));
    return true;
  }
  upgradeCrew(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id);
    if (!c || c.tier >= CREW_MAX_TIER) return false;
    if (!this.resources.spend({ units: crewUpgradeCost(c.tier) })) return false;
    c.tier += 1;
    if (c.type === "mdc") this.recomputeStats(); // MDC buff feeds the mining stats
    return true;
  }
  /** Re-roll a crew member's rarity/portrait/name (tier resets to 1). */
  rerollCrew(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id);
    if (!c) return false;
    if (!this.resources.spend({ units: REROLL_COST })) return false;
    rollCrewAgain(c);
    if (c.type === "mdc" && c.id === this.equippedMdcId) this.recomputeStats();
    return true;
  }
  equipMdc(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id && m.type === "mdc");
    if (!c) return false;
    this.equippedMdcId = id;
    this.recomputeStats();
    return true;
  }
  unequipMdc(): boolean {
    if (this.currentService !== "tavern") return false;
    this.equippedMdcId = null;
    this.recomputeStats();
    return true;
  }
  equipRdc(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id && m.type === "rdc");
    if (!c) return false;
    this.equippedRdcId = id;
    return true; // repair buff is read live each frame — no stat recompute needed
  }
  unequipRdc(): boolean {
    if (this.currentService !== "tavern") return false;
    this.equippedRdcId = null;
    return true;
  }
  equipGc(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id && m.type === "gc");
    if (!c) return false;
    this.equippedGcId = id;
    return true; // turret/cannon buff is read live each frame
  }
  unequipGc(): boolean {
    if (this.currentService !== "tavern") return false;
    this.equippedGcId = null;
    return true;
  }
  equipFdc(id: string): boolean {
    if (this.currentService !== "tavern") return false;
    const c = this.crew.find((m) => m.id === id && m.type === "fdc");
    if (!c) return false;
    this.equippedFdcId = id;
    return true; // fighter-drone buff is read live each frame
  }
  unequipFdc(): boolean {
    if (this.currentService !== "tavern") return false;
    this.equippedFdcId = null;
    return true;
  }
  /** Assign a recruited pilot to a fleet slot (from Fleet panel or Inventory —
   * a loadout choice available anywhere). */
  assignPilot(slot: number, crewId: string): boolean {
    if (slot < 0 || slot >= this.fighters.length || !this.fighters[slot]) return false;
    const p = this.pilotFor(crewId);
    if (!p || p.kia) return false; // a dead pilot can't be assigned
    // A pilot can only fly one fighter at a time — clear any other slot using it.
    for (const f of this.fighters) if (f && f.pilotId === crewId) f.pilotId = null;
    this.fighters[slot]!.pilotId = crewId;
    return true;
  }
  unassignPilot(slot: number): boolean {
    if (slot < 0 || slot >= this.fighters.length || !this.fighters[slot]) return false;
    this.fighters[slot]!.pilotId = null;
    return true;
  }
  /** A fighter was destroyed → its pilot is Killed In Action. Permanent (no respawn):
   * the bay stays assigned but grounded until the player rerolls the pilot at a
   * 🍺 tavern, which revives that same bay with a fresh pilot. */
  private killPilot(pilotId: string | null): void {
    if (!pilotId) return;
    const c = this.crew.find((m) => m.id === pilotId);
    if (c && c.type === "ftp") c.kia = true;
  }
  /** Manually muster the escort fighters into a ^ formation behind the mothership. */
  callFleetFormation(): void {
    this.playerFighters.callFormation();
    this.playerFighterDrones.callFormation(); // Swarm-lord drones ring up too
  }
  /** UI card for a crew member (name, rarity, tier, buff summary, upgrade cost). */
  private crewCard(m: CrewMember): CrewCard {
    const pct = (mul: number): string => `${Math.round((mul - 1) * 100)}%`;
    let buff: string;
    if (m.type === "mdc") {
      const b = mdcBuff(m);
      buff = `+${pct(b.mineSpeedMul)} mining · +${pct(b.cargoMul)} cargo`;
    } else if (m.type === "rdc") {
      const b = rdcBuff(m);
      buff = `+${pct(b.repairMul)} repair rate`;
    } else if (m.type === "gc") {
      const b = gcBuff(m);
      buff = `+${pct(b.damageMul)} gun dmg · +${pct(b.turnRateMul)} turn · +${pct(b.fireMul)} fire`;
    } else if (m.type === "fdc") {
      const b = fdcBuff(m);
      buff = `+${pct(b.damageMul)} drone dmg · +${pct(b.speedMul)} speed · +${pct(b.hpMul)} HP`;
    } else {
      const b = ftpBuff(m);
      buff = `+${pct(b.fireMul)} fire · +${pct(b.hpMul)} HP · +${pct(b.shieldMul)} shield`;
    }
    return {
      id: m.id,
      type: m.type,
      rarity: m.rarity,
      tier: m.tier,
      name: m.name,
      portrait: m.portrait,
      buff,
      upgradeCost: crewUpgradeCost(m.tier),
      canUpgrade: !m.kia && m.tier < CREW_MAX_TIER,
      kia: !!m.kia,
    };
  }
  /** Cruisers spawned in a planet layer: none until the 4th drive jump, then 1–2. */
  private get cruiserCount(): number {
    if (this.warps < CRUISER.fromWarp) return 0;
    return Math.min(
      CRUISER.maxPerLayer,
      1 + Math.floor((this.warps - CRUISER.fromWarp) * CRUISER.perWarp),
    );
  }
  /** Carriers spawned in a planet layer: none until the 6th drive jump, then 1. */
  private get carrierCount(): number {
    if (this.warps < ENEMY_CARRIER.fromWarp) return 0;
    return Math.min(
      ENEMY_CARRIER.maxPerLayer,
      1 + Math.floor((this.warps - ENEMY_CARRIER.fromWarp) * ENEMY_CARRIER.perWarp),
    );
  }

  // --- world-size progression (richer each warp jump) ---
  private get maxBigMoons(): number {
    return Math.min(PROGRESSION.bigMoonsBase + this.warps, PROGRESSION.bigMoonsMax);
  }
  private get maxSmallMoons(): number {
    return Math.min(PROGRESSION.smallMoonsBase + this.warps, PROGRESSION.smallMoonsMax);
  }
  private get maxPlanets(): number {
    return PROGRESSION.planetsBase + this.warps;
  }
  private get enemyCount(): number {
    return Math.min(
      ENEMY.maxCount,
      Math.round(ENEMY.baseCount + this.warps * ENEMY.perWarp),
    );
  }

  /** Buy an upgrade node if unlocked and affordable. */
  purchaseUpgrade(id: string): boolean {
    const node = UPGRADES_BY_ID[id];
    if (!node) return false;
    // Mechanical-only nodes (Mk II / Mk III, beams) require a mechanical planet.
    if (node.location === "mechanical" && this.currentService !== "mechanical") {
      return false;
    }
    const cores = node.cores ?? 0;
    if (
      !canPurchase(
        node,
        this.purchasedUpgrades,
        this.resources.bag.minerals,
        this.skillPoints,
        this.resources.bag.units,
      ) ||
      cores > this.itemCount("energyCore") ||
      !this.hasRequiredItems(node)
    ) {
      return false;
    }
    if (!this.resources.spend({ minerals: node.cost, units: node.units ?? 0 })) {
      return false;
    }
    this.skillPoints -= node.sp ?? 0;
    if (cores > 0) this.addItem("energyCore", -cores);
    if (node.requiresItems) {
      for (const [rid, rn] of Object.entries(node.requiresItems)) this.addItem(rid, -rn);
    }
    this.purchasedUpgrades.add(id);
    this.recomputeStats();
    this.applyShield(); // a refit can shift max shield (e.g. the Battleship +100 trait)
    // Tutorial: buying a mining-speed node clears the "buy upgrade" step; spending
    // a skill point on any node finishes the "spend skill" step.
    if (
      this.tutorialStep === TUTORIAL.BUY_UPGRADE &&
      node.effect.hullRateMul !== undefined
    ) {
      this.tutorialStep = TUTORIAL.WAIT_LEVEL2;
    }
    if (this.tutorialStep === TUTORIAL.SPEND_SKILL && (node.sp ?? 0) > 0) {
      this.tutorialStep = TUTORIAL.DONE;
    }
    return true;
  }

  /** All item requirements (e.g. the MK VII EOW parts) satisfied? */
  private hasRequiredItems(node: { requiresItems?: Record<string, number> }): boolean {
    if (!node.requiresItems) return true;
    for (const [id, n] of Object.entries(node.requiresItems)) {
      if (this.itemCount(id) < n) return false;
    }
    return true;
  }

  /** Deposit minerals with the economy (yield) multiplier applied. */
  private depositMinerals(amount: number): void {
    this.resources.add("minerals", amount * this.eff.yieldMul);
  }

  /** Drive the MK VII worm each frame: trail the body behind the head, run the maw beam +
   * segment turrets against foes, and eat any rock/moon a segment passes through. */
  private updatePlayerWorm(dt: number): void {
    const w = this.playerWorm;
    if (!w) return;
    const planet = this.layer !== "system" ? this.planetLayer : null;
    w.update(dt, {
      headX: this.mothership.pos.x,
      headY: this.mothership.pos.y,
      headHeading: this.mothership.heading,
      foes: planet ? this.hostiles : [],
      bodies: planet ? this.mineableBodies(planet) : [],
      combat: this.combatActive,
      consumeBody: (b) => this.consumeWormBody(b),
      onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      beamDurMul: this.eff.wormBeamDurMul,
      beamDmgMul: this.eff.wormBeamDmgMul,
    });
    // The rift AND the coil script the worm's own head — sync the mothership (camera + control
    // origin) to it so the camera follows the head into the coil and play resumes seamlessly.
    if (w.riftControlsHead || w.coilActive) {
      const h = w.segments[0]!;
      this.mothership.pos.set(h.pos.x, h.pos.y);
      this.mothership.heading = h.heading;
    }
  }

  /** The worm swallows a body whole: credit all its ore, then let the depletion pass
   * detonate + drop it this frame. */
  private consumeWormBody(b: { ore: number }): void {
    if (b.ore <= 0) return;
    this.depositMinerals(b.ore);
    b.ore = 0;
  }

  /** Fire the MK VII maw beam (manual ability button → charge → big beam → fan). */
  fireWormBeam(): void {
    if (this.isWorm && !this.inSafeZone) this.playerWorm?.fire();
  }

  /** Fire the MK VII red homing-missile salvo (manual ability button) — reuses the shared
   * salvo system (streams from the mouth, homes, trails), tinted red. */
  fireWormMissiles(): void {
    if (this.isWorm) this.launchMissileSalvo();
  }

  /** Two missile-launch points at the sides of the worm's mouth (fed to the shared salvo). */
  private wormMouthSilos(): { x: number; y: number }[] {
    const m = this.mothership;
    const hx = Math.cos(m.heading);
    const hy = Math.sin(m.heading);
    const px = -hy;
    const py = hx;
    const fwd = MKVII.headWorldHeight * 0.35;
    const side = 28;
    return [
      { x: m.pos.x + hx * fwd + px * side, y: m.pos.y + hy * fwd + py * side },
      { x: m.pos.x + hx * fwd - px * side, y: m.pos.y + hy * fwd - py * side },
    ];
  }

  // --- Mk VIII "Time & Space Rift" (manual teleport ability) ---
  riftArmed = false; // armed → the next minimap tap sets the teleport exit point

  /** True once the rift upgrade node is bought (enables the ability button). */
  get riftUnlocked(): boolean {
    return this.isMkviii && this.purchasedUpgrades.has("mkviii_rift");
  }

  /** Arm/disarm the rift — while armed, the next minimap tap sets the exit and fires it. */
  armRift(): void {
    if (!this.riftUnlocked || this.inSafeZone || !(this.playerWorm?.riftReady ?? false)) {
      this.riftArmed = false;
      return;
    }
    this.riftArmed = !this.riftArmed;
  }

  /** A minimap tap (world coords) — if armed + ready, carve the rift and teleport there. */
  riftTarget(worldX: number, worldY: number): void {
    if (!this.riftArmed) return;
    this.riftArmed = false;
    if (this.riftUnlocked && !this.inSafeZone) this.playerWorm?.fireRift(worldX, worldY);
  }

  /** True while the rift is carrying the worm through it (player control is suspended). */
  get riftBusy(): boolean {
    return !!this.playerWorm?.riftControlsHead;
  }

  /** True once the Maelstrom Coil node is bought (enables the ability button). */
  get coilUnlocked(): boolean {
    return this.isMkviii && this.purchasedUpgrades.has("mkviii_coil");
  }

  /** Fire the Maelstrom Coil (manual ability — no target; the worm curls up where it stands). */
  fireCoil(): void {
    if (this.coilUnlocked && !this.inSafeZone) this.playerWorm?.fireCoil();
  }

  /** True while the coil is running — player movement is suspended (rooted stance). */
  get coilBusy(): boolean {
    return !!this.playerWorm?.coilActive;
  }

  // --- simulation step ---
  update(dt: number): void {
    // The Eater's awakening cutscene freezes the whole world (player, NPCs, moons) and
    // runs its own timeline (dialogue → planet blast → the worm emerging).
    if (this.eaterPhase !== "none") {
      this.updateAwakening(dt);
      if (this.eaterPhase !== "none") return; // still in the cutscene → world stays frozen
    }
    // The jump cutscene ALSO freezes the world (planets stop revolving) and runs its own timeline
    // (spaghettify → dive through the rift → emerge in a new system).
    if (this.warpPhase !== "none") {
      this.updateWarpJump(dt);
      return; // world frozen; the cutscene drives the ship + rift itself
    }
    if (this.paused) return; // frozen while the upgrade menu is open
    // Hull destroyed → blow up once, then freeze until the player respawns.
    if (this.mothership.hp <= 0 && !this.playerDead) this.destroyPlayer();
    if (this.playerDead) return;
    this.elapsed += dt;
    if (!this.attract) {
      this.advanceTutorial();
      this.checkQuest();
      this.checkMkvIntro();
    }
    this.shieldFlash = Math.max(0, this.shieldFlash - dt * 3);
    this.combatActive = false;
    if (this.layer === "system" || !this.planetLayer) {
      this.system.update(dt);
      if (!this.riftBusy && !this.coilBusy && !this.attract) this.moveMothership(dt); // rift/coil/attract drive the ship themselves
      // Enter a planet only once the ship has flown into its range.
      if (this.systemTargetId) {
        const p = this.system.byId(this.systemTargetId);
        if (p) {
          const r = p.radius + SERVICE.interactMargin;
          if (this.mothership.pos.distanceSqTo(p.pos) <= r * r) {
            this.travelToPlanet(this.systemTargetId);
            return;
          }
        } else {
          this.systemTargetId = null;
        }
      }
      this.miningTargets = [];
      this.miningImpact = null;
      this.updatePlayerWorm(dt); // MK VII body keeps trailing the head in the system view
      return;
    }
    const layer = this.planetLayer;
    layer.update(dt);
    this.updateMoonTurrets(dt);
    this.station?.update(dt, {
      target: this.mothership,
      playerAlive: !this.playerDead && this.mothership.hp > 0,
      safe: this.inSafeZone,
      damagePlayer: (dmg, x, y) => this.damagePlayer(dmg, x, y),
      onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
    });
    this.driveStationHangars(dt); // ring hangars launch fighters + a cruiser
    if (this.station?.defeated && this.fromPlanetId && !this.stationDefeated.has(this.fromPlanetId)) {
      this.stationDefeated.add(this.fromPlanetId); // it stays a burning wreck; won't re-arm on return
      this.noticeText = "☠ Orbital ring-station destroyed!";
    }
    if (!this.riftBusy && !this.coilBusy && !this.attract) this.moveMothership(dt); // rift/coil/attract drive the ship themselves
    this.updateDefense(dt, layer);
    if (this.hub) this.updateHubRemotes(dt); // interpolate the other pilots' ships
    if (this.attract) this.updateAttract(dt); // menu preview: auto-fire the heavy guns + refill the swarm
    // In combat (a fighter/cruiser/moon-turret is aggro'd) the carrier's mining
    // lasers + drones go dark — the crew is fighting, not mining.
    this.combatActive =
      this.attract || // menu preview: keep the escort fighters permanently deployed
      this.enemyShips.some((e) => e.hp > 0 && e.aggro) ||
      this.cruiserShips.some((e) => e.hp > 0 && e.aggro) ||
      this.carrierShips.some((e) => e.hp > 0 && e.aggro) ||
      this.wormSegments.some((s) => s.hp > 0) ||
      this.liveMoonTurrets.some((t) => t.aggro) ||
      (this.station != null && !this.station.defeated); // the ring-station boss is a fight
    // Auto-mine: steer to the nearest ore (combatActive gates it — pause in a fight, resume after).
    this.updateAutoMine();
    // Battleship trades the mining laser for shoot-to-mine (its guns carve ore).
    if (this.hullMiningEnabled && !this.combatActive && this.mkvPath !== "battleship" && !this.isWorm) {
      const frame = this.mining.update(
        dt,
        this.mothership,
        this.mineableBodies(layer),
        (amt) => this.depositMinerals(amt),
        this.eff.miningRange,
        this.eff.beamRate,
        this.eff.beamCount,
        (x, y, amt) => this.emitFloater(x, y, amt, "mineral"),
        this.eff.omniMining,
      );
      this.miningTargets = frame.targets;
      this.miningImpact = frame.targets[0]?.impacts[0] ?? null;
    } else {
      this.mining.reset();
      this.miningTargets = [];
      this.miningImpact = null;
    }
    this.updatePlayerWorm(dt); // MK VII: trail the body, run the maw beam + turrets, eat rocks
    this.updateDroneBay(dt, layer);
    if (this.npcs) {
      this.npcs.update(
        dt,
        this.mineableBodies(layer),
        (id) => layer.byId(id),
        (b) => this.onNpcDeplete(b, layer),
        (x, y) => this.onNpcDeath(x, y),
      );
    }
    if (this.enemies) {
      this.enemies.update(dt, {
        playerX: this.mothership.pos.x,
        playerY: this.mothership.pos.y,
        playerRadius: this.playerHitRadius,
        playerAlive: this.mothership.hp > 0,
        allies: this.playerFighterShips,
        bodies: layer.bodies,
        onHitPlayer: (dmg, x, y) => this.damagePlayer(dmg, x, y),
        onDeath: (x, y) => this.onEnemyDeath(x, y),
      });
    }
    if (this.cruisers) {
      this.cruisers.update(dt, {
        warp: this.warps,
        onCallBackup: (x, y) => this.onCruiserCallBackup(x, y),
        playerX: this.mothership.pos.x,
        playerY: this.mothership.pos.y,
        playerRadius: this.playerHitRadius,
        playerAlive: this.mothership.hp > 0,
        playerSpeed: this.eff.shipSpeed,
        bodies: layer.bodies,
        onHitPlayer: (dmg, x, y) => this.damagePlayer(dmg, x, y),
        onDeath: (x, y) => this.onCruiserDeath(x, y),
      });
    }
    if (this.carriers) {
      this.carriers.update(dt, {
        warp: this.warps,
        onCallBackup: (x, y) => this.onCruiserCallBackup(x, y), // reuse the jump-in squad
        onLaunchFighter: (x, y, h) => this.onCarrierLaunchFighter(x, y, h),
        playerX: this.mothership.pos.x,
        playerY: this.mothership.pos.y,
        playerRadius: this.playerHitRadius,
        playerAlive: this.mothership.hp > 0,
        playerSpeed: this.eff.shipSpeed,
        bodies: layer.bodies,
        onHitPlayer: (dmg, x, y) => this.damagePlayer(dmg, x, y),
        onDeath: (x, y) => this.onCarrierDeath(x, y),
      });
    }
    if (this.worm && this.worm.active) {
      this.worm.update(dt, this.wormCtx(layer));
    }
    // Escort fighters deploy & fight while combat is active (idle-dock otherwise).
    // Side hangar gates (Mk IV), in world space — fighters launch from these.
    const fsh = Math.sin(this.mothership.heading);
    const fchh = Math.cos(this.mothership.heading);
    const fgs = (MOTHERSHIP.spriteWorldHeight * this.hullRenderMul) / 512;
    const fgate = (ox: number, oy: number): { x: number; y: number } => ({
      x: this.mothership.pos.x + fgs * (ox * fsh + oy * fchh),
      y: this.mothership.pos.y + fgs * (ox * -fchh + oy * fsh),
    });
    // Swarm-lord launches from the two WING RUNWAYS (fly forward out the nose) and
    // recalls into spinning defensive rings; other hulls use the side hangars + ^.
    const swl = this.mkvPath === "swarmlord";
    // Mustering the recall formation out of combat launches from BOTH wings at once
    // (1 per wing per wave = 4 units with fighters + drones). In combat the runways
    // fire 2 side-by-side out of one wing at a time.
    const muster = swl && this.playerFighters.formationActive && !this.combatActive;
    const gates = !swl
      ? [fgate(-98, 20), fgate(98, 20)] // Mk IV side hangars
      : this.hullTier >= 5
        ? (this.isMkviiSwl ? MKVI.swl7Hangars : MKVI.swlHangars).map((g) => fgate(g.ox, g.oy)) // Swarm-lord: 8 mapped wing hangars
        : muster
          ? [fgate(-121, -52), fgate(121, -52), fgate(-116, -40), fgate(116, -40)] // L,R,L,R
          : [fgate(-132, -52), fgate(-110, -52), fgate(110, -52), fgate(132, -52)]; // L,L,R,R
    const launchBatch = swl ? 2 : 1;
    const incoming = [
      ...this.enemyBullets,
      ...this.cruiserBullets,
      ...this.carrierBullets,
      ...this.wormBullets,
      ...this.moonTurretBullets,
    ];
    this.playerFighters.update(dt, this.buildFighterLoadouts(), {
      originX: this.mothership.pos.x,
      originY: this.mothership.pos.y,
      heading: this.mothership.heading,
      gates,
      launchForward: swl,
      launchBatch,
      ringFormation: false, // escort fighters keep the ^ chevron; only drones ring up
      active: this.combatActive,
      hostiles: this.hostiles,
      raiders: this.enemyShips,
      incoming,
      bodies: layer.bodies,
      onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      onFighterDown: (x, y) => {
        this.explosions.push({ x, y, radius: 26, big: false });
        this.pushCombatFx(x, y, "kill");
      },
      onPilotKilled: (pilotId) => this.killPilot(pilotId),
      canMissile: this.eff.fighterMissiles, // Mk VI SWL: fighters fire wing homing missiles
    });
    // Fighter drones (Swarm-lord only) — pilotless mini-fighters buffed by the FDC.
    // They form the spinning defensive rings and fire at anything in their arc.
    this.playerFighterDrones.update(dt, this.buildDroneLoadouts(), {
      originX: this.mothership.pos.x,
      originY: this.mothership.pos.y,
      heading: this.mothership.heading,
      gates,
      launchForward: swl,
      launchBatch,
      ringFormation: swl,
      damageMul: this.equippedFdc ? fdcBuff(this.equippedFdc).damageMul : 1,
      active: this.combatActive,
      hostiles: this.hostiles,
      raiders: this.enemyShips,
      incoming,
      bodies: layer.bodies,
      onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      onFighterDown: (x, y) => {
        this.explosions.push({ x, y, radius: 18, big: false });
        this.pushCombatFx(x, y, "hit");
      },
      onPilotKilled: () => {}, // drones carry no pilot
      canMissile: this.eff.droneMissiles, // Mk VI SWL: drones fire 1 homing missile / 15s
      missileCount: 1,
    });
    // Mk VII SWL Rift Swarm — tick the cooldown + rift-portal VFX, then fly the purple homing
    // arrows: each pierces enemies it passes through (short per-arrow cooldown) and re-targets
    // every few seconds (or instantly when its target dies) until its lifetime runs out.
    if (this.riftSwarmCd > 0) this.riftSwarmCd -= dt;
    const R = MKVI.swl7Rift;
    // Each open rift streams arrows out in waves: `perRift` every `batchInterval`s until it has
    // loosed `maxPerRift` (4 at a time, up to 20 per rift), then hangs a moment before fading.
    for (let i = this.riftPortals.length - 1; i >= 0; i--) {
      const p = this.riftPortals[i]!;
      p.age += dt;
      if (p.batchCd > 0) p.batchCd -= dt;
      if (p.emitted < R.maxPerRift && p.batchCd <= 0) {
        const n = Math.min(R.perRift, R.maxPerRift - p.emitted);
        for (let k = 0; k < n; k++) {
          this.riftArrows.push({
            pos: new Vector2(p.x, p.y),
            heading: p.heading + (Math.random() - 0.5) * 1.4, // fan out from the rift mouth
            target: null,
            retarget: R.retarget,
            life: R.lifetime,
            hitCd: 0,
            trail: [],
          });
        }
        p.emitted += n;
        p.batchCd = R.batchInterval;
      }
      if (p.age >= R.portalLife) this.riftPortals.splice(i, 1);
    }
    if (this.riftArrows.length > 0) {
      const speed = PLAYER_FIGHTER.speed * R.arrowSpeedMul;
      const foes = this.hostiles;
      const nearestFoe = (x: number, y: number, except: { hp: number } | null) => {
        let best: { pos: Vector2; hp: number } | null = null;
        let bd = Infinity;
        for (const h of foes) {
          if (h.hp <= 0 || h === except) continue;
          const d = (h.pos.x - x) ** 2 + (h.pos.y - y) ** 2;
          if (d < bd) {
            bd = d;
            best = h;
          }
        }
        return best;
      };
      for (let i = this.riftArrows.length - 1; i >= 0; i--) {
        const ar = this.riftArrows[i]!;
        ar.life -= dt;
        ar.retarget -= dt;
        ar.hitCd -= dt;
        if (ar.life <= 0) {
          this.riftArrows.splice(i, 1);
          continue;
        }
        // Target: re-acquire instantly if none/dead, else re-pick every `retarget` seconds.
        if (!ar.target || ar.target.hp <= 0) {
          ar.target = nearestFoe(ar.pos.x, ar.pos.y, null);
          ar.retarget = R.retarget;
        } else if (ar.retarget <= 0) {
          ar.target = nearestFoe(ar.pos.x, ar.pos.y, ar.target) ?? ar.target;
          ar.retarget = R.retarget;
        }
        // Fast homing toward the target, then fly forward at 1.7× fighter speed.
        if (ar.target) {
          const desired = Math.atan2(ar.target.pos.y - ar.pos.y, ar.target.pos.x - ar.pos.x);
          const diff = Math.atan2(Math.sin(desired - ar.heading), Math.cos(desired - ar.heading));
          const mt = R.turnRate * dt;
          ar.heading += diff > mt ? mt : diff < -mt ? -mt : diff;
        }
        ar.pos.x += Math.cos(ar.heading) * speed * dt;
        ar.pos.y += Math.sin(ar.heading) * speed * dt;
        ar.trail.push({ x: ar.pos.x, y: ar.pos.y });
        if (ar.trail.length > R.trailLen) ar.trail.shift();
        // Pierce-damage any foe it flies through, then swing toward a different one.
        if (ar.hitCd <= 0) {
          for (const h of foes) {
            if (h.hp <= 0) continue;
            if ((h.pos.x - ar.pos.x) ** 2 + (h.pos.y - ar.pos.y) ** 2 < R.hitRadius * R.hitRadius) {
              h.hp -= R.damage;
              this.pushCombatFx(ar.pos.x, ar.pos.y, "hit");
              ar.hitCd = R.hitInterval;
              const next = nearestFoe(ar.pos.x, ar.pos.y, h);
              if (next) {
                ar.target = next;
                ar.retarget = R.retarget;
              }
              break;
            }
          }
        }
      }
    }
    // Homing-missile salvo (Swarm-lord skill) — streams out of the wings, homes,
    // detonates on contact / after its life; the salvo cooldown ticks here too.
    {
      const mm = this.mothership;
      const msh = Math.sin(mm.heading);
      const mch = Math.cos(mm.heading);
      const mgs = (MOTHERSHIP.spriteWorldHeight * this.hullRenderMul) / 512;
      const wing = (ox: number, oy: number) => ({
        x: mm.pos.x + mgs * (ox * msh + oy * mch),
        y: mm.pos.y + mgs * (ox * -mch + oy * msh),
      });
      // Mk VI Battleship launches from its 6 mapped hull silos (2 at a time). The
      // Swarm-lord leaves this undefined and streams out of its two wing runways.
      const silos = this.isWorm
        ? this.wormMouthSilos() // Mk VII: two silos at the sides of the mouth
        : this.hullTier >= 5 && this.mkvPath === "battleship"
          ? MKVI.missileSilos.map((s) => wing(s.ox, s.oy))
          : this.hullTier >= 5 && this.mkvPath === "swarmlord"
            ? (this.isMkviiSwl ? MKVI.swl7Missiles : MKVI.swlMissiles).map((s) => wing(s.ox, s.oy)) // Swarm-lord: mapped hardpoints
            : undefined;
      this.missileSystem.update(dt, {
        heading: mm.heading,
        leftInner: wing(-65, -45),
        leftOuter: wing(-165, -115),
        rightInner: wing(65, -45),
        rightOuter: wing(165, -115),
        silos,
        enemies: this.hostiles,
        // Battleship shoot-to-mine: mining missiles carve ore + can finish a moon.
        bodies: this.planetLayer ? this.mineableBodies(this.planetLayer) : [],
        onOreMined:
          this.mkvPath === "battleship" ? (amt) => this.depositMinerals(amt) : undefined,
        onMoonKill: (b) => this.onCannonMoonKill(b),
        onDetonate: (x, y) => {
          this.explosions.push({ x, y, radius: MISSILE.blastRadius, big: false, blue: !this.isWorm });
          this.pushCombatFx(x, y, "hit");
        },
        onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
        // Branch missiles: Swarm-lord + Mk VII worm only (never Mk VI Battleship).
        branchChance: this.mkvPath === "swarmlord" || this.isWorm ? this.eff.branchChance : 0,
        branchCount: this.mkvPath === "swarmlord" || this.isWorm ? this.eff.branchCount : 0,
        smart: this.eff.smartMissiles, // Mk VI SWL Smart Missiles: re-acquire on target death
      });
    }
    // Turrets — only on the Mk II hull onward. They lock onto neutral miners AND
    // hostile raiders (nearest in range).
    if (this.purchasedUpgrades.has("mk2")) {
      const mul = this.hullRenderMul;
      const gcb = this.equippedGc ? gcBuff(this.equippedGc) : null; // Gun Commander buff
      this.turretSystem.update(dt, {
        shipX: this.mothership.pos.x,
        shipY: this.mothership.pos.y,
        heading: this.mothership.heading,
        renderH: MOTHERSHIP.spriteWorldHeight * mul,
        equipped: this.equipped,
        mountOffsets: mountsForTier(this.hullTier, this.mkvPath, this.isMkviiSwl),
        targets: [
          ...(this.npcs?.ships ?? []),
          ...this.enemyShips,
          ...this.cruiserShips,
          ...this.carrierShips,
          ...this.wormSegments.filter((s) => s.hp > 0),
          ...this.liveMoonTurrets,
          ...(this.station?.liveWeapons() ?? []), // player turrets auto-fire on station guns
        ],
        enabled: this.cannonsEnabled && !this.inSafeZone, // no turret fire in a safe zone
        dmgMul: gcb?.damageMul,
        fireMul: gcb?.fireMul,
        turnMul: gcb?.turnRateMul,
        rangeMul: this.eff.turretRangeMul, // Battleship turret-range upgrades
        onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      });
    }
    if (this.cannonsUnlocked) {
      const m = this.mothership;
      const safe = this.inSafeZone; // at a service planet → guns render but hold fire
      const bts = this.mkvPath === "battleship";
      // Player-directed aim (a locked body/enemy, or a manual crosshair point). On
      // the Battleship this commands the BIG cannons; elsewhere the spinal cannons.
      let pAim: { x: number; y: number } | null = null;
      let pLocked = false;
      let pEnemy = false;
      if (!safe) {
        if (this.cannonTargetBodyId) {
          const b = layer.bodies.find((x) => x.id === this.cannonTargetBodyId);
          if (b && b.ore > 0 && !this.protectedMoonIds.has(b.id)) {
            pAim = { x: b.pos.x, y: b.pos.y };
            pLocked = true;
          } else this.cannonTargetBodyId = null;
        }
        if (!pAim && this.cannonTargetEnemy) {
          const e = this.cannonTargetEnemy;
          if (e.hp > 0) {
            // Battleship big cannons aim DIRECTLY at the enemy ship (no lead); other
            // hulls' spinal cannons lead the target since their bolts travel slower.
            pAim = bts ? { x: e.pos.x, y: e.pos.y } : this.leadAim(e);
            pLocked = true;
            pEnemy = true;
          } else this.cannonTargetEnemy = null;
        }
        if (!pAim && this.cannonAim) {
          pAim = { x: this.cannonAim.x, y: this.cannonAim.y }; // manual point → waits for 🔥
        }
      }
      // Auto-combat aim: the nearest aggro hostile (like the turrets).
      let acAim: { x: number; y: number } | null = null;
      let acEnemy = false;
      if (!safe && this.combatActive) {
        let bestD = Infinity;
        let be: Damageable | null = null;
        for (const e of this.hostiles) {
          if (e.hp <= 0 || !(e as { aggro?: boolean }).aggro) continue;
          const d = (e.pos.x - m.pos.x) ** 2 + (e.pos.y - m.pos.y) ** 2;
          if (d < bestD) {
            bestD = d;
            be = e;
          }
        }
        if (be) {
          acAim = this.leadAim(be);
          acEnemy = true;
        }
      }
      // Reticle: the player-directed aim (locked pick or manual point).
      this.cannonReticle =
        !safe && pAim && (pLocked || this.cannonAim)
          ? { x: pAim.x, y: pAim.y, locked: pLocked }
          : null;
      const cgc = this.equippedGc ? gcBuff(this.equippedGc) : null; // Gun Commander buff
      // Spinal cannons: Battleship runs them AUTO (the aim button drives the big
      // cannons); other hulls use the player aim, else auto-combat, else idle.
      let sAim: { x: number; y: number } | null;
      let sFire: boolean;
      let sEnemy: boolean;
      let sBodyTargets: { x: number; y: number }[] | undefined;
      // In-range mineable rocks (nearest first) — the Battleship's guns SPREAD across
      // these when shoot-to-mining with no enemy present (one gun per rock; a lone
      // rock draws every gun). Shared by the spinal cannons AND the Big Cannons.
      let rockTargets: { x: number; y: number }[] = [];
      if (bts && !safe) {
        const reachSq = (CANNON.boltSpeed * CANNON.boltLife) ** 2;
        const inR: { x: number; y: number; d: number }[] = [];
        for (const b of layer.bodies) {
          if (b.ore <= 0 || this.protectedMoonIds.has(b.id)) continue;
          const d = (b.pos.x - m.pos.x) ** 2 + (b.pos.y - m.pos.y) ** 2;
          if (d <= reachSq) inR.push({ x: b.pos.x, y: b.pos.y, d });
        }
        inR.sort((a, b) => a.d - b.d);
        rockTargets = inR.map((r) => ({ x: r.x, y: r.y }));
      }
      if (bts) {
        // Battleship spinal cannons run automatically: shoot the nearest aggro enemy
        // in combat (all guns focus it), else spread across the in-range rocks to mine.
        if (acAim) {
          sAim = acAim;
          sFire = true;
          sEnemy = acEnemy;
        } else {
          sAim = rockTargets[0] ?? null;
          sFire = rockTargets.length > 0;
          sEnemy = false;
          sBodyTargets = rockTargets.length > 0 ? rockTargets : undefined;
        }
      } else if (pLocked) {
        sAim = pAim;
        sFire = true;
        sEnemy = pEnemy;
      } else if (acAim) {
        sAim = acAim;
        sFire = true;
        sEnemy = acEnemy;
      } else {
        sAim = pAim;
        sFire = false; // manual point → waits for the 🔥 button
        sEnemy = false;
      }
      this.cannonSystem.update(dt, {
        shipX: m.pos.x,
        shipY: m.pos.y,
        heading: m.heading,
        renderH: MOTHERSHIP.spriteWorldHeight * this.hullRenderMul,
        equipped: this.equippedCannons,
        mounts: this.cannonMounts,
        dmgMul: cgc?.damageMul,
        fireMul: cgc?.fireMul,
        turnMul: cgc?.turnRateMul,
        aim: sAim,
        autoFire: sFire,
        enemyTarget: sEnemy,
        bodyTargets: sBodyTargets, // mining spread: one rock per cannon
        bodies: this.mineableBodies(layer), // protected moons are indestructible until their turret dies
        enemies: this.hostiles,
        // Battleship "shoot-to-mine": carved ore is credited as minerals.
        onOreMined:
          this.mkvPath === "battleship" ? (amt) => this.depositMinerals(amt) : undefined,
        onMoonKill: (b) => this.onCannonMoonKill(b),
        onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      });
      // Big Cannons (Battleship). The player's aim/fire commands them; with no lock
      // they auto-target the nearest hostiles (split across the 2 guns). In a safe
      // zone they still render but hold fire (no enemies, no aim).
      this.bigCannonSystem.update(dt, {
        shipX: m.pos.x,
        shipY: m.pos.y,
        heading: m.heading,
        renderH: MOTHERSHIP.spriteWorldHeight * this.hullRenderMul,
        equipped: this.equippedBigCannons,
        mounts: this.bigCannonMounts,
        dmgMul: cgc?.damageMul,
        fireMul: cgc?.fireMul,
        turnMul: cgc?.turnRateMul,
        beamMul: this.eff.bigCannonBeamMul, // Mk VI Beam Capacitor upgrades
        enemies: safe ? [] : this.hostiles,
        bodies: this.mineableBodies(layer),
        bodyTargets: rockTargets.length > 0 ? rockTargets : undefined, // mine when no enemy
        playerAim: bts && !safe ? pAim : null,
        playerAutoFire: pLocked, // locked pick → auto-fire; manual point → wait for 🔥
        onOreMined:
          this.mkvPath === "battleship" ? (amt) => this.depositMinerals(amt) : undefined,
        onMoonKill: (b) => this.onCannonMoonKill(b),
        onHit: (x, y) => this.pushCombatFx(x, y, "hit"),
      });
    }
    this.checkDepletions(layer);
  }

  get cannonsUnlocked(): boolean {
    return this.purchasedUpgrades.has("mk4");
  }
  /** Cannon slots for the current hull: Mk VI Battleship 8, Mk V Battleship 4,
   * Swarm-lord 2, else Carrier 3. */
  get maxCannonSlots(): number {
    if (this.mkvPath === "battleship") return this.hullTier >= 5 ? MKVI.bts.cannons : 4;
    if (this.mkvPath === "swarmlord") return this.hullTier >= 5 ? MKVI.swl.cannons : 2;
    return this.cannonsUnlocked ? CANNON.slots : 0;
  }
  /** Cannon mount offsets for the current hull (per MK V path / Mk VI). */
  get cannonMounts(): { ox: number; oy: number }[] {
    if (this.mkvPath === "battleship")
      return this.hullTier >= 5 ? CANNON.mountsBtsVI : CANNON.mountsBts;
    if (this.mkvPath === "swarmlord")
      return this.hullTier >= 5
        ? (this.isMkviiSwl ? CANNON.mountsSwl7 : CANNON.mountsSwlVI)
        : CANNON.mountsSwl;
    return CANNON.mounts;
  }
  /** True once at least one spinal cannon is equipped (so aim/fire is usable). */
  get cannonsReady(): boolean {
    // The worm (Mk VII/VIII EOW) has no spinal cannons — hide the aim/fire controls for it.
    return !this.isWorm && this.cannonsUnlocked && this.equippedCannons.some((c) => c !== null);
  }
  private get nextCannonSlotCost(): number {
    // Cannon slots CONTINUE the turret slot-fee curve past the 8th turret port —
    // so the 1st cannon slot is priced as if it were the 9th turret slot, etc.
    const arr = TURRET.slotUnlockCost;
    const last = arr[arr.length - 1] ?? 0;
    const step = 260; // rising step for each slot beyond the 8 turret ports
    const beyond = 1 + this.unlockedCannonSlots.size; // 9th, 10th, 11th slot...
    return last + beyond * step;
  }
  /** Mechanical yard: buy one spinal cannon (stored as an item to equip). */
  buyCannon(): boolean {
    if (this.currentService !== "mechanical") return false;
    if (!this.resources.spend({ units: CANNON.buyCost })) return false;
    this.addItem("cannon_basic", 1);
    return true;
  }
  unlockCannonSlot(slot: number): boolean {
    if (this.currentService !== "mechanical" || !this.cannonsUnlocked) return false;
    if (slot < 0 || slot >= this.maxCannonSlots) return false;
    if (this.unlockedCannonSlots.has(slot)) return false;
    if (this.unlockedCannonSlots.size >= this.maxCannonSlots) return false;
    if (!this.resources.spend({ units: this.nextCannonSlotCost })) return false;
    this.unlockedCannonSlots.add(slot);
    return true;
  }
  equipCannon(slot: number, id: string): boolean {
    if (this.currentService !== "mechanical") return false;
    if (!this.unlockedCannonSlots.has(slot) || this.equippedCannons[slot]) return false;
    if (this.itemCount(id) < 1) return false;
    this.addItem(id, -1);
    this.equippedCannons[slot] = id;
    return true;
  }
  unequipCannon(slot: number): boolean {
    if (this.currentService !== "mechanical") return false;
    const cur = this.equippedCannons[slot];
    if (!cur) return false;
    this.addItem(cur, 1);
    this.equippedCannons[slot] = null;
    return true;
  }

  // ── Big Cannons (Mk V Battleship, centre-spine super-guns) ──────────────────
  /** True on the Battleship hull, where the 2 Big-Cannon slots exist. */
  get bigCannonsUnlocked(): boolean {
    return this.mkvPath === "battleship";
  }
  get maxBigCannonSlots(): number {
    if (this.mkvPath !== "battleship") return 0;
    return this.hullTier >= 5 ? MKVI.bts.bigCannons : 2; // Mk V bts = 2, Mk VI = 4
  }
  /** Big-cannon mount offsets — Mk V: 2 on the centre spine; Mk VI: 4 on the flanks. */
  get bigCannonMounts(): { ox: number; oy: number }[] {
    if (this.mkvPath !== "battleship") return [];
    return this.hullTier >= 5 ? CANNON.bigMountsBtsVI : CANNON.bigMountsBts;
  }
  get bigCannonsReady(): boolean {
    return this.bigCannonsUnlocked && this.equippedBigCannons.some((c) => c !== null);
  }
  private get nextBigCannonSlotCost(): number {
    return BIG_CANNON.slotUnlockCost[this.unlockedBigCannonSlots.size] ?? 0;
  }
  unlockBigCannonSlot(slot: number): boolean {
    if (this.currentService !== "mechanical" || !this.bigCannonsUnlocked) return false;
    if (slot < 0 || slot >= this.maxBigCannonSlots) return false;
    if (this.unlockedBigCannonSlots.has(slot)) return false;
    if (this.unlockedBigCannonSlots.size >= this.maxBigCannonSlots) return false;
    if (!this.resources.spend({ units: this.nextBigCannonSlotCost })) return false;
    this.unlockedBigCannonSlots.add(slot);
    return true;
  }
  /** Install a crafted Big Cannon (item "cannon_big") into an unlocked slot. */
  equipBigCannon(slot: number, id = "cannon_big"): boolean {
    if (this.currentService !== "mechanical") return false;
    if (!this.unlockedBigCannonSlots.has(slot) || this.equippedBigCannons[slot]) return false;
    if (this.itemCount(id) < 1) return false;
    this.addItem(id, -1);
    this.equippedBigCannons[slot] = id;
    return true;
  }
  unequipBigCannon(slot: number): boolean {
    if (this.currentService !== "mechanical") return false;
    const cur = this.equippedBigCannons[slot];
    if (!cur) return false;
    this.addItem(cur, 1);
    this.equippedBigCannons[slot] = null;
    return true;
  }
  /** Aim button, then tap: lock the spinal cannons onto an enemy or moon under the
   * tap (focus-fire), else drop a manual aim point. A locked pick overrides the
   * auto-target; tapping empty space clears the lock back to a manual point. */
  setCannonAim(x: number, y: number): void {
    if (!this.cannonsUnlocked) return;
    // Enemy or cruiser under the tap? (generous pick radius so it's easy on mobile)
    let picked: Damageable | null = null;
    let best = 80 * 80;
    for (const e of this.hostiles) {
      if (e.hp <= 0) continue;
      const d = (e.pos.x - x) ** 2 + (e.pos.y - y) ** 2;
      if (d < best) {
        best = d;
        picked = e;
      }
    }
    if (picked) {
      this.cannonTargetEnemy = picked;
      this.cannonTargetBodyId = null;
      this.cannonAim = null;
      return;
    }
    // Moon / asteroid under the tap → focus-fire it.
    const bid = this.pickBodyAt(x, y);
    if (bid) {
      this.cannonTargetBodyId = bid;
      this.cannonTargetEnemy = null;
      this.cannonAim = null;
      return;
    }
    // Empty space → manual aim point (fire with the 🔥 button).
    this.cannonAim = new Vector2(x, y);
    this.cannonTargetBodyId = null;
    this.cannonTargetEnemy = null;
  }
  /** Force a volley from all ready cannons at the current reticle. */
  fireCannons(): void {
    if (!this.cannonsUnlocked || this.inSafeZone || !this.cannonReticle) return;
    // On the Battleship the aim/fire button commands the BIG cannons; the spinal
    // cannons run automatically. Other hulls fire the spinal cannons.
    if (this.mkvPath === "battleship") {
      const cgc = this.equippedGc ? gcBuff(this.equippedGc) : null;
      this.bigCannonSystem.fire(cgc?.fireMul ?? 1);
    } else {
      this.cannonSystem.fire();
    }
  }
  /** Lead a moving target: aim a bit ahead along its velocity so slow cannon
   * bolts intercept it (moons have no velocity → aim at them directly). */
  private leadAim(e: Damageable): { x: number; y: number } {
    const vel = (e as { vel?: { x: number; y: number } }).vel;
    if (!vel) return { x: e.pos.x, y: e.pos.y };
    const m = this.mothership;
    const dist = Math.hypot(e.pos.x - m.pos.x, e.pos.y - m.pos.y);
    const t = Math.min(dist / CANNON.boltSpeed, 1.6); // intercept time, capped
    return { x: e.pos.x + vel.x * t, y: e.pos.y + vel.y * t };
  }
  /** A spinal cannon obliterated a moon → extra-large blast + screen shake. */
  private onCannonMoonKill(b: MineableBody): void {
    this.explosions.push({ x: b.pos.x, y: b.pos.y, radius: b.radius * 2.4, big: true });
    this.pushCombatFx(b.pos.x, b.pos.y, "kill");
  }

  /** An NPC mined a rock out → explode it (no XP, that's the player's) and mark
   * it destroyed so it stays gone. */
  private onNpcDeplete(b: MineableBody, layer: PlanetLayerManager): void {
    if (layer.destroyed.has(b.id)) return;
    layer.destroyed.add(b.id);
    const isMoon = b.id.startsWith("moon_") && !b.id.includes("_sub_");
    this.explosions.push({
      x: b.pos.x,
      y: b.pos.y,
      radius: b.radius,
      big: isMoon,
    });
    if (isMoon) this.onMoonDestroyed(b.id);
  }

  /** A moon was mined out (by anyone) → drop the matching orbiting moon from the
   * SYSTEM view so it no longer shows when the player flies back out. */
  private onMoonDestroyed(bodyId: string): void {
    if (!this.fromPlanetId) return;
    this.removeSystemMoon(this.fromPlanetId, bodyId);
  }

  /** Remove one main moon (`moon_<idx>`, not a sub-moon) from a planet's SYSTEM-view
   * orbit list, matched by its index. Shared by live destruction + the load restore. */
  private removeSystemMoon(planetId: string, bodyId: string): void {
    if (!bodyId.startsWith("moon_") || bodyId.includes("_sub_")) return; // main moons only
    const idx = parseInt(bodyId.split("_")[1] ?? "-1", 10);
    if (idx < 0) return;
    const p = this.system.byId(planetId);
    if (!p) return;
    const k = p.moons.findIndex((m) => m.idx === idx);
    if (k >= 0) p.moons.splice(k, 1);
  }

  /** Re-apply every persisted destroyed moon to the SYSTEM view. The world (incl. all
   * moons) regenerates from the seed on load, so without this a refresh would resurrect
   * moons the player already blew up. Mirrors the live `onMoonDestroyed` splice. */
  private applyDestroyedToSystem(): void {
    for (const planetId of Object.keys(this.pendingDestroyed)) {
      for (const bid of this.pendingDestroyed[planetId] ?? []) {
        this.removeSystemMoon(planetId, bid);
      }
    }
  }

  /** An NPC ship was destroyed → 3D fireball + a Pixi shock-ring. */
  private onNpcDeath(x: number, y: number): void {
    this.explosions.push({ x, y, radius: 26, big: false });
    this.pushCombatFx(x, y, "kill");
  }

  private pushCombatFx(x: number, y: number, kind: CombatFxEvent["kind"]): void {
    if (this.combatFx.length < 64) this.combatFx.push({ x, y, kind });
  }

  /** A hostile red bolt struck the player → shield first, then hull, with a spark. */
  private damagePlayer(dmg: number, x: number, y: number): void {
    const m = this.mothership;
    if (m.hp <= 0) return;
    if (this.hub) return; // the Central Hub is peaceful — no damage between players
    if (this.attract) {
      // Menu "aquarium": the showcase ship is immortal — flash the shield + spark, take no damage.
      this.shieldFlash = 1;
      this.pushCombatFx(x, y, "hit");
      return;
    }
    if (this.riftBusy) return; // invulnerable while diving through the Time & Space Rift
    if (this.playerWorm?.coilShieldUp) {
      dmg = this.playerWorm.coilAbsorb(dmg); // the Maelstrom Coil bubble soaks damage first
      if (dmg <= 0) return;
    }
    const wasUp = m.shield > 0;
    let d = dmg;
    if (m.shield > 0) {
      const a = Math.min(m.shield, d);
      m.shield -= a;
      d -= a;
    }
    if (d > 0) m.hp = Math.max(0, m.hp - d);
    if (wasUp && m.shield <= 0) {
      this.shieldBrokenTimer = SHIP_DEFENSE.shieldDepleteDelay;
    }
    this.shieldFlash = 1;
    this.hitTimer = 0; // hold off shield regen after taking fire
    this.pushCombatFx(x, y, "hit");
  }

  /** Hull hit 0 → the Carrier is destroyed: a big blast, then the sim freezes
   * until the player respawns (see the HUD overlay). */
  private destroyPlayer(): void {
    const m = this.mothership;
    this.playerDead = true;
    m.hp = 0;
    m.shield = 0;
    m.vel.set(0, 0);
    m.moveTarget = null;
    this.moveBodyId = null;
    this.explosions.push({ x: m.pos.x, y: m.pos.y, radius: 96, big: true });
    this.pushCombatFx(m.pos.x, m.pos.y, "kill");
    this.shieldFlash = 1;
  }

  /** Rebuild the Carrier and jump back to the system view — all progression
   * (upgrades, crew, fighters, level, Units) is kept. */
  respawnPlayer(): void {
    if (!this.playerDead) return;
    const m = this.mothership;
    m.hp = m.maxHp;
    m.shield = m.maxShield;
    this.playerDead = false;
    this.hitTimer = 999;
    this.shieldBrokenTimer = 0;
    this.bounceTimer = 0;
    this.exitToSystem(); // return to the solar system, safe
  }

  /** A hostile raider was destroyed → fireball, shock-ring, and combat XP. */
  /** A cruiser finished its radio broadcast → jump-in a fighter squadron. */
  private onCruiserCallBackup(x: number, y: number): void {
    const count =
      CRUISER.backupSquadMin +
      Math.floor(
        Math.random() * (CRUISER.backupSquadMax - CRUISER.backupSquadMin + 1),
      );
    this.enemies?.warpInSquad(count, { x, y });
    this.pushCombatFx(x, y, "kill"); // a pulse as the squadron jumps in
  }

  private onCruiserDeath(x: number, y: number): void {
    // A capital ship goes down with a big blast + a fat bounty.
    this.explosions.push({ x, y, radius: 72, big: true });
    this.pushCombatFx(x, y, "kill");
    this.emitFloater(x, y, CRUISER.killXp, "xp");
    this.gainExp(CRUISER.killXp);
    this.resources.add("units", CRUISER.killUnits);
  }

  /** A carrier's hangar bay ejects a fighter → hand it to the raider manager as a
   * one-shot (never-respawns) ship, with a small launch flash at the bay mouth. */
  private onCarrierLaunchFighter(x: number, y: number, heading: number): void {
    this.enemies?.launchFighter({ x, y }, heading, ENEMY_CARRIER.launchStraight);
    this.pushCombatFx(x, y, "hit");
  }

  private onCarrierDeath(x: number, y: number): void {
    // A carrier goes down with an even bigger blast + a fatter bounty.
    this.explosions.push({ x, y, radius: 96, big: true });
    this.pushCombatFx(x, y, "kill");
    this.emitFloater(x, y, ENEMY_CARRIER.killXp, "xp");
    this.gainExp(ENEMY_CARRIER.killXp);
    this.resources.add("units", ENEMY_CARRIER.killUnits);
  }

  // --- Eater of Worlds boss ---
  /** Awaken the Eater of Worlds at a point (default: the planet centre). Existing
   * raiders stay but stop respawning. */
  spawnEaterOfWorlds(spawn?: { x: number; y: number }, heading?: number): void {
    if (this.layer !== "planet" || this.worm) return;
    const c = this.planetLayer?.planet.pos ?? { x: 0, y: 0 };
    const at = spawn ?? { x: c.x, y: c.y };
    // Face AWAY from the player on spawn (it roams, it doesn't charge).
    const h = heading ?? Math.atan2(at.y - this.mothership.pos.y, at.x - this.mothership.pos.x);
    const count =
      WORM.minSegments + Math.floor(Math.random() * (WORM.maxSegments - WORM.minSegments + 1));
    this.worm = new WormManager(at, h, count);
    this.enemies?.haltRespawns(); // existing raiders stay, but no more respawns
  }
  /** An asteroid/moon fully carved by the boss's beam — destroy it (no player reward). */
  private onWormBeamKillBody(body: MineableBody): void {
    const layer = this.planetLayer;
    if (!layer || layer.destroyed.has(body.id)) return;
    layer.destroyed.add(body.id);
    const isMoon = body.id.startsWith("moon_") && !body.id.includes("_sub_");
    this.explosions.push({
      x: body.pos.x,
      y: body.pos.y,
      radius: body.radius * (isMoon ? 2.2 : 1.4),
      big: isMoon,
    });
    this.pushCombatFx(body.pos.x, body.pos.y, "kill");
    if (isMoon) this.onMoonDestroyed(body.id);
  }
  private onWormSegmentDeath(x: number, y: number, big: boolean): void {
    this.explosions.push({ x, y, radius: big ? 60 : 42, big });
    this.pushCombatFx(x, y, "kill");
    this.emitFloater(x, y, 60, "xp");
    this.gainExp(60);
  }
  private onWormDefeated(x: number, y: number): void {
    // The Eater falls — a cataclysmic blast and a legendary bounty.
    this.explosions.push({ x, y, radius: 200, big: true });
    this.pushCombatFx(x, y, "kill");
    this.emitFloater(x, y, WORM.killXp, "xp");
    this.gainExp(WORM.killXp);
    this.resources.add("units", WORM.killUnits);
    this.eaterDefeated = true; // clears the system-map head marker (planet stays a wreck)
    // Roll the boss loot table (each an independent chance) and record a summary popup.
    const drops: { id: string; count: number }[] = [];
    const roll = (id: string, chance: number, count = 1): void => {
      if (Math.random() < chance) {
        this.addItem(id, count);
        this.markNewDrop(id);
        drops.push({ id, count });
      }
    };
    roll("energyCore", 0.6);
    roll("darkMatter", 0.5, 5);
    roll("eowBody", 0.2);
    roll("eowTail", 0.1);
    roll("eowHead", 0.05);
    roll("eowEnergyCore", 0.01);
    this.bossResult = { xp: WORM.killXp, units: WORM.killUnits, drops };
  }

  /** Build the per-frame context the worm needs (player, bodies, foes, callbacks). */
  private wormCtx(layer: PlanetLayerManager): WormCtx {
    return {
      playerX: this.mothership.pos.x,
      playerY: this.mothership.pos.y,
      playerRadius: this.playerHitRadius,
      playerAlive: this.mothership.hp > 0,
      bodies: layer.bodies,
      foes: [...this.enemyShips, ...this.cruiserShips, ...this.carrierShips],
      onHitPlayer: (dmg, x, y) => this.damagePlayer(dmg, x, y),
      onBeamKillBody: (b) => this.onWormBeamKillBody(b),
      onSegmentDeath: (x, y, big) => this.onWormSegmentDeath(x, y, big),
      onDefeated: (x, y) => this.onWormDefeated(x, y),
      onBeamSpark: (x, y) => this.pushCombatFx(x, y, "hit"),
    };
  }

  /** Roll to awaken the Eater when the FIRST moon of a planet is destroyed by the
   * player: only from jump 15, only at MK V+, and only once per planet. */
  private maybeAwakenEater(bodyId: string): void {
    if (this.rolledEater || this.worm || this.eaterPhase !== "none") return;
    if (bodyId !== "moon_0") return; // the first (innermost) moon only
    if (this.warps < WORM.fromWarp || this.hullTier < 4) return; // jump 15+, MK V+
    this.rolledEater = true; // spent — try another planet if it doesn't wake
    if (Math.random() < WORM.spawnChance) this.beginEaterAwakening();
  }

  private beginEaterAwakening(): void {
    if (this.worm || this.eaterPhase !== "none") return;
    this.eaterPhase = "intro";
    this.introShown = false;
    this.enemies?.haltRespawns(); // the moment it stirs, raiders stop respawning for good
    const c = this.planetLayer?.planet.pos ?? { x: 0, y: 0 };
    this.cameraOverride = { x: c.x, y: c.y }; // pan to the planet at the map centre
    this.openDialogRich([
      { text: "Who dares disturb my slumber…", speaker: "eater" },
      {
        text: "Captain — that is no ordinary world. Something ancient just woke. Weapons hot!",
        speaker: "aria",
      },
    ]);
  }

  /** Runs every frame while the awakening cutscene plays (world otherwise frozen). */
  private updateAwakening(dt: number): void {
    if (this.eaterPhase === "intro") {
      // Wait for the player to read + dismiss the exchange, then blow the planet.
      if (this.dialogLines.length > 0) this.introShown = true;
      else if (this.introShown) this.startEaterExplosion();
      return;
    }
    if (this.eaterPhase === "explode") {
      this.eaterTimer -= dt;
      if (this.eaterTimer <= 0) this.startEaterEmerge();
      return;
    }
    if (this.eaterPhase === "emerge") {
      this.eaterTimer -= dt;
      const layer = this.planetLayer;
      if (this.worm && layer) this.worm.update(dt, this.wormCtx(layer));
      if (this.worm) this.cameraOverride = { x: this.worm.head.pos.x, y: this.worm.head.pos.y };
      if (this.eaterTimer <= 0) this.finishAwakening();
      return;
    }
  }

  private startEaterExplosion(): void {
    this.eaterPhase = "explode";
    this.eaterTimer = 1.2;
    this.introShown = false;
    const c = this.planetLayer?.planet.pos ?? { x: 0, y: 0 };
    // A cataclysmic planet blast: giant shock-rings + a compounded 3D fireball & shake.
    this.explosions.push({ x: c.x, y: c.y, radius: 340, big: true });
    this.explosions.push({ x: c.x, y: c.y, radius: 210, big: true });
    this.pushCombatFx(c.x, c.y, "kill");
    this.pushCombatFx(c.x + 46, c.y - 32, "kill");
    this.pushCombatFx(c.x - 52, c.y + 24, "kill");
    this.eaterPlanetId = this.fromPlanetId; // this planet is now a wreck with the Eater in it
    this.eaterDefeated = false;
    this.eaterExplodedFrame = Math.floor(Math.random() * 16);
    if (this.planetLayer) this.planetLayer.moonsFrozen = true; // all moons stop orbiting
    this.cameraOverride = { x: c.x, y: c.y };
  }

  private startEaterEmerge(): void {
    this.eaterPhase = "emerge";
    this.eaterTimer = WORM.emergeTime;
    const c = this.planetLayer?.planet.pos ?? { x: 0, y: 0 };
    // Emerge AWAY from the player (don't burst straight at them); it roams afterward.
    const h = Math.atan2(c.y - this.mothership.pos.y, c.x - this.mothership.pos.x);
    const count =
      WORM.minSegments + Math.floor(Math.random() * (WORM.maxSegments - WORM.minSegments + 1));
    this.worm = new WormManager({ x: c.x, y: c.y }, h, count, true); // emerge = true
    this.enemies?.haltRespawns(); // existing raiders stay, but no more respawns
  }

  private finishAwakening(): void {
    this.eaterPhase = "none";
    this.cameraOverride = null;
    if (this.worm) this.worm.emerging = false; // hand off to the live fight
  }

  private clearAwakening(): void {
    this.eaterPhase = "none";
    this.cameraOverride = null;
    this.introShown = false;
  }

  private onEnemyDeath(x: number, y: number): void {
    this.explosions.push({ x, y, radius: 30, big: false });
    this.pushCombatFx(x, y, "kill");
    this.emitFloater(x, y, ENEMY.killXp, "xp");
    this.gainExp(ENEMY.killXp);
    // Bounty: destroying a hostile fighter pays Universal Units (miners don't).
    this.resources.add("units", ENEMY.killUnits);
  }

  private emitFloater(
    x: number,
    y: number,
    amount: number,
    kind: FloaterEvent["kind"],
  ): void {
    if (this.floaterEvents.length < 64) {
      this.floaterEvents.push({ x, y, amount, kind });
    }
  }

  /** Bodies the player may currently mine — excludes moons still guarded by a
   * live turret (they're indestructible until the turret is gone). */
  private mineableBodies(layer: PlanetLayerManager): MineableBody[] {
    if (this.protectedMoonIds.size === 0) return layer.bodies;
    return layer.bodies.filter((b) => !this.protectedMoonIds.has(b.id));
  }

  /** Rogue-planet moon turrets: aggro, 360° aim, fire red bolts at the player, take
   * damage from the player's guns, and protect their moon until destroyed. */
  private updateMoonTurrets(dt: number): void {
    this.protectedMoonIds.clear();
    const layer = this.planetLayer;
    if (!layer || layer.turrets.length === 0) {
      if (this.moonTurretBullets.length) this.moonTurretBullets.length = 0;
      return;
    }
    const m = this.mothership;
    const px = m.pos.x;
    const py = m.pos.y;
    const alive = m.hp > 0;
    const aggroR2 = MOON_TURRET.aggroRange * MOON_TURRET.aggroRange;
    const dropR2 = (MOON_TURRET.aggroRange * 1.6) ** 2;
    for (const t of layer.turrets) {
      if (t.dead) continue;
      if (t.hp <= 0) {
        t.dead = true;
        t.aggro = false;
        this.explosions.push({ x: t.pos.x, y: t.pos.y, radius: t.radius * 1.8, big: false });
        this.pushCombatFx(t.pos.x, t.pos.y, "kill");
        continue;
      }
      this.protectedMoonIds.add(t.moonId); // its moon can't be mined yet
      t.shieldFlash = Math.max(0, t.shieldFlash - dt * 3);
      t.flash = Math.max(0, t.flash - dt * 4);
      const dx = px - t.pos.x;
      const dy = py - t.pos.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < aggroR2) t.aggro = true;
      else if (d2 > dropR2) t.aggro = false;
      if (t.aggro && alive) {
        const desired = Math.atan2(dy, dx);
        let diff = desired - t.angle;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        const maxTurn = MOON_TURRET.turnRate * dt;
        t.angle += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
        t.cooldown -= dt;
        if (d2 < MOON_TURRET.fireRange ** 2 && t.cooldown <= 0 && Math.abs(diff) < 0.28) {
          t.cooldown = MOON_TURRET.fireInterval;
          t.flash = 1;
          this.moonTurretBullets.push({
            x: t.pos.x + Math.cos(t.angle) * (t.radius + 4),
            y: t.pos.y + Math.sin(t.angle) * (t.radius + 4),
            vx: Math.cos(desired) * MOON_TURRET.boltSpeed,
            vy: Math.sin(desired) * MOON_TURRET.boltSpeed,
            life: MOON_TURRET.boltLife,
            dmg: t.damage,
          });
        }
      }
    }
    // Advance the red bolts and test them against the player.
    const hitR = this.playerHitRadius + 5;
    for (let i = this.moonTurretBullets.length - 1; i >= 0; i--) {
      const b = this.moonTurretBullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let gone = b.life <= 0;
      if (!gone && alive) {
        const ddx = b.x - px;
        const ddy = b.y - py;
        if (ddx * ddx + ddy * ddy < hitR * hitR) {
          this.damagePlayer(b.dmg, b.x, b.y);
          gone = true;
        }
      }
      if (gone) this.moonTurretBullets.splice(i, 1);
    }
  }

  /** Award XP + an explosion when a body is fully mined out. */
  private checkDepletions(layer: PlanetLayerManager): void {
    for (const b of layer.bodies) {
      if (b.ore <= 0 && !layer.destroyed.has(b.id)) {
        layer.destroyed.add(b.id);
        // Richer bodies grant more XP (scaled to their total mineral content).
        const xp = Math.round(4 + b.oreMax * 0.05);
        // A moon losing all its mass → planetary explosion; its sub-moons lose
        // their anchor and stop orbiting (handled in PlanetLayerManager.update).
        const isMoon = b.id.startsWith("moon_") && !b.id.includes("_sub_");
        this.explosions.push({
          x: b.pos.x,
          y: b.pos.y,
          radius: b.radius,
          big: isMoon,
        });
        this.emitFloater(b.pos.x, b.pos.y, xp, "xp");
        this.gainExp(xp);
        this.rollDrops(b.id, b.pos.x, b.pos.y);
        if (isMoon) {
          this.onMoonDestroyed(b.id);
          this.maybeAwakenEater(b.id); // first-moon kill → chance to wake the boss
        }
      }
    }
  }

  private gainExp(amount: number): void {
    this.exp += amount;
    while (this.exp >= this.expToNext) {
      this.exp -= this.expToNext;
      this.level++;
      this.skillPoints++;
      this.expToNext = this.xpForLevel(this.level);
      this.emitFloater(
        this.mothership.pos.x,
        this.mothership.pos.y - 40,
        this.level,
        "level",
      );
    }
  }

  private xpForLevel(level: number): number {
    return Math.round(50 + (level - 1) * 45);
  }

  // --- Tutor questline (replaces the old coach marks; highlights are reused) ---
  startTutorial(): void {
    // New game: keep the old coach machine inert; run the Tutor questline instead.
    this.tutorialStep = TUTORIAL.DONE;
    this.startQuestline();
  }
  skipTutorial(): void {
    this.tutorialStep = TUTORIAL.DONE;
    this.dialogLines = [];
    this.dialogIndex = 0;
    this.questStep = 99;
  }
  startQuestline(): void {
    if (this.questStep !== 0) return; // already started or resumed from a save
    this.questStep = 1;
    const q = questDef(1);
    this.openDialog([...INTRO_LINES, ...(q ? q.give : [])]);
  }
  /** Player tapped the screen to advance the Tutor dialog. */
  advanceDialog(): void {
    if (this.dialogLines.length === 0) return;
    this.dialogIndex++;
    if (this.dialogIndex >= this.dialogLines.length) {
      this.dialogLines = [];
      this.dialogSpeakers = [];
      this.dialogIndex = 0;
      if (this.questStep === QUEST_COUNT + 1) this.questStep = 99; // ending dismissed → done
    }
  }
  private openDialog(lines: string[]): void {
    this.dialogLines = lines;
    this.dialogSpeakers = lines.map(() => "aria" as const);
    this.dialogIndex = 0;
  }
  /** Open a dialog whose lines can have different speakers (e.g. the Eater, then Aria). */
  private openDialogRich(entries: { text: string; speaker: "aria" | "eater" }[]): void {
    this.dialogLines = entries.map((e) => e.text);
    this.dialogSpeakers = entries.map((e) => e.speaker);
    this.dialogIndex = 0;
  }
  /** First time the player reaches a mechanical yard already able to afford the
   * Mk V refit, Aria briefs the permanent Battleship / Swarm-lord choice (once). */
  private checkMkvIntro(): void {
    if (this.mkvIntroSeen) return;
    if (this.hullTier !== 3 || this.mkvPath !== "none") return; // Carrier, no path chosen yet
    if (this.currentService !== "mechanical") return; // standing at the mechanical yard
    if (this.dialogLines.length > 0) return; // don't interrupt another dialog
    if (
      this.resources.bag.minerals < MKV.refitMinerals ||
      this.resources.bag.units < MKV.refitUnits ||
      this.itemCount("energyCore") < MKV.refitCores
    ) {
      return; // can't afford it yet
    }
    this.mkvIntroSeen = true;
    this.openDialog([
      "Commander — the yard confirms it: you've got the materials for the Mk V refit.",
      "This is a one-way fork. Commit to a hull and the other path seals shut for good.",
      "The BATTLESHIP is a gun platform — a wall of turrets, twin Big Cannons that reach clean across the field, and a reinforced shield. It gives up the mining laser, so you'll carve ore with your guns.",
      "The SWARM-LORD floods the void with fighters and combat drones from wing runways, and can pull them into a spinning defensive ring. Win by numbers.",
      "Weigh it carefully and pick the hull that suits how you fight — there's no undo.",
    ]);
  }
  /** Each frame: when the current quest's condition is met, advance and hand out
   * the next quest's dialog. Never advances while a dialog is still open. */
  private checkQuest(): void {
    if (this.questStep < 1 || this.questStep > QUEST_COUNT) return;
    if (this.dialogLines.length > 0) return;
    if (this.questConditionMet(this.questStep)) {
      const next = this.questStep + 1;
      this.questStep = next;
      if (next === 16) this.didWarp = false; // final quest: only a warp made now counts
      if (next <= QUEST_COUNT) {
        const q = questDef(next);
        this.openDialog([PRAISE_LINE, ...(q ? q.give : [])]);
      } else {
        this.openDialog([...ENDING_LINES]); // questStep === QUEST_COUNT + 1 (the ending)
      }
      return;
    }
    // Not complete yet → a mid-quest nudge if Moon Core drops are being stubborn.
    if (
      this.questStep === 10 &&
      !this.moonCoreHintShown &&
      this.moonsDestroyed > 10
    ) {
      this.moonCoreHintShown = true;
      this.openDialog([
        "Still short on Moon Cores? The drops can be stubborn.",
        "You can also buy Moon Cores at a Trade Hub for 200 Units each — give that a try!",
      ]);
    }
  }
  private questConditionMet(step: number): boolean {
    switch (step) {
      case 1:
        return this.layer === "planet" && this.currentService !== "none";
      case 2:
        return this.resources.bag.minerals >= 100;
      case 3:
        return this.purchasedUpgrades.size >= 1;
      case 4:
        return this.level >= 2;
      case 5:
        return this.resources.bag.units >= 2;
      case 6:
        return this.layer === "planet" && this.currentService === "mechanical";
      case 7:
        return this.purchasedUpgrades.has("beam1");
      case 8:
        return ["laser4", "optic4", "engine3"].every((id) =>
          this.purchasedUpgrades.has(id),
        );
      case 9:
        return this.resources.bag.units >= 20;
      case 10:
        return this.itemCount("moonCore") >= 2;
      case 11:
        return this.itemCount("energyCore") >= 1;
      case 12:
        return this.purchasedUpgrades.has("mk2");
      case 13:
        return this.unlockedSlots.size >= 1;
      case 14:
        return this.itemCount("turret_basic") >= 1 || this.equipped.some((e) => e);
      case 15:
        return this.equipped.some((e) => e);
      case 16:
        return this.didWarp;
      default:
        return false;
    }
  }
  /** Tracker progress string for quests with a numeric target ("" otherwise). */
  get questProgress(): string {
    switch (this.questStep) {
      case 2:
        return `${Math.floor(this.resources.bag.minerals)} / 100`;
      case 5:
        return `${Math.floor(this.resources.bag.units)} / 2`;
      case 9:
        return `${Math.floor(this.resources.bag.units)} / 20`;
      case 10:
        return `${this.itemCount("moonCore")} / 2`;
      case 11:
        return `${this.itemCount("energyCore")} / 1`;
      default:
        return "";
    }
  }
  /** Advance step machine each frame from the player's state (minerals, level). */
  private advanceTutorial(): void {
    if (this.tutorialStep >= TUTORIAL.DONE) return;
    if (
      this.tutorialStep === TUTORIAL.MINE &&
      this.resources.bag.minerals >= TUTORIAL_FIRST_UPGRADE_COST
    ) {
      this.tutorialStep = TUTORIAL.BUY_UPGRADE;
    } else if (
      this.tutorialStep === TUTORIAL.WAIT_LEVEL2 &&
      this.level >= 2 &&
      this.skillPoints > 0
    ) {
      this.tutorialStep = TUTORIAL.SPEND_SKILL;
    }
  }
  get tutorialUiStep(): TutorialStep {
    switch (this.tutorialStep) {
      case TUTORIAL.GOTO_PLANET:
        return "goToPlanet";
      case TUTORIAL.MINE:
        return "mine";
      case TUTORIAL.BUY_UPGRADE:
        return "buyUpgrade";
      case TUTORIAL.SPEND_SKILL:
        return "spendSkill";
      default:
        return null;
    }
  }
  private get tutorialHighlightUpgrade(): string | null {
    if (this.questStep === 3) return "laser1"; // "buy your first upgrade"
    if (this.questStep === 7) return "beam1"; // "unlock the Tri-Beam Array"
    if (this.questStep === 12) return "mk2"; // "refit to Mk II"
    return null;
  }
  /** Renderer/minimap: pulse the safe planets while the "find harbor" quest is on. */
  get tutorialHighlightPlanets(): boolean {
    return this.questStep === 1;
  }

  /** Renderer drains transient VFX each frame. */
  drainFloaters(): FloaterEvent[] {
    const e = this.floaterEvents.slice();
    this.floaterEvents.length = 0;
    return e;
  }
  drainExplosions(): ExplosionEvent[] {
    const e = this.explosions.slice();
    this.explosions.length = 0;
    return e;
  }
  drainCombatFx(): CombatFxEvent[] {
    const e = this.combatFx.slice();
    this.combatFx.length = 0;
    return e;
  }

  toggleMiningRange(): void {
    this.showMiningRange = !this.showMiningRange;
  }
  toggleHullMining(): void {
    this.hullMiningEnabled = !this.hullMiningEnabled;
  }
  toggleAutoMine(): void {
    this.autoMineEnabled = !this.autoMineEnabled;
    if (this.autoMineEnabled) this.hullMiningEnabled = true; // auto-mine needs the laser on
    else this.moveBodyId = null; // stop seeking; hand control back to the pilot
  }
  toggleDrones(): void {
    this.dronesEnabled = !this.dronesEnabled;
  }
  toggleCannons(): void {
    this.cannonsEnabled = !this.cannonsEnabled;
  }
  toggleMechDrones(): void {
    this.mechDronesEnabled = !this.mechDronesEnabled;
  }
  get repairDrones(): DroneUnit[] {
    return this.repairBay.units;
  }
  setPlayerName(name: string): void {
    const n = name.trim().slice(0, 24);
    if (n) this.playerName = n;
  }

  // --- Admin cheats (only for players whose name contains [ADMINZ]) ---
  get isAdmin(): boolean {
    // Account-based admins (by login username) + the legacy [ADMINZ] display-name gate.
    // Add usernames to this list to grant the admin panel. (Client-side only — fine for a
    // testing/cheat panel; a truly secure admin role would be enforced server-side.)
    const admins = ["kiattipoltmkl"];
    return (
      admins.includes(this.accountName.trim().toLowerCase()) ||
      this.playerName.toUpperCase().includes("[ADMINZ]")
    );
  }
  adminSetLevel(n: number): void {
    if (!this.isAdmin) return;
    this.level = Math.max(1, Math.floor(n));
    this.exp = 0;
    this.expToNext = this.xpForLevel(this.level);
  }
  adminAddSkill(n: number): void {
    if (!this.isAdmin) return;
    this.skillPoints = Math.max(0, this.skillPoints + Math.floor(n));
  }
  adminAddMinerals(n: number): void {
    if (!this.isAdmin) return;
    this.resources.add("minerals", Math.floor(n));
  }
  adminAddUnits(n: number): void {
    if (!this.isAdmin) return;
    this.resources.add("units", Math.floor(n));
  }
  adminSetWarps(n: number): void {
    if (!this.isAdmin) return;
    this.warps = Math.max(0, Math.floor(n));
  }
  adminAddItem(id: string, n: number): void {
    if (!this.isAdmin || !id) return;
    this.addItem(id, Math.floor(n));
  }
  /** Set the hull tier directly (0 Miner … 3 Carrier Mk IV, 4 Mk V, 5 Mk VI) + MK V/VI path. */
  adminSetMk(tier: number, path: "battleship" | "swarmlord", mkviiSwl = false): void {
    if (!this.isAdmin) return;
    for (const id of [
      "mk2", "mk3", "mk4", "mkv_bts", "mkv_swl", "mkvi_bts", "mkvi_swl", "mkvii_swl",
      "mkvii", "mkviii", "mkviii_rift", "mkviii_coil", // also drop the EOW worm hulls + their secret unlocks
    ]) {
      this.purchasedUpgrades.delete(id);
    }
    if (tier >= 1) this.purchasedUpgrades.add("mk2");
    if (tier >= 2) this.purchasedUpgrades.add("mk3");
    if (tier >= 3) this.purchasedUpgrades.add("mk4");
    if (tier >= 4) this.purchasedUpgrades.add(path === "swarmlord" ? "mkv_swl" : "mkv_bts");
    if (tier >= 5) this.purchasedUpgrades.add(path === "swarmlord" ? "mkvi_swl" : "mkvi_bts");
    if (mkviiSwl && tier >= 5 && path === "swarmlord") this.purchasedUpgrades.add("mkvii_swl");
    this.recomputeStats();
    this.applyShield();
  }
  /** Awaken the Eater of Worlds on the spot (for testing the boss). */
  adminSpawnEater(): void {
    if (!this.isAdmin || this.layer !== "planet") return;
    this.eaterDefeated = false;
    this.beginEaterAwakening(); // run the full awakening (dialogue → blast → emerge)
  }
  /** Admin test: toggle the orbiting ring-station boss on/off. Two counter-rotating
   * rings of turrets/cannons/big-cannons/hangars around the planet (planet layer). */
  adminSpawnStation(): void {
    if (!this.isAdmin) return;
    this.station = this.station ? null : new StationManager({ x: 0, y: 0 });
  }
  /** Admin test: unlock the secret worm hulls directly (bypassing the EOW-part refit) so the
   * Mk VIII node + Time & Space Rift can be tested. Mk VIII also grants the rift unlock node. */
  adminUnlockWorm(mk: number): void {
    if (!this.isAdmin) return;
    this.purchasedUpgrades.add("mkvii");
    if (mk >= 8) {
      this.purchasedUpgrades.add("mkviii");
      this.purchasedUpgrades.add("mkviii_rift");
      this.purchasedUpgrades.add("mkviii_coil");
    }
    this.recomputeStats();
    this.applyShield();
  }
  /** Admin: grant every upgrade on the CURRENT hull line — Battleship / Swarm-lord / EOW worm —
   * skipping the other mutually-exclusive branches (so a Swarm-lord admin only gets Swarm-lord
   * upgrades, etc.). With no path chosen yet, grants only the shared pre-branch upgrades. */
  adminGetAllUpgrades(): void {
    if (!this.isAdmin) return;
    // Transitive `requires` of a node (memoised) — used to tell which branch a node belongs to.
    const cache = new Map<string, Set<string>>();
    const trans = (id: string): Set<string> => {
      const hit = cache.get(id);
      if (hit) return hit;
      const set = new Set<string>();
      cache.set(id, set); // set before recursing (the tree is a DAG; this also guards any cycle)
      const n = UPGRADES_BY_ID[id];
      if (n) for (const r of n.requires) { set.add(r); for (const x of trans(r)) set.add(x); }
      return set;
    };
    const onBranch = (id: string, keys: string[]): boolean =>
      keys.some((k) => id === k || trans(id).has(k));
    const BTS = ["mkv_bts", "mkvi_bts"];
    const SWL = ["mkv_swl", "mkvi_swl"];
    const EOW = ["mkvii", "mkviii"];
    const excluded = this.isWorm
      ? [...BTS, ...SWL]
      : this.mkvPath === "swarmlord"
        ? [...BTS, ...EOW]
        : this.mkvPath === "battleship"
          ? [...SWL, ...EOW]
          : [...BTS, ...SWL, ...EOW]; // no path yet → shared upgrades only, leave the branch choice
    for (const n of Object.values(UPGRADES_BY_ID)) {
      if (!onBranch(n.id, excluded)) this.purchasedUpgrades.add(n.id);
    }
    this.recomputeStats();
    this.applyShield();
    this.mothership.hp = this.mothership.maxHp; // fully kit the test ship
    this.mothership.shield = this.mothership.maxShield;
  }

  // --- main-menu attract mode ---------------------------------------------------------------
  /** Turn this (throwaway) state into the menu "aquarium": a fully-armed, immortal MK VI
   * Battleship parked in a busy deep-space world, endlessly blasting a swarm of raiders. The
   * menu owns a private GameState for this — it never touches the player's real save. */
  startAttract(): void {
    this.attract = true;
    this.playerName = "ADMIRAL";
    // MK VI Battleship hull + its missile/beam upgrade chains (the set adminSetMk builds, inline).
    for (const id of [
      "mk2", "mk3", "mk4", "mkv_bts", "mkvi_bts",
      "mkvi_missile1", "mkvi_missile2", "mkvi_missile3", "mkvi_missile4",
      "mkvi_beam1", "mkvi_beam2", "mkvi_beam3",
    ]) {
      this.purchasedUpgrades.add(id);
    }
    this.recomputeStats();
    this.applyShield();
    this.mothership.hp = this.mothership.maxHp;
    this.mothership.shield = this.mothership.maxShield;
    // Man EVERY weapon mount directly (bypass the mechanical-yard purchase/equip gate).
    for (let i = 0; i < this.maxTurretSlots; i++) {
      this.unlockedSlots.add(i);
      this.equipped[i] = "turret_basic";
    }
    for (let i = 0; i < this.maxCannonSlots; i++) {
      this.unlockedCannonSlots.add(i);
      this.equippedCannons[i] = "cannon_basic";
    }
    for (let i = 0; i < this.maxBigCannonSlots; i++) {
      this.unlockedBigCannonSlots.add(i);
      this.equippedBigCannons[i] = "cannon_big";
    }
    this.warps = 12; // deep space → the biggest layer + the fullest enemy tables
    // Drop into the first ordinary (non-service, non-rogue) world so raiders spawn but no boss does.
    const dest =
      this.system.planets.find((p) => p.service === "none" && !p.rogue) ??
      this.system.planets.find((p) => p.service === "none") ??
      this.system.planets[0];
    if (dest) this.travelToPlanet(dest.id);
    this.rolledEater = true; // never let the Eater-of-Worlds cutscene hijack the preview
    this.station = null;
    // Override travelToPlanet's corner spawn: drop the showcase ship in the MIDDLE of the
    // battlefield, on its slow circular flight path (it orbits the central hub).
    this.attractAngle = 0;
    this.applyAttractOrbit(0); // seat pos/heading on the orbit before the swarm spawns around it
    this.attractRefillSwarm(); // over-stuff the swarm for a lively showcase
  }

  // --- Central Hub (multiplayer) ---------------------------------------------------------------
  /** Enter hub mode: a peaceful open space (no enemies, mining, or damage) where the player flies
   * their REAL ship (upgrades must already be loaded via load()) and sees other pilots as ships. */
  startHub(): void {
    this.hub = true;
    const dest =
      this.system.planets.find((p) => p.service === "none" && !p.rogue) ??
      this.system.planets.find((p) => p.service === "none") ??
      this.system.planets[0];
    if (dest) this.travelToPlanet(dest.id);
    // Strip everything hostile / minable → a calm social space with just the central hub planet.
    this.enemies = null;
    this.cruisers = null;
    this.carriers = null;
    this.npcs = null;
    this.worm = null;
    this.station = null;
    this.rolledEater = true; // never awaken the Eater here
    if (this.planetLayer) {
      this.planetLayer.asteroids.length = 0;
      this.planetLayer.moons.length = 0;
      this.planetLayer.bodies.length = 0;
      this.planetLayer.turrets.length = 0;
    }
    // Spawn near the hub centre with a little scatter (so pilots don't stack), facing inward.
    const a = Math.random() * Math.PI * 2;
    const r = 300 + Math.random() * 520;
    this.mothership.pos.set(Math.cos(a) * r, Math.sin(a) * r);
    this.mothership.heading = Math.atan2(-this.mothership.pos.y, -this.mothership.pos.x);
    this.mothership.vel.set(0, 0);
    this.moveBodyId = null;
    this.mothership.hp = this.mothership.maxHp;
    this.resetActivity();
  }

  /** Upsert a remote pilot + set the interpolation target from their latest broadcast. */
  setRemotePlayer(
    id: string,
    s: { username: string; tier: number; x: number; y: number; h: number; vx: number; vy: number; seg: number },
  ): void {
    const p = this.remotePlayers.get(id);
    if (!p) {
      this.remotePlayers.set(id, {
        username: s.username, tier: s.tier, segCount: s.seg,
        x: s.x, y: s.y, heading: s.h,
        ax: s.x, ay: s.y, th: s.h,
        vx: s.vx, vy: s.vy, age: 0,
      });
    } else {
      p.ax = s.x; p.ay = s.y; p.th = s.h; // snap the dead-reckoning anchor to the latest truth
      p.vx = s.vx; p.vy = s.vy; p.age = 0;
      p.tier = s.tier; p.username = s.username; p.segCount = s.seg;
    }
  }

  removeRemotePlayer(id: string): void {
    this.remotePlayers.delete(id);
  }

  /** Smooth remote pilots each frame (hub only): dead-reckon along their last velocity so motion
   * stays fluid between the throttled network updates, and trail a worm chain for worm hulls. */
  private updateHubRemotes(dt: number): void {
    const k = Math.min(1, dt * 12);
    const cap = 0.45; // only dead-reckon this far past the last packet (bounds any stop overshoot)
    const spacing = MKVII.spacing;
    for (const p of this.remotePlayers.values()) {
      p.age += dt;
      // Chase the last authoritative position extrapolated along its last velocity.
      const ex = Math.min(p.age, cap);
      const tX = p.ax + p.vx * ex;
      const tY = p.ay + p.vy * ex;
      p.x += (tX - p.x) * k;
      p.y += (tY - p.y) * k;
      const dh = ((p.th - p.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      p.heading += dh * k;
      // Worm hulls (MK VII/VIII): trail a follow-the-leader chain behind the smoothed head so other
      // pilots see the real Eater-of-Worlds worm, not a single hull sprite.
      if (p.tier >= 6) {
        const total = Math.max(3, Math.min(p.segCount | 0, 14) + 2); // head + bodies + tail
        let tr = p.wormTrail;
        if (!tr || tr.length !== total) {
          tr = [];
          for (let i = 0; i < total; i++) {
            tr.push({
              x: p.x - Math.cos(p.heading) * i * spacing,
              y: p.y - Math.sin(p.heading) * i * spacing,
              heading: p.heading,
            });
          }
          p.wormTrail = tr;
        }
        tr[0]!.x = p.x;
        tr[0]!.y = p.y;
        tr[0]!.heading = p.heading;
        for (let i = 1; i < total; i++) {
          const a = tr[i - 1]!;
          const s = tr[i]!;
          let dx = s.x - a.x;
          let dy = s.y - a.y;
          let d = Math.hypot(dx, dy);
          if (d < 1e-4) {
            dx = -Math.cos(a.heading);
            dy = -Math.sin(a.heading);
            d = 1;
          }
          const nx = dx / d;
          const ny = dy / d;
          s.x = a.x + nx * spacing;
          s.y = a.y + ny * spacing;
          s.heading = Math.atan2(-ny, -nx);
        }
      } else if (p.wormTrail) {
        p.wormTrail = undefined; // tier changed away from a worm → drop the chain
      }
    }
  }

  /** Place the showcase ship on its circular flight path at the current `attractAngle`,
   * facing along the direction of travel (tangent), with a matching velocity so its
   * thrusters read as "cruising". Centre of the circle = the planet hub at the origin. */
  private applyAttractOrbit(dt: number): void {
    this.attractAngle += dt * this.attractOrbitW;
    const a = this.attractAngle;
    const m = this.mothership;
    const R = this.attractOrbitR;
    m.pos.set(Math.cos(a) * R, Math.sin(a) * R);
    // Tangent to the circle (CCW) = angle a + 90°; velocity = R·ω along that tangent.
    m.heading = a + Math.PI / 2;
    const v = R * this.attractOrbitW;
    m.vel.set(-Math.sin(a) * v, Math.cos(a) * v);
  }

  /** Warp a fresh wave of raiders in around the showcase ship (keeps the fight going). */
  private attractRefillSwarm(): void {
    const at = this.mothership.pos;
    this.enemies = new EnemyManager(21, at, true); // a thick fighter squadron (+30% for the showcase)
    this.cruisers = new CruiserManager(2, at); // a pair of capital cruisers
    this.carriers = new CarrierManager(1, at); // one hostile carrier
  }

  /** Drive the self-playing menu preview: top up the swarm, and auto-fire the Big Cannons +
   * missile salvos (the turrets & spinal cannons already fire themselves via updateDefense). */
  private updateAttract(dt: number): void {
    this.applyAttractOrbit(dt); // fly the slow circle around the hub (drives pos/heading/vel)
    // The swarm SELF-SUSTAINS for smooth footage: EnemyManager respawns each downed fighter on
    // its own staggered timer, warping it back in off-screen — no bulk "refresh" of the field.
    // Keep the Big Cannons trained on the nearest foe every frame so the reticle stays live…
    const tgt = this.nearestHostile();
    if (tgt) this.setCannonAim(tgt.pos.x, tgt.pos.y);
    // …then pull the trigger on a cadence (the growing kamehameha beam).
    this.attractCannonTimer -= dt;
    if (this.attractCannonTimer <= 0) {
      if (tgt) this.fireCannons();
      this.attractCannonTimer = 3.2;
    }
    this.attractMissileTimer -= dt;
    if (this.attractMissileTimer <= 0) {
      this.launchMissileSalvo();
      this.attractMissileTimer = 4.5;
    }
  }

  /** Nearest living hostile to the ship (for the attract-mode Big Cannon aim). */
  private nearestHostile(): Damageable | null {
    const m = this.mothership;
    let best = Infinity;
    let out: Damageable | null = null;
    for (const e of this.hostiles) {
      if (e.hp <= 0) continue;
      const d = (e.pos.x - m.pos.x) ** 2 + (e.pos.y - m.pos.y) ** 2;
      if (d < best) {
        best = d;
        out = e;
      }
    }
    return out;
  }

  /** Deterministically roll which rogue planets host a ring-station this system: only from
   * jump STATION.fromWarp, STATION.spawnChance each, capped at STATION.maxPerSystem. */
  private computeStationPlanets(): void {
    this.stationPlanetIds.clear();
    if (this.warps < STATION.fromWarp) return;
    let count = 0;
    for (let i = 0; i < this.system.planets.length; i++) {
      if (count >= STATION.maxPerSystem) break;
      const p = this.system.planets[i]!;
      if (!p.rogue) continue;
      // Own seed stream (offset from the moon seed) so stations don't track moon layouts.
      const roll = makeRng((planetSeed(this.system.seed, i) ^ 0x57a71031) >>> 0).next();
      if (roll < STATION.spawnChance) {
        this.stationPlanetIds.add(p.id);
        count++;
      }
    }
  }

  /** The live (undefeated) station-planet ids — the solar view draws rings on these worlds. */
  get stationRingIds(): string[] {
    const out: string[] = [];
    for (const id of this.stationPlanetIds) if (!this.stationDefeated.has(id)) out.push(id);
    return out;
  }

  private getOrCreateStation(planetId: string): StationManager {
    let st = this.stationCache.get(planetId);
    if (!st) {
      st = new StationManager({ x: 0, y: 0 }); // planet layer is centred on the world origin
      this.stationCache.set(planetId, st);
    }
    return st;
  }

  /** Ring hangars spew 4 fighters, then 1 cruiser, once the player closes in — staggered
   * launchInterval apart. Driven here (not in StationManager) since it needs the enemy managers. */
  private driveStationHangars(dt: number): void {
    const st = this.station;
    if (!st || st.defeated || this.inSafeZone || this.playerDead) return;
    const px = this.mothership.pos.x;
    const py = this.mothership.pos.y;
    for (const ring of st.rings) {
      if (ring.dead) continue;
      for (const w of ring.weapons) {
        if (w.kind !== "hangar" || w.dead || w.launchLeft <= 0) continue;
        const dx = px - w.pos.x;
        const dy = py - w.pos.y;
        if (dx * dx + dy * dy > STATION.cannonRange * STATION.cannonRange) continue; // wait for approach
        w.launchTimer -= dt;
        if (w.launchTimer > 0) continue;
        w.launchTimer = STATION.launchInterval;
        const heading = Math.atan2(w.pos.y - st.center.y, w.pos.x - st.center.x); // fly outward
        if (w.launchLeft === 1) this.cruisers?.spawnAt({ x: w.pos.x, y: w.pos.y }, heading);
        else
          this.enemies?.launchFighter(
            { x: w.pos.x, y: w.pos.y },
            heading,
            STATION.launchStraightTime,
          );
        w.launchLeft--;
        this.pushCombatFx(w.pos.x, w.pos.y, "hit");
      }
    }
  }

  addItem(id: string, n = 1): void {
    this.inventory[id] = (this.inventory[id] ?? 0) + n;
  }
  /** Dismiss the boss-defeat results popup. */
  clearBossResult(): void {
    this.bossResult = null;
  }
  itemCount(id: string): number {
    return this.inventory[id] ?? 0;
  }

  // --- planet services (only valid at the matching planet) ---
  get currentService(): PlanetService {
    return this.planetLayer?.service ?? "none";
  }
  /** True only when the ship has flown up close to a service planet's hub. */
  get nearServicePlanet(): boolean {
    const L = this.planetLayer;
    if (this.layer !== "planet" || !L || L.service === "none") return false;
    const p = L.planet;
    const r = p.radius + SERVICE.interactMargin;
    return this.mothership.pos.distanceSqTo(p.pos) <= r * r;
  }
  /** Safe zone = a service planet (trade / mechanical / tavern). Guns + turrets are
   * force-disabled here so the player can't open fire in a friendly hub. */
  get inSafeZone(): boolean {
    // The menu "aquarium" is always a live battlefield — never a safe zone, so the showcase
    // ship keeps firing even if the preview happens to spawn at a service world.
    if (this.attract) return false;
    return this.currentService !== "none";
  }

  /** Trade-hub: buy `n` Universal Units at 100 minerals each. */
  tradeForUnits(n: number): boolean {
    if (this.currentService !== "trade" || n <= 0) return false;
    const cost = n * TRADE.materialPerUnit;
    if (!this.resources.spend({ minerals: cost })) return false;
    this.resources.add("units", n);
    return true;
  }

  private ensureMarket(): void {
    if (this.elapsed >= this.marketRefreshAt) {
      this.market = rollMarket();
      this.marketRefreshAt = this.elapsed + MARKET_REFRESH_SEC;
    }
  }
  getMarket(): MarketStock[] {
    this.ensureMarket();
    return this.market.map((m) => ({ ...m }));
  }
  /** Trade-hub market: buy a stocked item with Universal Units. */
  buyMarketItem(id: string): boolean {
    if (this.currentService !== "trade") return false;
    this.ensureMarket();
    const item = this.market.find((m) => m.id === id && m.stock > 0);
    if (!item) return false;
    if (!this.resources.spend({ units: item.cost })) return false;
    item.stock--;
    this.addItem(id, 1);
    return true;
  }

  repairCost(): number {
    return Math.ceil(
      (this.mothership.maxHp - this.mothership.hp) * SHIP_DEFENSE.repairCostPerHp,
    );
  }
  /** Mechanical: restore the hull to full for minerals. */
  repairHull(): boolean {
    if (this.currentService !== "mechanical") return false;
    const cost = this.repairCost();
    if (cost <= 0 || !this.resources.spend({ minerals: cost })) return false;
    this.mothership.hp = this.mothership.maxHp;
    return true;
  }

  shieldUpgradeCost(): number {
    return SHIP_DEFENSE.shieldUpgradeBaseCost * (this.shieldLevel + 1);
  }
  /** Mechanical: raise max shield by one step. */
  upgradeShieldMax(): boolean {
    if (this.currentService !== "mechanical") return false;
    const cost = this.shieldUpgradeCost();
    if (!this.resources.spend({ minerals: cost })) return false;
    this.shieldLevel++;
    this.applyShield();
    this.mothership.shield = this.mothership.maxShield;
    return true;
  }
  private applyShield(): void {
    this.mothership.maxShield =
      SHIP_DEFENSE.maxShield +
      this.shieldLevel * SHIP_DEFENSE.shieldUpgradeStep +
      this.equippedShieldBonus +
      // Battleship trait: Mk V +100, Mk VI +250 max shield. Mk VI Swarm-lord: +150.
      (this.mkvPath === "battleship"
        ? this.hullTier >= 5
          ? MKVI.btsShieldBonus
          : MKV.btsShieldBonus
        : this.mkvPath === "swarmlord" && this.hullTier >= 5
          ? (this.isMkviiSwl ? MKVI.swl7ShieldBonus : MKVI.swlShieldBonus)
          : 0);
    if (this.mothership.shield > this.mothership.maxShield) {
      this.mothership.shield = this.mothership.maxShield;
    }
  }

  // --- ship modifications (shield generators, built at the trade hub) ---
  get modTier(): number {
    return 1 + Math.floor(this.warps / MODS.tierEveryWarps);
  }
  get buildModCost(): number {
    return MODS.buildBaseCost + (this.modTier - 1) * MODS.costPerTier;
  }
  get equippedShieldBonus(): number {
    const m = this.mods.find((x) => x.id === this.equippedShieldModId);
    return m ? m.shieldBonus : 0;
  }
  get equippedShieldRegenMul(): number {
    const m = this.mods.find((x) => x.id === this.equippedShieldModId);
    return m ? (m.regenMul ?? 1) : 1;
  }
  /** The shield-bubble radius (matches the VFX). While shields are up, incoming
   * fire is caught here; once the shield breaks the hitbox drops to the hull core. */
  get shieldRadius(): number {
    const mul = SHIP_DEFENSE.shieldBubbleMul[this.hullTier] ?? 1;
    return (SHIP_DEFENSE.collisionRadius + 16) * mul;
  }
  get playerHitRadius(): number {
    return this.mothership.shield > 0 ? this.shieldRadius : this.collisionRadiusWorld;
  }
  /** Trade hub: spend Units to build a randomised shield generator. */
  buildShieldMod(): Modification | null {
    if (this.currentService !== "trade") return null;
    if (!this.resources.spend({ units: this.buildModCost })) return null;
    const mod = makeShieldMod(this.modTier);
    this.mods.push(mod);
    return mod;
  }
  equipShieldMod(id: string): boolean {
    const m = this.mods.find((x) => x.id === id && x.kind === "shield");
    if (!m) return false;
    this.equippedShieldModId = id;
    this.applyShield();
    return true;
  }
  unequipShieldMod(): void {
    this.equippedShieldModId = null;
    this.applyShield();
  }
  /** Trade hub: sell a modification for Units. */
  sellMod(id: string): boolean {
    if (this.currentService !== "trade") return false;
    const i = this.mods.findIndex((x) => x.id === id);
    if (i < 0) return false;
    const value = Math.max(1, Math.round(this.mods[i]!.shieldBonus * MODS.sellFraction));
    if (this.equippedShieldModId === id) this.unequipShieldMod();
    this.mods.splice(i, 1);
    this.resources.add("units", value);
    return true;
  }
  /** Units to upgrade a shield generator one level, or -1 if maxed / not found. */
  modUpgradeCost(id: string): number {
    const m = this.mods.find((x) => x.id === id && x.kind === "shield");
    if (!m) return -1;
    if ((m.level ?? 1) >= MODS.maxModLevel) return -1;
    const gain = m.shieldBonus * (MODS.upgradeShieldMul - 1);
    return Math.max(1, Math.round(gain * MODS.upgradeCostPerShield));
  }
  /** Mechanical yard: spend Units to raise a generator's level (more shield + regen). */
  upgradeMod(id: string): boolean {
    if (this.currentService !== "mechanical") return false;
    const m = this.mods.find((x) => x.id === id && x.kind === "shield");
    if (!m) return false;
    if ((m.level ?? 1) >= MODS.maxModLevel) return false;
    const cost = this.modUpgradeCost(id);
    if (cost < 0 || !this.resources.spend({ units: cost })) return false;
    m.level = (m.level ?? 1) + 1;
    m.shieldBonus = Math.round(m.shieldBonus * MODS.upgradeShieldMul);
    m.regenMul = (m.regenMul ?? 1) + MODS.upgradeRegenAdd;
    if (this.equippedShieldModId === id) {
      this.applyShield();
      this.mothership.shield = this.mothership.maxShield;
    }
    return true;
  }
  /** Trade hub: sell one of a stacking inventory item for Units. */
  sellItem(id: string): boolean {
    if (this.currentService !== "trade") return false;
    const value = ITEM_SELL[id] ?? 0;
    if (value <= 0 || this.itemCount(id) < 1) return false;
    this.addItem(id, -1);
    this.resources.add("units", value);
    return true;
  }
  clearNotice(): void {
    this.noticeText = null;
  }

  /** Mechanical: craft a recipe if inputs are met. Returns false otherwise. */
  craft(recipeId: string): boolean {
    if (this.currentService !== "mechanical") return false;
    const r = RECIPES_BY_ID[recipeId];
    if (!r) return false;
    for (const inp of r.inputs) {
      if (inp.kind === "minerals" && this.resources.bag.minerals < inp.amount)
        return false;
      if (inp.kind === "units" && this.resources.bag.units < inp.amount)
        return false;
      if (inp.kind === "item" && this.itemCount(inp.id ?? "") < inp.amount)
        return false;
    }
    for (const inp of r.inputs) {
      if (inp.kind === "minerals") this.resources.spend({ minerals: inp.amount });
      else if (inp.kind === "units") this.resources.spend({ units: inp.amount });
      else if (inp.kind === "item") this.addItem(inp.id ?? "", -inp.amount);
    }
    this.addItem(r.outputItem, r.outputQty);
    return true;
  }

  get hullTier(): number {
    if (this.purchasedUpgrades.has("mkviii")) return 7; // secret mechanical Eater-of-Worlds
    if (this.purchasedUpgrades.has("mkvii")) return 6; // secret Eater-of-Worlds hull
    return this.purchasedUpgrades.has("mkvi_bts") || this.purchasedUpgrades.has("mkvi_swl")
      ? 5
      : this.purchasedUpgrades.has("mkv_bts") || this.purchasedUpgrades.has("mkv_swl")
        ? 4
        : this.purchasedUpgrades.has("mk4")
          ? 3
          : this.purchasedUpgrades.has("mk3")
            ? 2
            : this.purchasedUpgrades.has("mk2")
              ? 1
              : 0;
  }
  /** MK V path chosen (or "none"): Battleship (firepower) vs Swarm-lord (swarm).
   * Both are hullTier 4; the path picks sprite/name/arsenal in later phases. */
  get mkvPath(): "battleship" | "swarmlord" | "none" {
    if (this.isWorm) return "none"; // the worm hulls use none of the battleship/swarm systems
    // Mk VI continues each Mk V line — Battleship stays "battleship", Swarm-lord stays "swarmlord".
    if (this.purchasedUpgrades.has("mkv_bts") || this.purchasedUpgrades.has("mkvi_bts"))
      return "battleship";
    if (this.purchasedUpgrades.has("mkv_swl") || this.purchasedUpgrades.has("mkvi_swl"))
      return "swarmlord";
    return "none";
  }
  /** MK VII / VIII — the secret Eater-of-Worlds hulls: the player IS a worm. */
  get isWorm(): boolean {
    return this.purchasedUpgrades.has("mkvii") || this.purchasedUpgrades.has("mkviii");
  }
  /** MK VIII — the mechanical Eater-of-Worlds (renders the mechanical sprites). */
  get isMkviii(): boolean {
    return this.purchasedUpgrades.has("mkviii");
  }
  /** Mk VII Swarm-lord — a tier-5 Swarm-lord variant (bigger cosmic hull, new mount layout). */
  get isMkviiSwl(): boolean {
    return this.purchasedUpgrades.has("mkvii_swl");
  }
  /** Mk VII SWL Rift Swarm skill: ready when unlocked, off cooldown, and out of a safe zone. */
  get riftSwarmReady(): boolean {
    return this.isMkviiSwl && this.riftSwarmCd <= 0 && !this.inSafeZone;
  }
  /** Fire the Rift Swarm: tear open 2 still (non-spinning) rifts, one to each side of the ship. Each
   * rift stays fixed in world space ~5s and streams out waves of purple arrows (`perRift` every
   * `batchInterval`s up to `maxPerRift` — see the emission loop in the update). Manual; on cooldown. */
  fireRiftSwarm(): void {
    if (!this.riftSwarmReady) return;
    const m = this.mothership;
    const px = -Math.sin(m.heading); // perpendicular to the nose = the ship's two sides
    const py = Math.cos(m.heading);
    const R = MKVI.swl7Rift;
    for (const s of [-1, 1] as const) {
      const rx = m.pos.x + px * R.side * s;
      const ry = m.pos.y + py * R.side * s;
      // The rift stays put and remembers its launch heading; the update loop streams the waves.
      this.riftPortals.push({ x: rx, y: ry, age: 0, heading: m.heading, emitted: 0, batchCd: 0 });
    }
    this.riftSwarmCd = R.cooldown;
  }
  /** Total worm body segments = base + upgrades (head + tail are added on top). */
  get wormBodyCount(): number {
    return MKVII.startBodies + this.eff.wormBodies;
  }
  /** Missiles per worm salvo (0 until the missile branch), capped at MKVII.missileMax (20). */
  get wormMissileCount(): number {
    return Math.min(MKVII.missileMax, this.eff.wormMissiles);
  }
  /** Hull core collision radius — doubled on the Mk IV (bigger hitbox). */
  get collisionRadiusWorld(): number {
    return (
      SHIP_DEFENSE.collisionRadius *
      (this.hullTier >= 3 ? SHIP_DEFENSE.mk4CollisionMul : 1)
    );
  }

  /** Turret mounts available on the hull: 0 (Mk I), 4 (Mk II), 8 (Mk III). */
  /** Hull render-scale multiplier — mirrors PixiLayer HULL_SIZE_MUL so turret/cannon
   * mount offsets land on the correctly-sized hull (Mk V = Carrier ×1.2). */
  get hullRenderMul(): number {
    return this.hullTier >= 5
      ? this.mkvPath === "swarmlord"
        ? MKVI.hullRenderMul * (this.isMkviiSwl ? MKVI.swl7SizeMul : MKVI.swlSizeMul) // Mk VI SWL +10%, Mk VII SWL +32%
        : MKVI.hullRenderMul
      : this.hullTier >= 4
        ? 6.3
        : this.hullTier >= 3
          ? 5.25
          : this.hullTier >= 2
            ? 2.625
            : 1.2;
  }
  get maxTurretSlots(): number {
    return this.hullTier >= 5
      ? this.mkvPath === "swarmlord"
        ? MKVI.swl.turrets // Mk VI Swarm-lord: 14 light-defence turrets
        : TURRET.slotsMkVI
      : this.hullTier >= 4
        ? TURRET.slotsMkV
        : this.hullTier >= 2
          ? TURRET.slots
          : this.hullTier >= 1
            ? TURRET.slotsMkII
            : 0;
  }

  /** Radius (world units) the camera should frame — the player's biggest active
   * area. The renderer zooms so this ×1.3 fits, so the player leans on the map. */
  get viewRadius(): number {
    if (this.layer === "system") return 475; // ~2× closer default than before (was 950)
    let r = this.eff.miningRange;
    if (this.eff.hangarLevel > 0) r = Math.max(r, this.eff.droneRange);
    if (this.purchasedUpgrades.has("mk2")) r = Math.max(r, TURRET.range);
    // Once cannons are unlocked, frame their whole reach so the player can see what
    // the cannons + Big Cannons are shooting at (their engage range = bolt travel).
    if (this.cannonsUnlocked) r = Math.max(r, CANNON.boltSpeed * CANNON.boltLife);
    if (this.hullTier >= 3) r *= 1.1; // Mk IV is huge → frame 10% wider (zoom out)
    if (this.attract) r *= 0.66; // menu preview: frame the hero ship tighter (zoom in)
    return r;
  }

  // --- arsenal (mechanical yard) ---
  /** Unlock a SPECIFIC mount (any order). Each successive unlock costs more,
   * based on how many are already unlocked — not which slot is chosen. */
  unlockTurretSlot(slot: number): boolean {
    if (this.currentService !== "mechanical" || this.hullTier < 1) return false;
    if (slot < 0 || slot >= this.maxTurretSlots) return false;
    if (this.unlockedSlots.has(slot)) return false;
    if (this.unlockedSlots.size >= this.maxTurretSlots) return false;
    const cost = this.nextSlotCost;
    if (!this.resources.spend({ units: cost })) return false;
    this.unlockedSlots.add(slot);
    return true;
  }
  buyTurret(id: string): boolean {
    if (this.currentService !== "mechanical" || id !== "turret_basic") return false;
    if (!this.resources.spend({ units: TURRET.buyCost })) return false;
    this.addItem(id, 1);
    return true;
  }
  equipTurret(slot: number, id: string): boolean {
    if (this.currentService !== "mechanical") return false;
    if (!this.unlockedSlots.has(slot) || this.equipped[slot]) return false;
    if (this.itemCount(id) < 1) return false;
    this.addItem(id, -1);
    this.equipped[slot] = id;
    return true;
  }
  unequipTurret(slot: number): boolean {
    if (this.currentService !== "mechanical") return false;
    const cur = this.equipped[slot];
    if (!cur) return false;
    this.addItem(cur, 1);
    this.equipped[slot] = null;
    return true;
  }

  /** Item drops when the PLAYER destroys a body: moon cores from moons/sub-moons,
   * dark matter from asteroids. */
  private rollDrops(id: string, x: number, y: number): void {
    const isSub = id.includes("_sub_");
    const isMoon = id.startsWith("moon_") && !isSub;
    const isAsteroid = id.startsWith("ast_") || id.startsWith("sast_");
    // Count moons cracked during the Moon Core quest → triggers the trade-hub hint.
    if ((isMoon || isSub) && this.questStep === 10) this.moonsDestroyed++;
    const coreChance = isMoon
      ? DROPS.moonCoreBig
      : isSub
        ? DROPS.moonCoreSmall
        : 0;
    if (coreChance > 0 && Math.random() < coreChance) {
      this.addItem("moonCore", 1);
      this.markNewDrop("moonCore");
      this.floaterEvents.push({
        x,
        y: y - 18,
        amount: 1,
        kind: "loot",
        label: "◆ Moon Core",
      });
    }
    if (isAsteroid && Math.random() < DROPS.darkMatterAsteroid) {
      this.addItem("darkMatter", 1);
      this.markNewDrop("darkMatter");
      this.floaterEvents.push({
        x,
        y: y - 18,
        amount: 1,
        kind: "loot",
        label: "✦ Dark Matter",
      });
    }
  }

  private markNewDrop(id: string): void {
    this.newDropIds.add(id);
    this.newDropCount++;
  }
  /** Clear the inventory notification (called when the panel is opened). */
  markInventorySeen(): void {
    this.newDropIds.clear();
    this.newDropCount = 0;
  }
  get miningRangeWorld(): number {
    return this.eff.miningRange;
  }

  private updateDroneBay(dt: number, layer: PlanetLayerManager): void {
    const m = this.mothership;
    const fwd = Vector2.fromAngle(m.heading);
    const perp = new Vector2(-fwd.y, fwd.x);
    const hangar = m.pos
      .add(fwd.scale(HANGAR.offsetForward))
      .addSelf(perp.scale(HANGAR.offsetLateral));
    // Mk III has two blue side gates — drones stream out of them. Map the gate
    // texture offsets (±83, +16 in the 512-tall MTS_3) into world space.
    let gateLeft: Vector2 | undefined;
    let gateRight: Vector2 | undefined;
    if (this.hullTier >= 2) {
      const sh = Math.sin(m.heading);
      const chh = Math.cos(m.heading);
      const mk4 = this.hullTier >= 3;
      const gs = (MOTHERSHIP.spriteWorldHeight * this.hullRenderMul) / 512;
      const gate = (ox: number, oy: number): Vector2 =>
        new Vector2(
          m.pos.x + gs * (ox * sh + oy * chh),
          m.pos.y + gs * (ox * -chh + oy * sh),
        );
      const gox = mk4 ? 98 : 83;
      const goy = mk4 ? 20 : 16;
      gateLeft = gate(-gox, goy);
      gateRight = gate(gox, goy);
    }
    // Drones work autonomously inside a circle around the ship: every ore rock
    // in range is a candidate, and the bay splits drones across them.
    const dr2 = this.eff.droneRange * this.eff.droneRange;
    const shipPos = m.pos;
    // With drones toggled off (or while in combat) there are no candidates →
    // deployed drones dock & despawn. The Battleship gives up mining entirely
    // (laser + mining drones) in exchange for shoot-to-mine.
    const candidates =
      this.dronesEnabled && !this.combatActive && this.mkvPath !== "battleship"
        ? layer.bodies.filter(
            (b) =>
              b.ore > 0 &&
              !this.protectedMoonIds.has(b.id) &&
              b.pos.distanceSqTo(shipPos) <= dr2,
          )
        : [];
    const hullTargets = new Set(this.miningTargets.map((t) => t.id));

    this.droneBay.update(dt, {
      hangar,
      hangarFacing: fwd,
      gateLeft,
      gateRight,
      candidates,
      hullTargets,
      maxConcurrent: this.eff.hangarLevel,
      droneSpeed: this.eff.droneSpeed,
      droneCargo: this.eff.droneCargo,
      droneRate: this.eff.droneRate,
      bodyById: (id) => layer.byId(id),
      deposit: (amt) => this.depositMinerals(amt),
      emitFloater: (x, y, amt) => this.emitFloater(x, y, amt, "mineral"),
    });

    // Mechanical repair drones — deploy like mining drones, beam the hull green,
    // and repair HP for minerals. Off during combat / when toggled off / hull full.
    const repairActive =
      this.mechDronesEnabled &&
      this.eff.mechDrones > 0 &&
      !this.combatActive &&
      m.hp < m.maxHp &&
      this.resources.bag.minerals > 0;
    this.repairBay.update(dt, {
      active: repairActive,
      shipPos: m.pos,
      hangar,
      hangarFacing: fwd,
      gateLeft,
      gateRight,
      maxDrones: this.eff.mechDrones,
      speed: this.eff.droneSpeed,
      hullRadius: this.collisionRadiusWorld * 1.4, // spread beams across the hull
    });
    const beaming = this.repairBay.beaming;
    if (beaming > 0 && m.hp < m.maxHp) {
      // Equipped Repair Drone Controller speeds up the healing.
      const rdc = this.equippedRdc;
      const repairMul = rdc ? rdcBuff(rdc).repairMul : 1;
      const hp = Math.min(
        MECH_DRONE.repairPerDroneSec * repairMul * beaming * dt,
        m.maxHp - m.hp,
        this.resources.bag.minerals / MECH_DRONE.mineralPerHp,
      );
      if (hp > 0 && this.resources.spend({ minerals: hp * MECH_DRONE.mineralPerHp })) {
        m.hp += hp;
      }
    }
  }

  commandTo(x: number, y: number): void {
    const id = this.pickBodyAt(x, y);
    if (this.layer === "system") {
      // Tap a planet → fly to it; entry happens once inside its range.
      if (id) {
        this.systemTargetId = id;
        this.mothership.moveTarget = null;
      } else this.setMoveTarget(x, y);
    } else if (id) {
      // Tap a body → fly toward it (curving) and mine once inside the (upgraded)
      // mining range; the hull auto-fires when the body is in range + in-cone.
      // Tap the same body again to stop.
      if (this.moveBodyId === id) {
        this.moveBodyId = null; // tap again to stop
        this.mothership.moveTarget = null;
      } else {
        this.moveBodyId = id;
        this.mothership.moveTarget = null; // body target overrides a point move
      }
    } else {
      this.setMoveTarget(x, y);
    }
  }

  /** Planet-layer size / spawn / spawn-count multiplier — the map grows in deeper space
   * (×layerGrowMul from jump `layerGrowWarp1`, again from `layerGrowWarp2`). */
  get worldScale(): number {
    if (this.warps >= PROGRESSION.layerGrowWarp2) return PROGRESSION.layerGrowMul ** 2;
    if (this.warps >= PROGRESSION.layerGrowWarp1) return PROGRESSION.layerGrowMul;
    return 1;
  }

  travelToPlanet(planetId: string): void {
    const p = this.system.byId(planetId);
    if (!p) return;
    this.moonTurretBullets.length = 0; // clear any stale rogue bolts from a prior planet
    // Reuse the cached layer so destroyed rocks stay destroyed on return.
    let mgr = this.planetCache.get(planetId);
    if (!mgr) {
      const idx = parseInt(planetId.split("_")[1] ?? "0", 10);
      const seed = planetSeed(this.system.seed, idx);
      mgr = new PlanetLayerManager(
        seed,
        p.type,
        p.color,
        p.sprite,
        p.service,
        this.maxBigMoons,
        this.maxSmallMoons,
        p.rogue,
        this.moonTurretChance,
        this.worldScale, // deeper space → bigger layer + more rocks
      );
      this.planetCache.set(planetId, mgr);
      // Re-apply bodies destroyed in a previous session (persisted across a refresh).
      const gone = this.pendingDestroyed[planetId];
      if (gone) {
        for (const bid of gone) {
          mgr.destroyed.add(bid);
          const b = mgr.bodies.find((x) => x.id === bid);
          if (b) b.ore = 0;
        }
      }
    }
    // Spawn in a CORNER of the map (the corner matching the direction you approached from in the
    // solar view) and fly inward. The corner distance = the map's populated radius, which already
    // scales with the jump-level layer size.
    const adx = this.mothership.pos.x - p.pos.x;
    const ady = this.mothership.pos.y - p.pos.y;
    let ext = mgr.planet.radius + 200;
    for (const a of mgr.asteroids) {
      const d = Math.hypot(a.pos.x, a.pos.y);
      if (d > ext) ext = d;
    }
    for (const mn of mgr.moons) if (mn.distance > ext) ext = mn.distance;
    const sx = (adx >= 0 ? 1 : -1) * ext;
    const sy = (ady >= 0 ? 1 : -1) * ext;
    this.planetLayer = mgr;
    this.fromPlanetId = planetId;
    this.layer = "planet";
    if (this.tutorialStep === TUTORIAL.GOTO_PLANET) this.tutorialStep = TUTORIAL.MINE;
    this.mothership.pos.set(sx, sy);
    this.mothership.heading = Math.atan2(-sy, -sx); // face inward toward the planet
    this.npcs = new NpcManager(); // competitor miners populate the system
    // Trade hubs & mechanical yards are SAFE ZONES — no raiders spawn there, and
    // fighters never spawn near the player's entry point.
    const safe =
      p.service === "mechanical" || p.service === "trade" || p.service === "tavern";
    const spawnScale = this.worldScale; // deeper space → more raiders too
    this.enemies = new EnemyManager(
      safe ? 0 : Math.round(this.enemyCount * spawnScale),
      this.mothership.pos,
      this.warps >= 3, // squadron formations from the 3rd jump onward
    );
    // Capital-ship cruisers join the fight from the 4th drive jump onward (max 2).
    this.cruisers = new CruiserManager(
      safe ? 0 : Math.round(this.cruiserCount * spawnScale),
      this.mothership.pos,
    );
    // Hostile capital carriers join the fight from the 6th drive jump onward (max 1).
    this.carriers = new CarrierManager(
      safe ? 0 : Math.round(this.carrierCount * spawnScale),
      this.mothership.pos,
    );
    this.rolledEater = false; // a fresh awakening roll is available on this planet
    this.clearAwakening();
    // The planet the Eater awoke in stays a frozen wreck; if it still lives, it's lurking.
    if (planetId === this.eaterPlanetId) {
      if (this.planetLayer) this.planetLayer.moonsFrozen = true;
      if (!this.eaterDefeated) this.spawnEaterOfWorlds();
    }
    // Ring-station boss: this rogue world may host one (rolled per system). Reuse the cached
    // instance so its battle damage persists if the player leaves this planet and returns.
    this.station =
      this.stationPlanetIds.has(planetId) && !this.stationDefeated.has(planetId)
        ? this.getOrCreateStation(planetId)
        : null;
    this.resetActivity();
  }

  exitToSystem(): void {
    const p = this.fromPlanetId ? this.system.byId(this.fromPlanetId) : null;
    this.layer = "system";
    this.planetLayer = null;
    this.fromPlanetId = null;
    this.npcs = null;
    this.enemies = null;
    this.cruisers = null;
    this.carriers = null;
    this.worm = null;
    this.station = null; // its per-planet state stays in stationCache for a return visit
    if (p) this.mothership.pos.set(p.pos.x, p.pos.y - 160);
    else this.mothership.pos.set(0, -180);
    this.resetActivity();
  }

  private resetActivity(): void {
    this.mothership.moveTarget = null;
    this.moveBodyId = null;
    this.systemTargetId = null;
    this.droneBay.clear();
    this.repairBay.reset();
    this.mining.reset();
    this.turretSystem.reset();
    this.cannonSystem.reset();
    this.bigCannonSystem.reset();
    this.playerFighters.reset();
    this.playerFighterDrones.reset();
    this.missileSystem.reset();
    this.cannonAim = null;
    this.cannonTargetBodyId = null;
    this.cannonTargetEnemy = null;
    this.cannonReticle = null;
    this.miningTargets = [];
    this.miningImpact = null;
    this.bounceTimer = 0;
    this.floaterEvents.length = 0;
    this.explosions.length = 0;
    this.combatFx.length = 0;
  }

  pickBodyAt(x: number, y: number): string | null {
    const pt = new Vector2(x, y);
    let best: string | null = null;
    let bestSq = Infinity;
    const consider = (id: string, pos: Vector2, radius: number, mined: boolean) => {
      if (mined) return;
      const r = radius + 16;
      const dSq = pos.distanceSqTo(pt);
      if (dSq <= r * r && dSq < bestSq) {
        bestSq = dSq;
        best = id;
      }
    };
    if (this.layer === "system") {
      for (const p of this.system.planets) consider(p.id, p.pos, p.radius, false);
    } else if (this.planetLayer) {
      for (const b of this.planetLayer.bodies)
        consider(b.id, b.pos, b.radius, b.ore <= 0);
    }
    return best;
  }

  bodyOre(id: string): { ore: number; oreMax: number } | null {
    if (this.layer === "system") return null;
    const b = this.planetLayer?.byId(id) ?? null;
    return b ? { ore: b.ore, oreMax: b.oreMax } : null;
  }

  private moveMothership(dt: number): void {
    const m = this.mothership;
    // Knock-back after a collision briefly overrides normal steering.
    if (this.bounceTimer > 0) {
      this.bounceTimer -= dt;
      m.pos.x += this.bounceVel.x * dt;
      m.pos.y += this.bounceVel.y * dt;
      this.bounceVel.x *= 0.9;
      this.bounceVel.y *= 0.9;
      m.vel.copy(this.bounceVel);
      m.heading = Math.atan2(this.bounceVel.y, this.bounceVel.x);
      return;
    }
    // Mining draws power from the engines → the ship crawls at half speed.
    const miningSlow =
      this.miningTargets.length > 0 ? SHIP_DEFENSE.slowWhileMining : 1;
    // Solar-Sail upgrades speed up interplanetary flight ONLY (system layer).
    const layerMul =
      this.layer === "system" ? 0.5 * this.eff.solarSpeedMul : 1;
    const speed = this.eff.shipSpeed * layerMul * miningSlow;

    // Resolve the goal: a live body (fly to mining range and hold) or a point.
    let goal: Vector2 | null = null;
    let stopDist = MOTHERSHIP.arriveRadius;
    let holdFacing = false;
    if (this.layer === "system" && this.systemTargetId) {
      const p = this.system.byId(this.systemTargetId);
      if (p) {
        goal = p.pos;
        stopDist = p.radius + SERVICE.interactMargin;
        holdFacing = true;
      } else {
        this.systemTargetId = null;
      }
    } else if (this.moveBodyId && this.planetLayer) {
      const b = this.planetLayer.byId(this.moveBodyId);
      if (b && b.ore > 0) {
        goal = b.pos;
        stopDist = this.eff.miningRange + b.radius - 4; // hold at mining range
        holdFacing = true;
      } else {
        this.moveBodyId = null; // depleted / gone
      }
    } else if (m.moveTarget) {
      goal = m.moveTarget;
    }

    if (!goal) {
      m.vel.set(0, 0);
      return;
    }

    const to = goal.sub(m.pos);
    const dist = to.length();

    // Steer heading toward the goal at a capped turn rate → smooth curved flight
    // (no snapping). Tighten the turn when close so it can settle on the target.
    const desired = Math.atan2(to.y, to.x);
    let diff = desired - m.heading;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    // The Mk IV Battleship steers like a heavy ship — much slower to come about.
    const tierTurnMul = this.hullTier >= 3 ? 0.4 : 1;
    const rate =
      (dist < 90 ? MOTHERSHIP.turnRate * 2 : MOTHERSHIP.turnRate) * tierTurnMul;
    const maxTurn = rate * dt;
    m.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;

    if (dist <= stopDist) {
      m.vel.set(0, 0);
      if (!holdFacing) {
        // Point move: arrive and stop. (Body target: hold here and keep facing.)
        m.pos.copy(goal);
        m.moveTarget = null;
      }
      return;
    }

    // Fly forward along the (curving) heading.
    const vx = Math.cos(m.heading) * speed;
    const vy = Math.sin(m.heading) * speed;
    m.vel.set(vx, vy);
    m.pos.x += vx * dt;
    m.pos.y += vy * dt;
  }

  setMoveTarget(x: number, y: number): void {
    this.moveBodyId = null;
    this.systemTargetId = null;
    this.mothership.moveTarget = new Vector2(x, y);
  }

  /** Auto-mine: when enabled, keep steering the hull to the nearest mineable body (rock or
   * unprotected moon), holding at mining range so the laser/guns chew it, then seek the next when
   * it's spent. Combat suspends it (drops the target so the ship holds to fight) and it resumes when
   * the fight ends. Nothing left to mine → the ship simply stays put. Reuses the moveBodyId autopilot. */
  private updateAutoMine(): void {
    if (!this.autoMineEnabled || this.isWorm || this.layer !== "planet" || !this.planetLayer) return;
    const m = this.mothership;
    if (this.combatActive) {
      if (this.moveBodyId) this.moveBodyId = null; // fight → drop the auto target, hold still
      return;
    }
    // Keep chewing the current body while it still has ore (the autopilot flies + holds range).
    if (this.moveBodyId) {
      const b = this.planetLayer.byId(this.moveBodyId);
      if (b && b.ore > 0) return;
      this.moveBodyId = null; // depleted / gone
    }
    if (m.moveTarget) return; // let a manual point-move finish before taking over
    // Seek the nearest body that still has ore; null when the field is empty → the ship stays still.
    let best: string | null = null;
    let bd = Infinity;
    for (const b of this.mineableBodies(this.planetLayer)) {
      if (b.ore <= 0) continue;
      const d = (b.pos.x - m.pos.x) ** 2 + (b.pos.y - m.pos.y) ** 2;
      if (d < bd) {
        bd = d;
        best = b.id;
      }
    }
    this.moveBodyId = best;
  }

  /** Rock collisions damage the shield first, then the hull. The shield slowly
   * recharges after a lull, spending minerals; the hull does not self-repair. */
  private updateDefense(dt: number, layer: PlanetLayerManager): void {
    const m = this.mothership;
    this.hitTimer += dt;
    // One discrete hit per bump; skipped during the knock-back (i-frames). The MK VII
    // worm PHASES through rocks/moons (it eats them), so it never collides or bounces.
    if (this.bounceTimer <= 0 && !this.isWorm) {
      const mk4 = this.hullTier >= 3;
      const cr = this.collisionRadiusWorld;
      for (const b of layer.bodies) {
        if (b.ore <= 0 || b.id === this.moveBodyId) continue; // ignore husks + mine target
        const rr = cr + b.radius;
        const dx = m.pos.x - b.pos.x;
        const dy = m.pos.y - b.pos.y;
        if (dx * dx + dy * dy < rr * rr) {
          const len = Math.hypot(dx, dy) || 1;
          const nx = dx / len;
          const ny = dy / len;
          if (mk4) {
            // Battleship hull PLOWS through: shove the rock fully clear of the
            // hitbox (so it's flung off rather than sinking in), keep flying, and
            // only bleed a little shield (throttled). No knock-back.
            b.pos.set(m.pos.x - nx * (rr + 10), m.pos.y - ny * (rr + 10));
            if (this.hitTimer >= 0.3) {
              const wasUp2 = m.shield > 0;
              let d = SHIP_DEFENSE.mk4CollisionDamage;
              if (m.shield > 0) {
                const a = Math.min(m.shield, d);
                m.shield -= a;
                d -= a;
              }
              if (d > 0) m.hp = Math.max(0, m.hp - d);
              if (wasUp2 && m.shield <= 0) {
                this.shieldBrokenTimer = SHIP_DEFENSE.shieldDepleteDelay;
              }
              this.shieldFlash = 1;
              this.hitTimer = 0;
            }
            continue; // no bounce — plow on to the next rock
          }
          const wasUp = m.shield > 0;
          let dmg = SHIP_DEFENSE.collisionDamage;
          if (m.shield > 0) {
            const a = Math.min(m.shield, dmg);
            m.shield -= a;
            dmg -= a;
          }
          if (dmg > 0) m.hp = Math.max(0, m.hp - dmg);
          if (wasUp && m.shield <= 0) {
            this.shieldBrokenTimer = SHIP_DEFENSE.shieldDepleteDelay;
          }
          this.shieldFlash = 1;
          this.hitTimer = 0;
          // Bounce: shove out of the rock and fling away along the normal.
          m.pos.set(b.pos.x + nx * (rr + 2), b.pos.y + ny * (rr + 2));
          this.bounceVel.set(nx * SHIP_DEFENSE.bounceSpeed, ny * SHIP_DEFENSE.bounceSpeed);
          this.bounceTimer = SHIP_DEFENSE.bounceTime;
          this.moveBodyId = null; // stop steering back into it
          m.moveTarget = null;
          break;
        }
      }
    }
    // Shield recharge after a lull — spends minerals. A fully-broken shield waits
    // a longer cooldown before regenerating; higher-rarity generators recharge faster.
    this.shieldBrokenTimer = Math.max(0, this.shieldBrokenTimer - dt);
    if (
      this.hitTimer >= SHIP_DEFENSE.regenDelay &&
      this.shieldBrokenTimer <= 0 &&
      m.shield < m.maxShield
    ) {
      const want = Math.min(
        SHIP_DEFENSE.shieldRegenPerSec * this.equippedShieldRegenMul * dt,
        m.maxShield - m.shield,
      );
      const cost = want * SHIP_DEFENSE.shieldRegenCostPerPoint;
      if (want > 0 && this.resources.bag.minerals >= cost) {
        this.resources.spend({ minerals: cost });
        m.shield += want;
      }
    }
  }

  /** Bodies currently being mined (hull + attacking drones) with live ore. */
  activeMinedBodies(): { id: string; x: number; y: number; ore: number }[] {
    if (this.layer !== "planet" || !this.planetLayer) return [];
    const ids = new Set<string>();
    for (const t of this.miningTargets) ids.add(t.id);
    for (const u of this.droneBay.units) {
      if (u.state === "attacking") ids.add(u.targetId);
    }
    const out: { id: string; x: number; y: number; ore: number }[] = [];
    for (const id of ids) {
      const b = this.planetLayer.byId(id);
      if (b && b.ore > 0) {
        out.push({ id, x: b.pos.x, y: b.pos.y - b.radius - 12, ore: b.ore });
      }
    }
    return out;
  }

  /** Confirm a jump: burns 1 Warp Drive Fuel and starts the animated cutscene (freeze →
   * spaghettify → dive through a rift → emerge in a new system). The heavy system regeneration
   * happens mid-cutscene in applyWarpArrival(); the sim is frozen until it finishes. */
  warp(seed = (Math.random() * 2 ** 32) >>> 0): boolean {
    if (this.layer !== "system") return false; // the warp drive only engages from the solar layer
    if (this.itemCount("warpFuel") < 1) return false;
    if (this.warpPhase !== "none") return false; // a jump is already under way
    this.addItem("warpFuel", -1);
    this.warpSeed = seed;
    this.beginWarpJump();
    return true;
  }

  /** True while the jump cutscene plays — the world is frozen and the renderer draws the rift. */
  get warpActive(): boolean {
    return this.warpPhase !== "none";
  }

  private beginWarpJump(): void {
    // warp() gates this to the solar layer, so the spaghettification always has a system to eat.
    const m = this.mothership;
    const fx = Math.cos(m.heading);
    const fy = Math.sin(m.heading);
    this.warpRift = {
      cx: m.pos.x + fx * WARP.frontDist,
      cy: m.pos.y + fy * WARP.frontDist,
      heading: m.heading,
      open: 0,
      fade: 0,
      worldWidth: MOTHERSHIP.spriteWorldHeight * this.hullRenderMul * WARP.riftSizeMul,
    };
    this.warpProgress = 0;
    // warpShipScale is the FULL render multiplier the system view uses during the cutscene: it
    // starts at the normal system-view size (0.25) so the hull doesn't snap big, grows to full as
    // the jump charges, shrinks into the rift, grows back out, then settles to 0.25 again.
    this.warpShipScale = WARP.navScale;
    this.warpPhase = "collapse";
    this.warpTimer = WARP.collapseTime;
    this.miningTargets = [];
    this.combatActive = false;
    // The worm can't shrink into a point — cancel its abilities so nothing renders frozen; it's
    // driven THROUGH the rift head-first (body following) during the enter/emerge phases instead.
    if (this.isWorm) this.playerWorm?.stopForTransit();
  }

  /** The jump cutscene timeline. Runs every frame while `warpPhase !== "none"`; the rest of the
   * sim is frozen (see update()), so this drives the ship, the rift and the spaghetti progress. */
  private updateWarpJump(dt: number): void {
    const m = this.mothership;
    const r = this.warpRift;
    if (!r) {
      this.warpPhase = "none";
      return;
    }
    this.cameraOverride = { x: m.pos.x, y: m.pos.y }; // keep the ship framed throughout
    this.warpTimer -= dt;
    const rem = Math.max(0, this.warpTimer);
    if (this.warpPhase === "collapse") {
      // Planets + sun stretch into streaks; the (still-closed) rift fades in ahead of the ship.
      this.warpProgress = 1 - rem / WARP.collapseTime;
      r.open = 0;
      r.fade = Math.min(1, this.warpProgress * 2);
      // Smoothly grow the hull from its normal system size up to full (no snap at jump start).
      this.warpShipScale = WARP.navScale + (1 - WARP.navScale) * this.warpProgress;
      if (this.warpTimer <= 0) {
        this.warpProgress = 1;
        this.warpShipScale = 1;
        this.warpPhase = "open";
        this.warpTimer = WARP.openTime;
      }
      return;
    }
    if (this.warpPhase === "open") {
      // The closed slit widens into an open "eye".
      r.fade = 1;
      r.open = 1 - rem / WARP.openTime;
      this.warpShipScale = 1;
      if (this.warpTimer <= 0) {
        r.open = 1;
        this.warpEnterFrom = { x: m.pos.x, y: m.pos.y };
        this.warpPhase = "enter";
        this.warpTimer = this.isWorm ? WARP.wormEnterTime : WARP.enterTime;
      }
      return;
    }
    if (this.warpPhase === "enter") {
      // WORM hulls: the head dives through the rift and the body threads in behind it, each
      // segment vanishing as it crosses the rift plane (no shrink — see PlayerWorm.warpEnter).
      if (this.isWorm && this.playerWorm) {
        const w = this.playerWorm;
        const speed = (WARP.frontDist + w.transitLength) / WARP.wormEnterTime;
        const done = w.warpEnter(r.cx, r.cy, r.heading, speed, dt);
        const hp = w.headPos;
        m.pos.set(hp.x, hp.y); // keep the mothership (camera anchor) on the head
        m.vel.set(Math.cos(r.heading) * 140, Math.sin(r.heading) * 140);
        this.cameraOverride = { x: hp.x, y: hp.y };
        if (done || this.warpTimer <= 0) this.applyWarpArrival();
        return;
      }
      // The ship slides into the rift centre, shrinking as it crosses the threshold.
      const t = 1 - rem / WARP.enterTime;
      const e = t * t; // ease-in
      m.pos.set(
        this.warpEnterFrom.x + (r.cx - this.warpEnterFrom.x) * e,
        this.warpEnterFrom.y + (r.cy - this.warpEnterFrom.y) * e,
      );
      m.vel.set(Math.cos(r.heading) * 140, Math.sin(r.heading) * 140); // thrusters blaze as it dives in
      this.warpShipScale = Math.max(0, 1 - t);
      if (this.warpTimer <= 0) this.applyWarpArrival();
      return;
    }
    if (this.warpPhase === "emerge") {
      // WORM hulls: the head slides out of the exit rift and the body follows it out, each segment
      // reappearing as it clears the rift plane (see PlayerWorm.warpEmerge). Set up in applyWarpArrival.
      if (this.isWorm && this.playerWorm) {
        const w = this.playerWorm;
        const speed = (w.transitLength + WARP.emergeDist) / WARP.wormEmergeTime;
        w.warpEmerge(r.cx, r.cy, r.heading, speed, dt);
        const hp = w.headPos;
        m.pos.set(hp.x, hp.y);
        m.vel.set(Math.cos(r.heading) * 140, Math.sin(r.heading) * 140);
        this.cameraOverride = { x: hp.x, y: hp.y };
        if (this.warpTimer <= 0) {
          this.playerWorm.stopForTransit(); // reveal every segment before normal play resumes
          this.warpShipScale = 1;
          this.warpPhase = "close";
          this.warpTimer = WARP.closeTime;
        }
        return;
      }
      // In the NEW system: the ship grows out of the exit rift and slides forward.
      const t = 1 - rem / WARP.emergeTime;
      const fx = Math.cos(m.heading);
      const fy = Math.sin(m.heading);
      m.pos.set(r.cx + fx * WARP.emergeDist * t, r.cy + fy * WARP.emergeDist * t);
      m.vel.set(fx * 140, fy * 140); // thrusters blaze as it climbs out
      this.warpShipScale = Math.min(1, t * 1.25);
      if (this.warpTimer <= 0) {
        this.warpShipScale = 1;
        this.warpPhase = "close";
        this.warpTimer = WARP.closeTime;
      }
      return;
    }
    if (this.warpPhase === "close") {
      // The exit rift closes + fades behind the ship, and the hull eases from full size back down
      // to its normal system-view size so it doesn't SNAP when the cutscene ends.
      const t = 1 - rem / WARP.closeTime;
      r.open = rem / WARP.closeTime;
      r.fade = rem / WARP.closeTime;
      this.warpShipScale = 1 - (1 - WARP.navScale) * t;
      if (this.warpTimer <= 0) this.finishWarpJump();
      return;
    }
  }

  /** Mid-cutscene: build the destination system (the old instant-warp guts), then hand off to the
   * "emerge" phase so the ship climbs out of a rift at its arrival point. */
  private applyWarpArrival(): void {
    this.warps++;
    this.didWarp = true; // marks the final questline objective complete
    if (this.warps === MOON_TURRET.fromWarp) {
      // First jump where rogue planets appear — Aria warns the player.
      this.openDialog([
        "Commander — long-range scans just flagged ROGUE planets ahead.",
        "Their moons mount auto-turrets that open fire on anyone who drifts close. A red dot on the minimap marks an armed moon.",
        "Destroy a moon's turret before you can mine that moon — but the ore out there is worth three times as much. Stay sharp.",
      ]);
    }
    if (this.warps === PROGRESSION.layerGrowWarp1) {
      // Deeper space unlocks here — bigger, richer planet layers. Aria briefs the player.
      this.openDialog([
        "Commander — the drive just carried us into a deeper stratum of space.",
        "The gathering grounds out here are far wider and the fields much richer — more ore, more room to run.",
        "But the deeper we go, the bolder the raiders. Expect more company. Stay sharp.",
      ]);
    }
    // Every few jumps the trade-hub modifications get stronger (and pricier).
    if (this.modTier > this.lastModTier) {
      this.lastModTier = this.modTier;
      this.noticeText = `⚙ Trade-hub modifications upgraded to tier ${this.modTier} — go check the trade hub!`;
    }
    // A richer system each jump: more planets, more (and bigger) moons.
    this.system.regenerate(this.warpSeed, this.maxPlanets, this.maxBigMoons, this.warps);
    this.layer = "system";
    this.planetLayer = null;
    this.fromPlanetId = null;
    this.planetCache.clear(); // new system → discard cached planets
    this.pendingDestroyed = {}; // ...and their destroyed-body records — fresh moons this jump
    this.npcs = null;
    this.enemies = null;
    this.cruisers = null;
    this.carriers = null;
    this.worm = null;
    this.station = null;
    this.stationCache.clear(); // new system → fresh stations
    this.stationDefeated.clear();
    this.computeStationPlanets(); // re-roll which rogue worlds host a ring-station
    this.mothership.pos.set(0, -180);
    this.resetActivity();
    // Emerge: put the exit rift on the ship's spawn and start climbing out of it. The new system
    // renders normally (warpProgress 0 → no spaghetti).
    const m = this.mothership;
    this.warpRift = {
      cx: m.pos.x,
      cy: m.pos.y,
      heading: m.heading,
      open: 1,
      fade: 1,
      worldWidth: MOTHERSHIP.spriteWorldHeight * this.hullRenderMul * WARP.riftSizeMul,
    };
    this.warpProgress = 0;
    this.warpShipScale = 0;
    this.warpPhase = "emerge";
    this.warpTimer = WARP.emergeTime;
    // WORM hulls: stack the whole chain straight behind the exit rift (all hidden), ready to slide
    // out head-first over wormEmergeTime — the emerge phase reveals segments as they clear the rift.
    if (this.isWorm && this.playerWorm) {
      this.playerWorm.warpEmergeStart(m.pos.x, m.pos.y, m.heading);
      this.warpShipScale = 1; // the worm ignores hull scale — keep it neutral
      this.warpTimer = WARP.wormEmergeTime;
    }
  }

  private finishWarpJump(): void {
    this.warpPhase = "none";
    this.warpRift = null;
    this.warpProgress = 0;
    this.warpShipScale = 1;
    this.cameraOverride = null;
  }

  // --- persistence (meta-progression only; the world regenerates on load) ---
  serialize(): SaveData {
    const m = this.mothership;
    return {
      v: 1,
      name: this.playerName,
      minerals: this.resources.bag.minerals,
      alloy: this.resources.bag.alloy,
      plasma: this.resources.bag.plasma,
      darkMatter: this.resources.bag.darkMatter,
      units: this.resources.bag.units,
      inventory: { ...this.inventory },
      shieldLevel: this.shieldLevel,
      mods: this.mods.map((m) => ({ ...m })),
      equippedShieldModId: this.equippedShieldModId,
      unlockedSlots: [...this.unlockedSlots],
      equipped: [...this.equipped],
      cannonSlots: [...this.unlockedCannonSlots],
      cannonsEquipped: [...this.equippedCannons],
      bigCannonSlots: [...this.unlockedBigCannonSlots],
      bigCannonsEquipped: [...this.equippedBigCannons],
      fighters: this.fighters.map((f) => (f ? { pilotId: f.pilotId } : null)),
      crew: this.crew.map((m) => ({ ...m })),
      equippedMdcId: this.equippedMdcId,
      equippedRdcId: this.equippedRdcId,
      equippedGcId: this.equippedGcId,
      equippedFdcId: this.equippedFdcId,
      upgrades: [...this.purchasedUpgrades],
      level: this.level,
      exp: this.exp,
      expToNext: this.expToNext,
      skillPoints: this.skillPoints,
      tutorialStep: this.tutorialStep,
      questStep: this.questStep,
      mkvIntroSeen: this.mkvIntroSeen,
      warps: this.warps,
      elapsed: this.elapsed,
      systemSeed: this.system.seed, // pin the current solar system across refreshes
      destroyed: this.collectDestroyed(),
      eaterPlanetId: this.eaterPlanetId,
      eaterDefeated: this.eaterDefeated,
      eaterExplodedFrame: this.eaterExplodedFrame,
      hp: m.hp,
      shield: m.shield,
    };
  }

  /** Per-planet ids of everything mined/blown out — persisted so a refresh keeps it gone. */
  private collectDestroyed(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [id, mgr] of this.planetCache) {
      if (mgr.destroyed.size > 0) out[id] = [...mgr.destroyed];
    }
    return out;
  }

  load(data: SaveData): void {
    if (!data || data.v !== 1) return;
    if (data.name) this.playerName = data.name;
    const bag = this.resources.bag;
    bag.minerals = data.minerals ?? 0;
    bag.alloy = data.alloy ?? 0;
    bag.plasma = data.plasma ?? 0;
    bag.darkMatter = data.darkMatter ?? 0;
    bag.units = data.units ?? 0;
    for (const k of Object.keys(this.inventory)) delete this.inventory[k];
    if (data.inventory) {
      for (const [k, v] of Object.entries(data.inventory)) this.inventory[k] = v;
    }
    this.purchasedUpgrades.clear();
    for (const id of data.upgrades ?? []) this.purchasedUpgrades.add(id);
    this.level = data.level ?? 1;
    this.exp = data.exp ?? 0;
    this.skillPoints = data.skillPoints ?? 0;
    this.tutorialStep = data.tutorialStep ?? TUTORIAL.DONE; // old saves skip the tutorial
    this.questStep = data.questStep ?? 99; // pre-questline saves → treat as finished
    this.mkvIntroSeen = data.mkvIntroSeen ?? false;
    this.warps = data.warps ?? 0;
    this.elapsed = data.elapsed ?? 0;
    this.crew.length = 0;
    if (Array.isArray(data.crew)) {
      for (const c of data.crew) if (c && c.id) this.crew.push({ ...c });
    }
    this.equippedMdcId = data.equippedMdcId ?? null;
    this.equippedRdcId = data.equippedRdcId ?? null;
    this.equippedGcId = data.equippedGcId ?? null;
    this.equippedFdcId = data.equippedFdcId ?? null;
    this.recomputeStats(); // picks up the equipped MDC buff

    this.expToNext = data.expToNext ?? this.xpForLevel(this.level);
    this.shieldLevel = data.shieldLevel ?? 0;
    this.mods.length = 0;
    if (Array.isArray(data.mods)) {
      for (const m of data.mods) if (m && m.id) this.mods.push({ ...m, level: m.level ?? 1 });
    }
    this.equippedShieldModId = data.equippedShieldModId ?? null;
    this.lastModTier = this.modTier;
    this.applyShield();
    this.unlockedSlots.clear();
    const us = data.unlockedSlots;
    if (Array.isArray(us)) {
      // Cap by the CURRENT hull's slot count (tier-aware: Mk VI = 18) so refit tiers
      // keep their extra turret ports across a save/reload — matches the cannon path.
      for (const i of us) if (i >= 0 && i < this.maxTurretSlots) this.unlockedSlots.add(i);
    } else if (typeof us === "number") {
      // legacy save stored a COUNT → unlock the first N mounts.
      for (let i = 0; i < Math.min(this.maxTurretSlots, us); i++) this.unlockedSlots.add(i);
    }
    for (let i = 0; i < this.equipped.length; i++) {
      this.equipped[i] = data.equipped?.[i] ?? null;
    }
    this.unlockedCannonSlots.clear();
    if (Array.isArray(data.cannonSlots)) {
      for (const i of data.cannonSlots) {
        if (i >= 0 && i < this.maxCannonSlots) this.unlockedCannonSlots.add(i);
      }
    }
    for (let i = 0; i < this.equippedCannons.length; i++) {
      this.equippedCannons[i] = data.cannonsEquipped?.[i] ?? null;
    }
    this.unlockedBigCannonSlots.clear();
    if (Array.isArray(data.bigCannonSlots)) {
      for (const i of data.bigCannonSlots) {
        if (i >= 0 && i < this.maxBigCannonSlots) this.unlockedBigCannonSlots.add(i);
      }
    }
    for (let i = 0; i < this.equippedBigCannons.length; i++) {
      this.equippedBigCannons[i] = data.bigCannonsEquipped?.[i] ?? null;
    }
    for (let i = 0; i < this.fighters.length; i++) {
      const f = data.fighters?.[i];
      this.fighters[i] = f ? { pilotId: f.pilotId ?? null } : null;
    }
    this.mothership.hp = Math.min(data.hp ?? this.mothership.maxHp, this.mothership.maxHp);
    this.mothership.shield = Math.min(
      data.shield ?? this.mothership.maxShield,
      this.mothership.maxShield,
    );
    // The world regenerates on load, but from the SAVED seed — so a browser
    // refresh restores the identical solar system (warp fuel keeps its meaning).
    // Old saves without a stored seed fall back to a fresh random system.
    const savedSeed =
      typeof data.systemSeed === "number"
        ? data.systemSeed >>> 0
        : (Math.random() * 2 ** 32) >>> 0;
    this.system.regenerate(savedSeed, this.maxPlanets, this.maxBigMoons, this.warps);
    this.planetCache.clear(); // discard any planet layers cached under the old seed
    this.stationCache.clear();
    this.stationDefeated.clear();
    this.computeStationPlanets(); // deterministic per seed → same rogue worlds host stations
    // Restore what was mined/blown out (re-applied lazily when each planet is entered) and
    // the Eater's mark, so a refresh keeps destroyed bodies gone (player must jump for more).
    this.pendingDestroyed = data.destroyed ?? {};
    this.applyDestroyedToSystem(); // reflect blown-up moons in the regenerated solar view
    this.eaterPlanetId = data.eaterPlanetId ?? null;
    this.eaterDefeated = data.eaterDefeated ?? false;
    this.eaterExplodedFrame = data.eaterExplodedFrame ?? 0;
  }

  /** Info for the Mk-refit comparison screen: the next hull and its gating. */
  private buildRefit(): RefitInfo {
    // Carrier (tier 3) branches into the two mutually-exclusive Mk V paths.
    if (this.hullTier === 3) {
      const node = UPGRADES_BY_ID["mkv_bts"]!; // both paths share the same cost/gating
      return {
        tier: this.hullTier,
        nextId: null,
        nextName: null,
        cost: node.cost,
        units: node.units ?? 0,
        cores: node.cores ?? 0,
        reqsMet: node.requires.every((r) => this.purchasedUpgrades.has(r)),
        missingReqs: node.requires.filter((r) => !this.purchasedUpgrades.has(r)),
        atMechanical: this.currentService === "mechanical",
        coresHave: this.itemCount("energyCore"),
        branchIds: ["mkv_bts", "mkv_swl"],
      };
    }
    // Mk V (tier 4) refits linearly into its Mk VI along the SAME path:
    // Battleship → Mk VI Battleship, Swarm-lord → Mk VI Swarm-lord.
    const nextId: string | null =
      this.hullTier === 4
        ? this.mkvPath === "battleship"
          ? "mkvi_bts"
          : "mkvi_swl"
        : this.hullTier === 5 && this.mkvPath === "swarmlord" && !this.isMkviiSwl
          ? "mkvii_swl" // Mk VI Swarm-lord → Mk VII Swarm-lord (cosmic apex)
          : ((["mk2", "mk3", "mk4"] as const)[this.hullTier] ?? null);
    const node = nextId ? UPGRADES_BY_ID[nextId] : null;
    return {
      tier: this.hullTier,
      nextId,
      nextName: node?.name ?? null,
      cost: node?.cost ?? 0,
      units: node?.units ?? 0,
      cores: node?.cores ?? 0,
      reqsMet: node ? node.requires.every((r) => this.purchasedUpgrades.has(r)) : false,
      missingReqs: node ? node.requires.filter((r) => !this.purchasedUpgrades.has(r)) : [],
      atMechanical: this.currentService === "mechanical",
      coresHave: this.itemCount("energyCore"),
    };
  }

  /** Build the current zone's minimap dots (see HUD Minimap). */
  private buildMinimap(): MinimapData {
    const player = { x: this.mothership.pos.x, y: this.mothership.pos.y };
    const wp = this.mothership.moveTarget;
    const waypoint = wp ? { x: wp.x, y: wp.y } : null; // click-to-move destination marker
    if (this.layer === "system" || !this.planetLayer) {
      let extent = 400;
      const planets = this.system.planets.map((p) => {
        extent = Math.max(extent, Math.hypot(p.pos.x, p.pos.y));
        return { x: p.pos.x, y: p.pos.y, service: p.service, rogue: p.rogue };
      });
      extent = Math.max(extent, Math.hypot(player.x, player.y)) + 220;
      return {
        isSystem: true,
        extent,
        player,
        planet: null,
        moons: [],
        asteroids: [],
        enemies: [],
        playerFighters: [],
        planets,
        highlightSafe: this.tutorialHighlightPlanets,
        riftArmed: this.riftArmed,
        waypoint,
      };
    }
    const L = this.planetLayer;
    const moons: MinimapDot[] = [];
    for (const mn of L.moons) {
      if (mn.ore > 0) moons.push({ x: mn.pos.x, y: mn.pos.y, s: 0 });
      for (const sm of mn.subMoons) {
        if (sm.ore > 0) moons.push({ x: sm.pos.x, y: sm.pos.y, s: 0 });
      }
    }
    let extent = L.planet.radius + 200;
    const asteroids: MinimapDot[] = [];
    for (const a of L.asteroids) {
      if (a.ore <= 0) continue;
      const s = a.oreMax > 1000 ? 2 : a.oreMax > 200 ? 1 : 0;
      asteroids.push({ x: a.pos.x, y: a.pos.y, s });
      const d = Math.hypot(a.pos.x, a.pos.y);
      if (d > extent) extent = d;
    }
    extent += 120;
    const enemies: MinimapDot[] = this.enemyShips
      .filter((e) => e.hp > 0)
      .map((e) => ({ x: e.pos.x, y: e.pos.y, s: 0 }))
      // Cruisers are a big red dot (s: 2).
      .concat(
        this.cruiserShips
          .filter((e) => e.hp > 0)
          .map((e) => ({ x: e.pos.x, y: e.pos.y, s: 2 })),
      )
      // Carriers are a big red dot too (s: 2).
      .concat(
        this.carrierShips
          .filter((e) => e.hp > 0)
          .map((e) => ({ x: e.pos.x, y: e.pos.y, s: 2 })),
      )
      // The Eater of Worlds — each live segment is a red dot (its head the biggest).
      .concat(
        this.wormSegments
          .filter((s) => s.hp > 0)
          .map((s) => ({ x: s.pos.x, y: s.pos.y, s: s.kind === "head" ? 2 : 1 })),
      )
      // Armed moons (live turret) get a small red dot in their middle.
      .concat(this.liveMoonTurrets.map((t) => ({ x: t.pos.x, y: t.pos.y, s: 0 })));
    return {
      isSystem: false,
      extent,
      player,
      planet: { x: L.planet.pos.x, y: L.planet.pos.y, r: L.planet.radius },
      moons,
      asteroids,
      enemies,
      playerFighters: this.playerFighterShips
        .filter((s) => s.hp > 0)
        .map((s) => ({ x: s.pos.x, y: s.pos.y, s: 0 })),
      planets: [],
      highlightSafe: false,
      riftArmed: this.riftArmed,
      waypoint,
    };
  }

  snapshot(): HudSnapshot {
    const m = this.mothership;
    const active = this.droneBay.units;
    const inPlanet = this.layer === "planet" && this.planetLayer !== null;
    const remaining = inPlanet
      ? this.planetLayer!.bodies.filter((b) => b.ore > 0).length
      : this.system.planets.filter((p) => p.ore > 0).length;
    const qDef = questDef(this.questStep);
    return {
      tier: m.tier,
      shipName: `${this.playerName} · ${this.isMkviii ? "Mechanical Eater of Worlds" : this.isWorm ? "Eater of Worlds" : this.hullTier === 0 ? "Miner" : this.hullTier >= 4 ? (this.mkvPath === "battleship" ? "Battleship" : "Swarm-lord") : "Carrier"} Mk ${this.isMkviiSwl ? "VII" : (["I", "II", "III", "IV", "V", "VI", "VII", "VIII"][this.hullTier] ?? "I")}`,
      hp: m.hp,
      maxHp: m.maxHp,
      shield: m.shield,
      maxShield: m.maxShield,
      minerals: this.resources.bag.minerals,
      alloy: this.resources.bag.alloy,
      darkMatter: this.resources.bag.darkMatter,
      units: this.resources.bag.units,
      activeDrones: active.length,
      orbitingDrones: active.filter((u) => u.state === "attacking").length,
      hangarLevel: this.eff.hangarLevel,
      upgrades: [...this.purchasedUpgrades],
      level: this.level,
      exp: this.exp,
      expToNext: this.expToNext,
      skillPoints: this.skillPoints,
      isAdmin: this.isAdmin,
      showMiningRange: this.showMiningRange,
      hullMiningEnabled: this.hullMiningEnabled,
      autoMineEnabled: this.autoMineEnabled,
      dronesEnabled: this.dronesEnabled,
      cannonsEnabled: this.cannonsEnabled,
      mechDronesEnabled: this.mechDronesEnabled,
      mechDronesUnlocked: this.eff.mechDrones > 0,
      cannonsUnlocked: this.cannonsUnlocked,
      cannonsReady: this.cannonsReady,
      cannonAim: this.cannonAim ? { x: this.cannonAim.x, y: this.cannonAim.y } : null,
      cannonTargeted: this.cannonReticle != null,
      cannonLocked:
        this.cannonTargetBodyId != null || this.cannonTargetEnemy != null,
      cannonArsenal: {
        available: this.cannonsUnlocked,
        slots: this.maxCannonSlots,
        unlocked: Array.from({ length: this.maxCannonSlots }, (_, i) =>
          this.unlockedCannonSlots.has(i),
        ),
        equipped: [...this.equippedCannons],
        count: this.itemCount("cannon_basic"),
        buyCost: CANNON.buyCost,
        nextSlotCost: this.nextCannonSlotCost,
      },
      bigCannonArsenal: {
        available: this.bigCannonsUnlocked,
        slots: this.maxBigCannonSlots,
        unlocked: Array.from({ length: this.maxBigCannonSlots }, (_, i) =>
          this.unlockedBigCannonSlots.has(i),
        ),
        equipped: [...this.equippedBigCannons],
        count: this.itemCount("cannon_big"),
        nextSlotCost: this.nextBigCannonSlotCost,
      },
      inCombat: this.combatActive,
      playerDead: this.playerDead,
      missileSkill: {
        available: this.maxMissiles > 0 && !this.isWorm, // worm uses its own combined rail button
        count: this.maxMissiles,
        ready: this.missileSystem.ready,
        cooldownFrac: this.missileSystem.cooldown / MISSILE.cooldown, // 1 = just fired → 0 = ready
      },
      wormBeam: {
        active: this.isWorm, // MK VII worm hull → show the maw-beam fire button
        ready: this.playerWorm?.ready ?? false,
        busyFrac: this.playerWorm?.busyFrac ?? 0,
      },
      wormMissile: {
        active: this.isWorm && this.wormMissileCount > 0, // shown once the missile branch is bought
        ready: this.missileSystem.ready,
        count: this.wormMissileCount,
        cooldownFrac: this.missileSystem.cooldown / MISSILE.cooldown,
      },
      rift: {
        active: this.riftUnlocked, // Mk VIII: shown once the Time & Space Rift node is bought
        ready: this.playerWorm?.riftReady ?? false,
        armed: this.riftArmed,
        cooldownFrac: this.playerWorm?.riftCooldownFrac ?? 0,
      },
      coil: {
        active: this.coilUnlocked, // Mk VIII: shown once the Maelstrom Coil node is bought
        ready: this.playerWorm?.coilReady ?? false,
        cooldownFrac: this.playerWorm?.coilCooldownFrac ?? 0,
      },
      riftSwarm: {
        show: this.isMkviiSwl, // Mk VII Swarm-lord signature skill
        ready: this.riftSwarmReady,
        cooldownFrac: this.riftSwarmCd / MKVI.swl7Rift.cooldown,
      },
      tutorial: {
        step: this.tutorialUiStep,
        highlightUpgrade: this.tutorialHighlightUpgrade,
      },
      quest: {
        step: this.questStep,
        title: qDef ? qDef.title : "",
        objective: qDef ? qDef.objective : "",
        progress: this.questProgress,
        dialogLine: this.dialogLines[this.dialogIndex] ?? null,
        dialogMore: this.dialogIndex < this.dialogLines.length - 1,
        dialogSpeaker: this.dialogSpeakers[this.dialogIndex] ?? "aria",
      },
      minimap: this.buildMinimap(),
      inventory: { ...this.inventory },
      newItemCount: this.newDropCount,
      newItems: [...this.newDropIds],
      bossResult: this.bossResult
        ? {
            xp: this.bossResult.xp,
            units: this.bossResult.units,
            drops: this.bossResult.drops.map((d) => ({ ...d })),
          }
        : null,
      planetService: this.currentService,
      atServicePlanet: this.nearServicePlanet,
      market: this.getMarket(),
      marketRefreshIn: Math.max(0, this.marketRefreshAt - this.elapsed),
      shieldLevel: this.shieldLevel,
      mods: this.mods.map((m) => ({ ...m })),
      equippedShieldModId: this.equippedShieldModId,
      modTier: this.modTier,
      buildModCost: this.buildModCost,
      modUpgradeCosts: Object.fromEntries(
        this.mods.map((m) => [m.id, this.modUpgradeCost(m.id)]),
      ),
      notice: this.noticeText,
      arsenal: {
        hullTier: this.hullTier,
        mkvPath: this.mkvPath,
        isMkviiSwl: this.isMkviiSwl,
        unlocked: Array.from({ length: this.maxTurretSlots }, (_, i) =>
          this.unlockedSlots.has(i),
        ),
        unlockedCount: this.unlockedSlots.size,
        maxSlots: this.maxTurretSlots,
        equipped: [...this.equipped],
        turretCount: this.itemCount("turret_basic"),
        nextSlotCost: this.nextSlotCost,
        buyCost: TURRET.buyCost,
      },
      fleet: {
        slots: this.maxFighterSlots, // usable slots (from Fighter Bay upgrades)
        maxSlots: PLAYER_FIGHTER.maxSlots,
        buyCost: PLAYER_FIGHTER.buyCost,
        baseHp: PLAYER_FIGHTER.maxHp,
        baseShield: PLAYER_FIGHTER.maxShield,
        deployed: this.playerFighters.ships.filter((s) => s.hp > 0).length,
        ready: this.buildFighterLoadouts().length, // bays with a living pilot (can fly)
        formationActive: this.playerFighters.formationActive,
        droneSlots: this.maxFighterDroneSlots, // Swarm-lord fighter-drone swarm cap
        dronesDeployed: this.playerFighterDrones.ships.filter((s) => s.hp > 0).length,
        fighters: this.fighters.slice(0, PLAYER_FIGHTER.maxSlots).map((f) => {
          if (!f) return null;
          const p = this.pilotFor(f.pilotId);
          const kia = !!(p && p.kia);
          return {
            pilotId: f.pilotId,
            pilot: p ? this.crewCard(p) : null,
            grounded: !p || kia,
            kia,
          };
        }),
        availablePilots: this.crew
          .filter(
            (m) =>
              m.type === "ftp" &&
              !m.kia &&
              !this.fighters.some((f) => f && f.pilotId === m.id),
          )
          .map((m) => this.crewCard(m)),
      },
      crew: {
        roster: this.crew.map((m) => this.crewCard(m)),
        equippedMdcId: this.equippedMdcId,
        equippedRdcId: this.equippedRdcId,
        equippedGcId: this.equippedGcId,
        equippedFdcId: this.equippedFdcId,
        recruitCost: RECRUIT_COST,
        rerollCost: REROLL_COST,
        ftpUnlocked: this.ftpUnlocked,
        gcUnlocked: this.gcUnlocked,
        fdcUnlocked: this.fdcUnlocked,
        maxTier: CREW_MAX_TIER,
      },
      refit: this.buildRefit(),
      stats: {
        miningRate: this.eff.hullRate,
        beamRate: this.eff.beamRate,
        beamCount: this.eff.beamCount,
        miningRange: this.eff.miningRange,
        shipSpeed: this.eff.shipSpeed,
        yieldMul: this.eff.yieldMul,
        droneSpeed: this.eff.droneSpeed,
        droneCargo: this.eff.droneCargo,
        droneRate: this.eff.droneRate,
        droneRange: this.eff.droneRange,
        hangarLevel: this.eff.hangarLevel,
      },
      hasSelection: this.moveBodyId !== null,
      inPlanetLayer: inPlanet,
      layerLabel: inPlanet ? (this.planetLayer!.planet.label ?? "Planet") : "System",
      bodiesRemaining: remaining,
      warps: this.warps,
      elapsed: this.elapsed,
      shipMining: this.miningTargets.length > 0,
    };
  }
}

export interface SaveData {
  name?: string;
  v: number;
  minerals: number;
  alloy: number;
  plasma: number;
  darkMatter: number;
  units: number;
  inventory: Record<string, number>;
  shieldLevel: number;
  unlockedSlots: number[] | number; // number[] = which mounts; number = legacy count
  equipped: (string | null)[];
  mods?: Modification[];
  equippedShieldModId?: string | null;
  cannonSlots?: number[];
  cannonsEquipped?: (string | null)[];
  bigCannonSlots?: number[];
  bigCannonsEquipped?: (string | null)[];
  fighters?: ({ pilotId: string | null } | null)[];
  crew?: CrewMember[];
  equippedMdcId?: string | null;
  equippedRdcId?: string | null;
  equippedGcId?: string | null;
  equippedFdcId?: string | null;
  upgrades: string[];
  level: number;
  exp: number;
  expToNext: number;
  skillPoints: number;
  tutorialStep?: number;
  questStep?: number;
  mkvIntroSeen?: boolean;
  warps: number;
  elapsed: number;
  systemSeed?: number; // RNG seed of the current solar system (persists the map)
  destroyed?: Record<string, string[]>; // per-planet ids of bodies mined/blown out (persist)
  eaterPlanetId?: string | null; // the planet the Eater awoke in
  eaterDefeated?: boolean;
  eaterExplodedFrame?: number;
  hp: number;
  shield: number;
}

export interface PlayerStats {
  miningRate: number; // total hull minerals/sec
  beamRate: number; // per beam
  beamCount: number;
  miningRange: number;
  shipSpeed: number;
  yieldMul: number;
  droneSpeed: number;
  droneCargo: number;
  droneRate: number;
  droneRange: number;
  hangarLevel: number;
}

export interface RefitInfo {
  tier: number; // current hull tier
  nextId: string | null; // next Mk upgrade node id, or null at max hull / a branch
  nextName: string | null;
  cost: number; // minerals
  units: number; // universal units required
  cores: number; // energy cores required
  reqsMet: boolean; // prerequisite nodes owned
  missingReqs: string[];
  atMechanical: boolean;
  coresHave: number;
  branchIds?: string[]; // Carrier → the two mutually-exclusive Mk V path node ids
}
export interface CrewCard {
  id: string;
  type: CrewType;
  rarity: Rarity;
  tier: number;
  name: string;
  portrait: string;
  buff: string; // human-readable buff summary
  upgradeCost: number;
  canUpgrade: boolean;
  kia: boolean; // pilot Killed In Action — must be rerolled at a tavern to revive
}
export interface FleetFighter {
  pilotId: string | null;
  pilot: CrewCard | null;
  grounded: boolean; // true = no living pilot, can't launch
  kia: boolean; // this bay's pilot is Killed In Action
}
export interface HudSnapshot {
  tier: number;
  shipName: string;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  minerals: number;
  alloy: number;
  darkMatter: number;
  units: number;
  activeDrones: number;
  orbitingDrones: number;
  hangarLevel: number;
  upgrades: string[];
  level: number;
  exp: number;
  expToNext: number;
  skillPoints: number;
  isAdmin: boolean;
  showMiningRange: boolean;
  hullMiningEnabled: boolean;
  autoMineEnabled: boolean;
  dronesEnabled: boolean;
  cannonsEnabled: boolean;
  mechDronesEnabled: boolean;
  mechDronesUnlocked: boolean;
  cannonsUnlocked: boolean;
  cannonsReady: boolean;
  cannonAim: { x: number; y: number } | null;
  cannonTargeted: boolean; // cannons have a live reticle (manual point / lock / auto)
  cannonLocked: boolean; // a specific moon or enemy is locked as the focus target
  cannonArsenal: {
    available: boolean;
    slots: number;
    unlocked: boolean[];
    equipped: (string | null)[];
    count: number;
    buyCost: number;
    nextSlotCost: number;
  };
  bigCannonArsenal: {
    available: boolean;
    slots: number;
    unlocked: boolean[];
    equipped: (string | null)[];
    count: number;
    nextSlotCost: number;
  };
  inCombat: boolean;
  playerDead: boolean;
  missileSkill: {
    available: boolean; // Swarm-lord with ≥1 Missile Pod upgrade
    count: number; // missiles per salvo
    ready: boolean; // off cooldown
    cooldownFrac: number; // 1 = just fired → 0 = ready
  };
  wormBeam: { active: boolean; ready: boolean; busyFrac: number };
  wormMissile: { active: boolean; ready: boolean; count: number; cooldownFrac: number };
  rift: { active: boolean; ready: boolean; armed: boolean; cooldownFrac: number };
  coil: { active: boolean; ready: boolean; cooldownFrac: number };
  riftSwarm: { show: boolean; ready: boolean; cooldownFrac: number };
  tutorial: { step: TutorialStep; highlightUpgrade: string | null };
  quest: {
    step: number; // 0 none, 1..11 active, 12 ending, 99 done
    title: string;
    objective: string;
    progress: string; // "" when the quest has no numeric target
    dialogLine: string | null; // current Tutor line, or null when no dialog is open
    dialogMore: boolean; // more lines follow (drives the "tap to continue" hint)
    dialogSpeaker: "aria" | "eater"; // who's speaking (drives the portrait + name)
  };
  minimap: MinimapData;
  inventory: Record<string, number>;
  newItemCount: number;
  newItems: string[];
  bossResult: { xp: number; units: number; drops: { id: string; count: number }[] } | null;
  planetService: PlanetService;
  atServicePlanet: boolean;
  market: MarketStock[];
  marketRefreshIn: number;
  shieldLevel: number;
  mods: Modification[];
  equippedShieldModId: string | null;
  modTier: number;
  buildModCost: number;
  modUpgradeCosts: Record<string, number>; // mod id → Units to level up (-1 if maxed)
  notice: string | null;
  arsenal: {
    hullTier: number;
    mkvPath: "battleship" | "swarmlord" | "none"; // MK V path (tier 4)
    isMkviiSwl: boolean; // Mk VII Swarm-lord variant (tier-5 swarmlord)
    unlocked: boolean[]; // per-mount unlock state (length = maxSlots)
    unlockedCount: number;
    maxSlots: number;
    equipped: (string | null)[];
    turretCount: number;
    nextSlotCost: number;
    buyCost: number;
  };
  fleet: {
    slots: number; // usable fleet slots
    maxSlots: number;
    buyCost: number;
    baseHp: number;
    baseShield: number;
    deployed: number; // fighters currently flying
    ready: number; // bays with a living pilot (can launch)
    formationActive: boolean; // ^ formation mustered
    droneSlots: number; // Swarm-lord fighter-drone swarm size (0 on other hulls)
    dronesDeployed: number; // fighter drones currently flying
    fighters: (FleetFighter | null)[];
    availablePilots: CrewCard[]; // unassigned FTP pilots
  };
  crew: {
    roster: CrewCard[];
    equippedMdcId: string | null;
    equippedRdcId: string | null;
    equippedGcId: string | null;
    equippedFdcId: string | null;
    recruitCost: number;
    rerollCost: number;
    ftpUnlocked: boolean;
    gcUnlocked: boolean;
    fdcUnlocked: boolean;
    maxTier: number;
  };
  refit: RefitInfo;
  stats: PlayerStats;
  hasSelection: boolean;
  inPlanetLayer: boolean;
  layerLabel: string;
  bodiesRemaining: number;
  warps: number;
  elapsed: number;
  shipMining: boolean;
}
